import React, { useState, useEffect, useRef } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { LogOut, Plus, Pencil, Trash2, Eye, Users, UserCheck, Award, Download, FileSpreadsheet, RefreshCw, Maximize, X, Music, Upload, Settings, Video, Play, Square, Key, Copy, Ticket, Mail } from 'lucide-react';
import { format } from 'date-fns';
import ProgrammesTab from '@/components/ProgrammesTab';
import AnnouncementUploader from '@/components/AnnouncementUploader';
import RotationScheduleEditor from '@/components/RotationScheduleEditor';
import SumUpSettings from '@/components/SumUpSettings';
import { formatUKDateTime, formatUKDate } from '@/utils/ukTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [allGymnasts, setAllGymnasts] = useState([]);
  const [allCoaches, setAllCoaches] = useState([]);
  const [allJudges, setAllJudges] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [selectedClub, setSelectedClub] = useState('all');
  const [selectedCompetition, setSelectedCompetition] = useState('all');
  const [entryStatusFilter, setEntryStatusFilter] = useState('all'); // 'all' | 'submitted' | 'in-progress'
  const [activeTab, setActiveTab] = useState('clubs');
  
  // Page title
  usePageTitle('Super Admin');
  
  // Search state
  const [gymnastSearch, setGymnastSearch] = useState('');
  const [coachSearch, setCoachSearch] = useState('');
  const [judgeSearch, setJudgeSearch] = useState('');
  
  // Super admin toggle confirmation state
  const [showSuperAdminDialog, setShowSuperAdminDialog] = useState(false);
  const [clubToToggle, setClubToToggle] = useState(null);
  
  // Delete club confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clubToDelete, setClubToDelete] = useState(null);
  
  // Delete entry confirmation state
  const [showDeleteEntryDialog, setShowDeleteEntryDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [showDeleteAllEntriesDialog, setShowDeleteAllEntriesDialog] = useState(false);

  // Resend confirmation email state
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendEntry, setResendEntry] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  
  // Entry editing state
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryGymnasts, setEntryGymnasts] = useState([]);

  // Excel import state (for competition edit form)
  const [importClubId, setImportClubId] = useState('');
  const [importFolder, setImportFolder] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Scoring system state
  const [scoringCompetition, setScoringCompetition] = useState('');
  const [judgeCodes, setJudgeCodes] = useState([]);
  const [judgeDisciplines, setJudgeDisciplines] = useState(['WAG']);
  const [judgeMagMode, setJudgeMagMode] = useState('individual');
  const [isDualPanel, setIsDualPanel] = useState(false);
  const [allScores, setAllScores] = useState([]);
  const [editingScore, setEditingScore] = useState(null);
  const [showEditScoreDialog, setShowEditScoreDialog] = useState(false);

  // SMS system state
  const [smsCompetition, setSmsCompetition] = useState('');
  const [clubsSMS, setClubsSMS] = useState([]);
  const [gymnastsSMS, setGymnastsSMS] = useState([]);
  const [newClubPhone, setNewClubPhone] = useState({});
  const [newParentPhone, setNewParentPhone] = useState({});
  const [showClubsSMS, setShowClubsSMS] = useState(false);
  const [showParentsSMS, setShowParentsSMS] = useState(false);
  const [editingParentPhone, setEditingParentPhone] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Edit club state
  const [showEditClubDialog, setShowEditClubDialog] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [clubFormData, setClubFormData] = useState({ name: '', phone: '', email: '' });


  const [entryCoaches, setEntryCoaches] = useState([]);
  const [entryJudges, setEntryJudges] = useState([]);
  const [selectedEntryGymnasts, setSelectedEntryGymnasts] = useState([]);
  const [selectedEntryCoaches, setSelectedEntryCoaches] = useState([]);
  const [selectedEntryJudges, setSelectedEntryJudges] = useState([]);
  const [entryGymnastCategories, setEntryGymnastCategories] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);
  const [entryJudgeDayAssignments, setEntryJudgeDayAssignments] = useState({});
  const [entryTeams, setEntryTeams] = useState([]);
  
  // Competition form state
  const [showCompDialog, setShowCompDialog] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [compFormData, setCompFormData] = useState({
    title: '',
    location: '',
    startsOn: '',
    endsOn: '',
    displayDate: '',
    closingDate: '',
    closingTime: '',
    isOpen: true,
    isHidden: false,
    entryFee: 40,
    categoryFees: {},
    categoryAgeBands: {},
    categories: [],
    // Bank Account Details
    bankAccountName: '',
    bankAccountNumber: '',
    bankSortCode: '',
    bankName: '',
    referenceTemplate: '',
    // Entry Window
    entryStartDate: '',
    entryStartTime: '',
    closedMessage: 'Entries for this competition are now closed',
    entriesOpenSoonMessage: 'Entries open soon',
    judgeBypassMessage: "Don't have a judge available? Contact Scorz for a one-time bypass code.",
    // Club Eligibility (managed separately)
    eligibleClubIds: [],
    // Spectator Payment Settings
    includedSpectators: 0,
    extraSpectatorPriceGbp: 0,
    requirePaymentForExtraSpectators: false,
    // Public Display Fields
    organiserImage: '',
    publicVenue: '',
    publicAddress: '',
    publicDates: '',
    // Scoring Configuration
    scoreBaseValue: 10,
    scoreComponents: { D: true, E: true, B: false, ND: true },
    // SMS Notifications
    smsCoachesEnabled: false,
    smsSpectatorsEnabled: false,
    // Programme / Rotation
    programmeEnabled: true,
    // Per-competition Google Sheets
    googleSheetsEnabled: false,
    googleSheetsId: '',
    sheetsDiscipline: 'WAG',
    // Entry requirements
    gymnastsRequired: true,
    coachesRequired: true,
    minCoaches: 1,
    judgesRequired: true,
    minJudges: 1,
    // Flexible judges ladder — overrides minJudges when non-empty
    judgeRequirementLadder: [],
    // Payment proof
    paymentProofRequired: true,
    paymentMessage: '',
    selfWithdrawEnabled: true,
    withdrawContactMessage: '',
    secondaryOrganizerEmail: '',
    // Team event
    teamEventEnabled: false,
    teamSize: 6,
    teamCountingScores: 4,
    teamEntryFee: 0,
    teamMaxPerClub: 0,
    // Team event advanced (Wave 1)
    teamCompetingScores: 0,
    teamAllowMultiClub: false,
    teamMaxClubsPerTeam: 1,
    teamCategories: [],
    teamAwardsTopN: 3,
    teamTrophyForFirst: true,
    // Rich category config (Wave 1)
    categoriesConfig: [],
    // Advanced validation & info (Wave 1)
    entryCapacity: 0,
    requireFloorMusicForEntry: false,
    strictBirthYearValidation: false,
    petitionedGymnastsEnabled: false,
    refundCutoffDate: '',
    missingJudgeFinePence: 0,
    competitionDays: 1,
    entryEligibilityText: '',
    photographyOptOutEmail: '',
    sponsorLogoUrl: '',
    sponsorText: '',
    sponsors: [],
    splitPanelDisplay: false,
    // Registration / Check-in toggles
    checkInMusicEnabled: true,
    checkInPhoneEnabled: true,
    checkInPrintNumberEnabled: true,
    checkInSpectatorsEnabled: true,
    checkInCompetingApparatusEnabled: true,
    competitionApparatus: ['vault', 'bars', 'beam', 'floor', 'range'],
    // Custom slug
    slug: ''
  });
  const [newCompCategory, setNewCompCategory] = useState('');
  
  // Eligibility dialog state
  const [showEligibilityDialog, setShowEligibilityDialog] = useState(false);
  // ─── Team Builder ───────────────────────────────────────────────────────────
  const [showTeamBuilder, setShowTeamBuilder] = useState(false);
  const [teamBuilderComp, setTeamBuilderComp] = useState(null);
  const [builderTeams, setBuilderTeams] = useState([]);
  const [builderGymnasts, setBuilderGymnasts] = useState([]);
  const [builderLoadingTeams, setBuilderLoadingTeams] = useState(false);
  const [builderEditTeam, setBuilderEditTeam] = useState(null); // team being edited
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamCategory, setNewTeamCategory] = useState('');
  const [newTeamGymnastIds, setNewTeamGymnastIds] = useState([]);
  const [builderGymnastSearch, setBuilderGymnastSearch] = useState('');
  const [savingTeam, setSavingTeam] = useState(false);
  const [eligibilityComp, setEligibilityComp] = useState(null);
  const [selectedEligibleClubs, setSelectedEligibleClubs] = useState([]);
  
  // Category Splits dialog state
  const [showCategorySplitsDialog, setShowCategorySplitsDialog] = useState(false);
  const [categorySplitsComp, setCategorySplitsComp] = useState(null);
  const [categorySplits, setCategorySplits] = useState({});
  const [newSplitYear, setNewSplitYear] = useState('');
  const [newSplitYear2, setNewSplitYear2] = useState('');
  const [newSplitType, setNewSplitType] = useState('exact'); // 'exact', 'plus', or 'range'
  
  // Qualification dialog state
  const [showQualificationDialog, setShowQualificationDialog] = useState(false);
  const [qualificationComp, setQualificationComp] = useState(null);
  const [qualificationEnabled, setQualificationEnabled] = useState(false);
  const [qualificationText, setQualificationText] = useState('');
  const [qualificationCategories, setQualificationCategories] = useState({});
  
  // Welcome Message Template state
  const [welcomeTemplate, setWelcomeTemplate] = useState('');
  const [welcomeTemplateLoading, setWelcomeTemplateLoading] = useState(false);

  // Club SMS Template state  
  const [clubSmsTemplate, setClubSmsTemplate] = useState('');
  const [clubSmsTemplateLoading, setClubSmsTemplateLoading] = useState(false);

  // Google Sheets state
  const [googleSheetId, setGoogleSheetId] = useState('');
  const [googleSheetTitle, setGoogleSheetTitle] = useState('');

  // Training Video state
  const [showTrainingVideoDialog, setShowTrainingVideoDialog] = useState(false);
  const [trainingVideoComp, setTrainingVideoComp] = useState(null);
  const [trainingVideoSettings, setTrainingVideoSettings] = useState(null);
  const [trainingVideoUploading, setTrainingVideoUploading] = useState(false);
  const [trainingVideoUrl, setTrainingVideoUrl] = useState('');

  // Judge Bypass Code state
  const [showBypassCodeDialog, setShowBypassCodeDialog] = useState(false);
  const [bypassCodeComp, setBypassCodeComp] = useState(null);
  const [bypassCode, setBypassCode] = useState(null);
  const [bypassCodeLoading, setBypassCodeLoading] = useState(false);

  // Email Templates state
  const [emailTemplates, setEmailTemplates] = useState({
    save_for_later: { subject: '', body: '', fromName: 'Scorz Entry System' },
    entry_submitted: { subject: '', body: '', fromName: 'Scorz Entry System' },
    withdrawal_refund: { subject: '', body: '', fromName: 'Scorz Entry System' },
    withdrawal_no_refund: { subject: '', body: '', fromName: 'Scorz Entry System' }
  });
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('save_for_later');

  // Background Music Tracks state
  const [backgroundTracks, setBackgroundTracks] = useState([]);
  const [backgroundTrackUploading, setBackgroundTrackUploading] = useState(false);
  const backgroundTrackInputRef = useRef(null);

  // System Status state
  const [systemStatusText, setSystemStatusText] = useState('All Systems Operational');
  const [systemStatusColor, setSystemStatusColor] = useState('green');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [systemStatusLoading, setSystemStatusLoading] = useState(false);

  // Wordsearch state (for floor music page entertainment)
  const [wordsearchWords, setWordsearchWords] = useState('');
  const [wordsearchEnabled, setWordsearchEnabled] = useState(true);
  const [wordsearchLoading, setWordsearchLoading] = useState(false);

  // Gymnast multi-select (for bulk music download)
  const [selectedGymnastIds, setSelectedGymnastIds] = useState(() => new Set());
  const [downloadingMusic, setDownloadingMusic] = useState(false);

  const toggleGymnastSelection = (id) => {
    setSelectedGymnastIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllFilteredWithMusic = () => {
    const ids = filteredGymnasts
      .filter(g => !!g.floorMusicUrl)
      .map(g => g.id);
    setSelectedGymnastIds(new Set(ids));
  };

  const clearGymnastSelection = () => setSelectedGymnastIds(new Set());

  const handleDownloadSelectedMusic = async () => {
    const ids = Array.from(selectedGymnastIds);
    if (ids.length === 0) {
      toast.error('Please select at least one gymnast');
      return;
    }
    setDownloadingMusic(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/superadmin/download-music-by-gymnasts`,
        { gymnastIds: ids },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      // Filename from header
      let filename = 'Floor_Music_Selection.zip';
      const dispo = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      if (dispo) {
        const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
      }
      const added = parseInt(res.headers['x-files-added'] || '0', 10);
      const missing = parseInt(res.headers['x-files-missing'] || '0', 10);

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      if (missing > 0) {
        toast.success(`Downloaded ${added} track${added === 1 ? '' : 's'} (${missing} had no music or failed)`);
      } else {
        toast.success(`Downloaded ${added} track${added === 1 ? '' : 's'}`);
      }
    } catch (err) {
      let msg = 'Download failed';
      if (err.response && err.response.data) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {
          msg = err.response.statusText || msg;
        }
      }
      toast.error(msg);
    } finally {
      setDownloadingMusic(false);
    }
  };

  // Raffle state
  const [raffleEnabled, setRaffleEnabled] = useState(false);
  const [raffleCompetitionId, setRaffleCompetitionId] = useState('');
  const [raffleLoading, setRaffleLoading] = useState(false);

  useEffect(() => {
    loadAllData();
    loadWelcomeTemplate();
    loadClubSmsTemplate();
    loadEmailTemplates();
    loadBackgroundTracks();
    loadSystemStatus();
    loadRaffleSettings();
    loadGoogleSheetsConfig();
    loadWordsearchWords();
  }, []);

  const loadGoogleSheetsConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/google-sheets-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.sheet_id) {
        setGoogleSheetId(res.data.sheet_id);
      }
      // Also get the current title
      const statusRes = await axios.get(`${API}/superadmin/google-sheets-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statusRes.data.spreadsheet_title) {
        setGoogleSheetTitle(statusRes.data.spreadsheet_title);
      }
    } catch (error) {
      console.error('Failed to load Google Sheets config:', error);
    }
  };

  const loadWelcomeTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/welcome-message-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWelcomeTemplate(res.data.template);
    } catch (error) {
      console.error('Failed to load welcome template:', error);
    }
  };

  const saveWelcomeTemplate = async () => {
    setWelcomeTemplateLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/welcome-message-template`, 
        { template: welcomeTemplate },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Welcome message template saved');
    } catch (error) {
      console.error('Failed to save welcome template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setWelcomeTemplateLoading(false);
    }
  };

  const loadClubSmsTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/club-sms-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClubSmsTemplate(res.data.template);
    } catch (error) {
      console.error('Failed to load club SMS template:', error);
    }
  };

  const saveClubSmsTemplate = async () => {
    setClubSmsTemplateLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/club-sms-template`, 
        { template: clubSmsTemplate },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Club SMS template saved');
    } catch (error) {
      console.error('Failed to save club SMS template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setClubSmsTemplateLoading(false);
    }
  };

  const loadEmailTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(res.data.templates);
    } catch (error) {
      console.error('Failed to load email templates:', error);
    }
  };

  const saveEmailTemplates = async () => {
    setEmailTemplatesLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/email-templates`, 
        { templates: emailTemplates },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Email templates saved');
    } catch (error) {
      console.error('Failed to save email templates:', error);
      toast.error(error.response?.data?.detail || 'Failed to save templates');
    } finally {
      setEmailTemplatesLoading(false);
    }
  };

  const updateEmailTemplate = (templateKey, field, value) => {
    setEmailTemplates(prev => ({
      ...prev,
      [templateKey]: {
        ...prev[templateKey],
        [field]: value
      }
    }));
  };

  const resetEmailTemplateToDefault = (templateKey) => {
    const defaults = {
      save_for_later: {
        subject: "{COMP_NAME} - Continue Entry",
        body: "<p>Hi {CLUB_NAME},</p><p>Thanks for starting your entry to {COMP_NAME}. Just a heads up that entries close <strong>{END_DATE_TIME}</strong> so please ensure you submit by then.</p><p>Kind regards,<br>Scorz Team</p>",
        fromName: "Scorz Entry System"
      },
      entry_submitted: {
        subject: "Thanks {CLUB_NAME}! We've received your entry.",
        body: "<p>Hi {CLUB_NAME},</p><p>Thanks for submitting your entry to {COMP_NAME}. Here's a confirmation below:</p>{CONFIRMATION_DETAILS}<p>If you have made an error by mistake, or need to change something, please contact <a href=\"mailto:support@scorz.co.uk\">support@scorz.co.uk</a></p><p>Kind regards,<br>Scorz Team</p>",
        fromName: "Scorz Entry System"
      },
      withdrawal_refund: {
        subject: "{COMP_NAME} - Withdrawal Confirmed (Refund Requested)",
        body: "<p>Hi {CLUB_NAME},</p><p>This is to confirm the withdrawal of <strong>{GYMNAST_NAME}</strong> from {COMP_NAME}.</p><p>You have requested a refund. Please submit your evidence to <a href=\"mailto:support@scorz.co.uk\">support@scorz.co.uk</a> within 7 days using the subject line:<br><code>Refund Evidence – {REFUND_CASE_ID} – {CLUB_NAME} – {GYMNAST_NAME}</code></p><p>Kind regards,<br>Scorz Team</p>",
        fromName: "Scorz Entry System"
      },
      withdrawal_no_refund: {
        subject: "{COMP_NAME} - Withdrawal Confirmed",
        body: "<p>Hi {CLUB_NAME},</p><p>This is to confirm the withdrawal of <strong>{GYMNAST_NAME}</strong> from {COMP_NAME}.</p><p>No refund has been requested for this withdrawal.</p><p>Kind regards,<br>Scorz Team</p>",
        fromName: "Scorz Entry System"
      }
    };
    setEmailTemplates(prev => ({
      ...prev,
      [templateKey]: defaults[templateKey]
    }));
    toast.success('Template reset to default');
  };

  const loadAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [clubsRes, gymnastsRes, coachesRes, judgesRes, compsRes, entriesRes] = await Promise.all([
        axios.get(`${API}/superadmin/clubs`, config),
        axios.get(`${API}/superadmin/all-gymnasts`, config),
        axios.get(`${API}/superadmin/all-coaches`, config),
        axios.get(`${API}/superadmin/all-judges`, config),
        axios.get(`${API}/competitions`, config),
        axios.get(`${API}/superadmin/all-entries`, config)
      ]);

      setClubs(clubsRes.data);
      setAllGymnasts(gymnastsRes.data);
      setAllCoaches(coachesRes.data);
      setAllJudges(judgesRes.data);
      setCompetitions(compsRes.data);
      setAllEntries(entriesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load super admin data');
    }
  };

  // Background Music Track Management
  const loadBackgroundTracks = async () => {
    try {
      const res = await axios.get(`${API}/music/background-tracks`);
      setBackgroundTracks(res.data.tracks || []);
    } catch (error) {
      console.error('Failed to load background tracks:', error);
    }
  };

  const uploadBackgroundTrack = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.mp3')) {
      toast.error('Please select an MP3 file');
      return;
    }

    setBackgroundTrackUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace('.mp3', '').replace('.MP3', ''));

      await axios.post(`${API}/music/background-tracks`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Track uploaded');
      loadBackgroundTracks();
    } catch (error) {
      console.error('Failed to upload track:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload track');
    } finally {
      setBackgroundTrackUploading(false);
      if (backgroundTrackInputRef.current) {
        backgroundTrackInputRef.current.value = '';
      }
    }
  };

  const removeBackgroundTrack = async (trackId) => {
    try {
      await axios.delete(`${API}/music/background-tracks/${trackId}`);
      toast.success('Track removed');
      loadBackgroundTracks();
    } catch (error) {
      console.error('Failed to remove track:', error);
      toast.error('Failed to remove track');
    }
  };

  const loadSystemStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/system-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemStatusText(res.data.statusText || 'All Systems Operational');
      setSystemStatusColor(res.data.statusColor || 'green');
      setMaintenanceMode(res.data.maintenanceMode || false);
    } catch {
      // defaults are fine
    }
  };

  const saveSystemStatus = async (overrides = {}) => {
    setSystemStatusLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/system-status`, {
        statusText: overrides.statusText !== undefined ? overrides.statusText : systemStatusText,
        statusColor: overrides.statusColor !== undefined ? overrides.statusColor : systemStatusColor,
        maintenanceMode: overrides.maintenanceMode !== undefined ? overrides.maintenanceMode : maintenanceMode
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('System status updated');
    } catch (error) {
      console.error('Failed to update system status:', error);
      toast.error('Failed to update system status');
    } finally {
      setSystemStatusLoading(false);
    }
  };

  // Wordsearch functions
  const loadWordsearchWords = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/wordsearch-words`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWordsearchWords(res.data.words || '');
      setWordsearchEnabled(res.data.enabled !== false);
    } catch {
      // defaults are fine
    }
  };

  const saveWordsearchWords = async () => {
    setWordsearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const wordCount = wordsearchWords.split(',').filter(w => w.trim()).length;
      if (wordCount > 150) {
        toast.error(`Too many words (${wordCount}/150). Please reduce the list.`);
        return;
      }
      await axios.put(`${API}/superadmin/wordsearch-words`, {
        words: wordsearchWords,
        enabled: wordsearchEnabled,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Wordsearch ${wordsearchEnabled ? 'enabled' : 'disabled'} — ${wordCount} words saved`);
    } catch (error) {
      console.error('Failed to save wordsearch words:', error);
      toast.error('Failed to save wordsearch words');
    } finally {
      setWordsearchLoading(false);
    }
  };

  const loadRaffleSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/raffle/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRaffleEnabled(res.data.enabled || false);
      setRaffleCompetitionId(res.data.competitionId || '');
    } catch {
      // defaults are fine
    }
  };

  const saveRaffleSettings = async () => {
    setRaffleLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/raffle/settings`, {
        enabled: raffleEnabled,
        competitionId: raffleCompetitionId
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Raffle settings updated');
    } catch (error) {
      console.error('Failed to update raffle settings:', error);
      toast.error('Failed to update raffle settings');
    } finally {
      setRaffleLoading(false);
    }
  };

  const filteredGymnasts = allGymnasts
    .filter(g => selectedClub === 'all' || g.clubId === selectedClub)
    .filter(g => {
      if (!gymnastSearch) return true;
      const search = gymnastSearch.toLowerCase();
      const name = `${g.legalFirstName || g.firstName || ''} ${g.legalLastName || g.lastName || ''}`.toLowerCase();
      const bgNum = (g.bgNumber || '').toLowerCase();
      const clubName = (g.clubName || '').toLowerCase();
      return name.includes(search) || bgNum.includes(search) || clubName.includes(search);
    });

  const filteredCoaches = allCoaches
    .filter(c => selectedClub === 'all' || c.clubId === selectedClub)
    .filter(c => {
      if (!coachSearch) return true;
      const search = coachSearch.toLowerCase();
      const name = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
      const bgNum = (c.bgNumber || '').toLowerCase();
      const clubName = (c.clubName || '').toLowerCase();
      return name.includes(search) || bgNum.includes(search) || clubName.includes(search);
    });

  const filteredJudges = allJudges
    .filter(j => selectedClub === 'all' || j.clubId === selectedClub)
    .filter(j => {
      if (!judgeSearch) return true;
      const search = judgeSearch.toLowerCase();
      const name = `${j.firstName || ''} ${j.lastName || ''}`.toLowerCase();
      const bgNum = (j.bgNumber || '').toLowerCase();
      const clubName = (j.clubName || '').toLowerCase();
      return name.includes(search) || bgNum.includes(search) || clubName.includes(search);
    });

  const handleCompChange = (e) => {
    const { name, value, type } = e.target;
    let processedValue = value;
    
    // Handle numeric fields
    if (type === 'number') {
      processedValue = value === '' ? 0 : parseFloat(value);
    }
    
    setCompFormData({ ...compFormData, [name]: processedValue });
  };

  const handleOpenCompDialog = (comp = null) => {
    if (comp) {
      setEditingComp(comp);
      setCompFormData({
        title: comp.title,
        location: comp.location || '',
        startsOn: comp.startsOn,
        endsOn: comp.endsOn,
        displayDate: comp.displayDate || '',
        closingDate: comp.closingDate || '',
        closingTime: comp.closingTime || '',
        isOpen: comp.isOpen,
        isHidden: comp.isHidden || false,
        categoryFees: comp.categoryFees || {},
        categories: comp.categories || [],
        // Bank Account Details
        bankAccountName: comp.bankAccountName || '',
        bankAccountNumber: comp.bankAccountNumber || '',
        bankSortCode: comp.bankSortCode || '',
        bankName: comp.bankName || '',
        referenceTemplate: comp.referenceTemplate || '',
        // Entry Window
        entryStartDate: comp.entryStartDate || '',
        entryStartTime: comp.entryStartTime || '',
        closedMessage: comp.closedMessage || 'Entries for this competition are now closed',
        entriesOpenSoonMessage: comp.entriesOpenSoonMessage || 'Entries open soon',
        judgeBypassMessage: comp.judgeBypassMessage || "Don't have a judge available? Contact Scorz for a one-time bypass code.",
        eligibleClubIds: comp.eligibleClubIds || [],
        // Spectator Payment Settings
        includedSpectators: comp.includedSpectators || 0,
        extraSpectatorPriceGbp: comp.extraSpectatorPriceGbp || 0,
        requirePaymentForExtraSpectators: comp.requirePaymentForExtraSpectators || false,
        // Public Display Fields
        organiserImage: comp.organiserImage || '',
        publicVenue: comp.publicVenue || '',
        publicAddress: comp.publicAddress || '',
        publicDates: comp.publicDates || '',
        // Scoring Configuration
        scoreBaseValue: comp.scoreBaseValue !== undefined ? comp.scoreBaseValue : 10,
        scoreComponents: comp.scoreComponents || { D: true, E: true, B: false, ND: true },
        // SMS Notifications
        smsCoachesEnabled: comp.smsCoachesEnabled || false,
        smsSpectatorsEnabled: comp.smsSpectatorsEnabled || false,
        // Programme / Rotation
        programmeEnabled: comp.programmeEnabled !== false, // default true
        // Per-competition Google Sheets
        googleSheetsEnabled: comp.googleSheetsEnabled || false,
        googleSheetsId: comp.googleSheetsId || '',
        sheetsDiscipline: comp.sheetsDiscipline || 'WAG',
        // Entry requirements
        gymnastsRequired: comp.gymnastsRequired !== false,
        coachesRequired: comp.coachesRequired !== false,
        minCoaches: comp.minCoaches !== undefined ? comp.minCoaches : 1,
        judgesRequired: comp.judgesRequired !== false,
        minJudges: comp.minJudges !== undefined ? comp.minJudges : 1,
        judgeRequirementLadder: Array.isArray(comp.judgeRequirementLadder) ? comp.judgeRequirementLadder : [],
        // Payment proof
        paymentProofRequired: comp.paymentProofRequired !== false,
        paymentMessage: comp.paymentMessage || '',
        selfWithdrawEnabled: comp.selfWithdrawEnabled !== false,
        withdrawContactMessage: comp.withdrawContactMessage || '',
        secondaryOrganizerEmail: comp.secondaryOrganizerEmail || '',
        // Team event
        teamEventEnabled: comp.teamEventEnabled === true,
        teamSize: comp.teamSize !== undefined ? comp.teamSize : 6,
        teamCountingScores: comp.teamCountingScores !== undefined ? comp.teamCountingScores : 4,
        teamEntryFee: comp.teamEntryFee !== undefined ? comp.teamEntryFee : 0,
        teamMaxPerClub: comp.teamMaxPerClub !== undefined ? comp.teamMaxPerClub : 0,
        // Team event advanced (Wave 1)
        teamCompetingScores: comp.teamCompetingScores !== undefined ? comp.teamCompetingScores : 0,
        teamAllowMultiClub: comp.teamAllowMultiClub === true,
        teamMaxClubsPerTeam: comp.teamMaxClubsPerTeam !== undefined ? comp.teamMaxClubsPerTeam : 1,
        teamCategories: Array.isArray(comp.teamCategories) ? comp.teamCategories : [],
        teamAwardsTopN: comp.teamAwardsTopN !== undefined ? comp.teamAwardsTopN : 3,
        teamTrophyForFirst: comp.teamTrophyForFirst !== false,
        // Rich category config (Wave 1)
        categoriesConfig: Array.isArray(comp.categoriesConfig) ? comp.categoriesConfig : [],
        // Validation knobs (Wave 1)
        entryCapacity: comp.entryCapacity !== undefined ? comp.entryCapacity : 0,
        requireFloorMusicForEntry: comp.requireFloorMusicForEntry === true,
        strictBirthYearValidation: comp.strictBirthYearValidation === true,
        petitionedGymnastsEnabled: comp.petitionedGymnastsEnabled === true,
        refundCutoffDate: comp.refundCutoffDate || '',
        missingJudgeFinePence: comp.missingJudgeFinePence !== undefined ? comp.missingJudgeFinePence : 0,
        competitionDays: comp.competitionDays ?? 1,
        entryEligibilityText: comp.entryEligibilityText || '',
        photographyOptOutEmail: comp.photographyOptOutEmail || '',
        sponsorLogoUrl: comp.sponsorLogoUrl || '',
        sponsorText: comp.sponsorText || '',
        sponsors: comp.sponsors || [],
        splitPanelDisplay: comp.splitPanelDisplay === true,
        // Registration / Check-in toggles
        checkInMusicEnabled: comp.checkInMusicEnabled !== false,
        checkInPhoneEnabled: comp.checkInPhoneEnabled !== false,
        checkInPrintNumberEnabled: comp.checkInPrintNumberEnabled !== false,
        checkInSpectatorsEnabled: comp.checkInSpectatorsEnabled !== false,
        checkInCompetingApparatusEnabled: comp.checkInCompetingApparatusEnabled !== false,
        competitionApparatus: comp.competitionApparatus?.length ? comp.competitionApparatus : ['vault', 'bars', 'beam', 'floor', 'range'],
        // Custom slug
        slug: comp.slug || ''
      });
    } else {
      setEditingComp(null);
      setCompFormData({
        title: '',
        location: '',
        startsOn: '',
        endsOn: '',
        displayDate: '',
        closingDate: '',
        closingTime: '',
        isOpen: true,
    isHidden: false,
        entryFee: 40,
        categoryFees: {},
        categoryAgeBands: {},
        categories: [],
        bankAccountName: '',
        bankAccountNumber: '',
        bankSortCode: '',
        bankName: '',
        referenceTemplate: '',
        entryStartDate: '',
        entryStartTime: '',
        closedMessage: 'Entries for this competition are now closed',
        entriesOpenSoonMessage: 'Entries open soon',
        judgeBypassMessage: "Don't have a judge available? Contact Scorz for a one-time bypass code.",
        eligibleClubIds: [],
        // Spectator Payment Settings
        includedSpectators: 0,
        extraSpectatorPriceGbp: 0,
        requirePaymentForExtraSpectators: false,
        // Public Display Fields
        organiserImage: '',
        publicVenue: '',
        publicAddress: '',
        publicDates: '',
        // Scoring Configuration
        scoreBaseValue: 10,
        scoreComponents: { D: true, E: true, B: false, ND: true },
        // SMS Notifications
        smsCoachesEnabled: false,
        smsSpectatorsEnabled: false,
        // Programme / Rotation
        programmeEnabled: true,
        // Per-competition Google Sheets
        googleSheetsEnabled: false,
        googleSheetsId: '',
        sheetsDiscipline: 'WAG',
        // Entry requirements
        gymnastsRequired: true,
        coachesRequired: true,
        minCoaches: 1,
        judgesRequired: true,
        minJudges: 1,
        judgeRequirementLadder: [],
        // Payment proof
        paymentProofRequired: true,
        paymentMessage: '',
        selfWithdrawEnabled: true,
        withdrawContactMessage: '',
        secondaryOrganizerEmail: '',
        // Team event
        teamEventEnabled: false,
        teamSize: 6,
        teamCountingScores: 4,
        teamEntryFee: 0,
        teamMaxPerClub: 0,
        teamCompetingScores: 0,
        teamAllowMultiClub: false,
        teamMaxClubsPerTeam: 1,
        teamCategories: [],
        teamAwardsTopN: 3,
        teamTrophyForFirst: true,
        // Rich category config
        categoriesConfig: [],
        // Advanced validation
        entryCapacity: 0,
        requireFloorMusicForEntry: false,
        strictBirthYearValidation: false,
        petitionedGymnastsEnabled: false,
        refundCutoffDate: '',
        missingJudgeFinePence: 0,
        competitionDays: 1,
        entryEligibilityText: '',
        photographyOptOutEmail: '',
        sponsorLogoUrl: '',
        sponsorText: '',
        sponsors: [],
        splitPanelDisplay: false,
        // Registration / Check-in toggles
        checkInMusicEnabled: true,
        checkInPhoneEnabled: true,
        checkInPrintNumberEnabled: true,
        checkInSpectatorsEnabled: true,
        checkInCompetingApparatusEnabled: true,
        competitionApparatus: ['vault', 'bars', 'beam', 'floor', 'range'],
        // Custom slug
        slug: ''
      });
    }
    setNewCompCategory('');
    setImportClubId('');
    setImportFolder('');
    setImportFile(null);
    setImportResult(null);
    setShowCompDialog(true);
  };

  const handleAddCompCategory = () => {
    if (!newCompCategory.trim()) return;
    if (compFormData.categories.includes(newCompCategory.trim())) {
      toast.error('Category already exists');
      return;
    }
    setCompFormData({
      ...compFormData,
      categories: [...compFormData.categories, newCompCategory.trim()]
    });
    setNewCompCategory('');
  };

  const handleRemoveCompCategory = (index) => {
    const removed = compFormData.categories[index];
    const nextFees = { ...(compFormData.categoryFees || {}) };
    if (removed && nextFees[removed] !== undefined) {
      delete nextFees[removed];
    }
    setCompFormData({
      ...compFormData,
      categories: compFormData.categories.filter((_, i) => i !== index),
      categoryFees: nextFees,
    });
  };

  const handleSaveCompetition = async (e) => {
    e.preventDefault();

    try {
      if (editingComp) {
        await axios.put(`${API}/competitions/${editingComp.id}`, compFormData);
        toast.success('Competition updated successfully');
      } else {
        await axios.post(`${API}/competitions`, compFormData);
        toast.success('Competition created successfully');
      }
      setShowCompDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to save competition:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => `${(d.loc || []).slice(1).join('.')} — ${d.msg}`).join('; ')
        : (typeof detail === 'string' ? detail : 'Failed to save competition. Check all required fields.');
      toast.error(msg, { duration: 8000 });
    }
  };

  const handleImportExcel = async () => {
    if (!editingComp) return toast.error('Save the competition first');
    if (!importClubId) return toast.error('Pick a club');
    if (!importFile) return toast.error('Choose an Excel file');
    if (!window.confirm('This will WIPE & RECREATE all imported gymnasts, entries and the programme for this competition. Continue?')) return;

    setImporting(true);
    setImportResult(null);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('clubId', importClubId);
      fd.append('cloudflareFolder', importFolder.trim());
      const res = await axios.post(
        `${API}/superadmin/competitions/${editingComp.id}/import-excel`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported_gymnasts} gymnasts across ${res.data.rounds} round(s)`);
      loadAllData();
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteCompetition = async (compId) => {
    if (!window.confirm('Are you sure you want to delete this competition?')) return;

    try {
      await axios.delete(`${API}/competitions/${compId}`);
      toast.success('Competition deleted successfully');
      loadAllData();
    } catch (error) {
      console.error('Failed to delete competition:', error);
      toast.error('Failed to delete competition');
    }
  };

  // Team Builder handlers
  const openTeamBuilder = async (comp) => {
    setTeamBuilderComp(comp);
    setShowTeamBuilder(true);
    setBuilderEditTeam(null);
    setNewTeamName('');
    setNewTeamCategory('');
    setNewTeamGymnastIds([]);
    setBuilderGymnastSearch('');
    setBuilderLoadingTeams(true);
    try {
      const token = localStorage.getItem('token');
      const [teamsRes, gymRes] = await Promise.all([
        axios.get(`${API}/superadmin/teams/${comp.id}`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/superadmin/competitions/${comp.id}/entered-gymnasts`, { headers: { Authorization: `Bearer ${token}` }}),
      ]);
      setBuilderTeams(teamsRes.data.teams || []);
      setBuilderGymnasts(gymRes.data.gymnasts || []);
    } catch (e) {
      toast.error('Failed to load team builder data');
    } finally {
      setBuilderLoadingTeams(false);
    }
  };

  const refreshBuilderTeams = async () => {
    if (!teamBuilderComp) return;
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/superadmin/teams/${teamBuilderComp.id}`, { headers: { Authorization: `Bearer ${token}` }});
    setBuilderTeams(res.data.teams || []);
  };

  const saveBuilderTeam = async () => {
    if (!newTeamName.trim()) { toast.error('Enter a team name'); return; }
    if (newTeamGymnastIds.length === 0) { toast.error('Add at least one gymnast'); return; }
    setSavingTeam(true);
    const token = localStorage.getItem('token');
    try {
      if (builderEditTeam) {
        await axios.put(`${API}/superadmin/teams/${builderEditTeam.id}`, {
          name: newTeamName, category: newTeamCategory, gymnastIds: newTeamGymnastIds
        }, { headers: { Authorization: `Bearer ${token}` }});
        toast.success('Team updated');
      } else {
        await axios.post(`${API}/superadmin/competitions/${teamBuilderComp.id}/builder/teams`, {
          name: newTeamName, category: newTeamCategory, gymnastIds: newTeamGymnastIds
        }, { headers: { Authorization: `Bearer ${token}` }});
        toast.success('Team created');
      }
      setBuilderEditTeam(null);
      setNewTeamName('');
      setNewTeamCategory('');
      setNewTeamGymnastIds([]);
      await refreshBuilderTeams();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save team');
    } finally {
      setSavingTeam(false);
    }
  };

  const deleteBuilderTeam = async (teamId) => {
    if (!window.confirm('Delete this team?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/superadmin/teams/${teamId}`, { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Team deleted');
      await refreshBuilderTeams();
    } catch (e) {
      toast.error('Failed to delete team');
    }
  };

  // ─── Team Builder category editing state ──────────────────────────────────
  const [editingGymnastCat, setEditingGymnastCat] = useState({}); // {gid: 'new cat value'}
  const [savingGymnastCat, setSavingGymnastCat] = useState({});   // {gid: true|false}

  const saveGymnastCategory = async (team, gid, newCat) => {
    if (!newCat.trim()) return;
    setSavingGymnastCat(prev => ({ ...prev, [gid]: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/superadmin/competitions/${teamBuilderComp.id}/gymnast-category`,
        { gymnastId: gid, category: newCat },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Category updated');
      // Update local gymnast info in builderTeams
      setBuilderTeams(prev => prev.map(t =>
        t.id === team.id
          ? { ...t, gymnasts: t.gymnasts.map(g => g.id === gid ? { ...g, category: newCat } : g) }
          : t
      ));
      setEditingGymnastCat(prev => { const n = { ...prev }; delete n[gid]; return n; });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update category');
    } finally {
      setSavingGymnastCat(prev => { const n = { ...prev }; delete n[gid]; return n; });
    }
  };

  const startEditBuilderTeam = (team) => {
    setBuilderEditTeam(team);
    setNewTeamName(team.name || '');
    setNewTeamCategory(team.category || '');
    setNewTeamGymnastIds(team.gymnastIds || []);
  };

  // Eligibility handlers
  const handleOpenEligibilityDialog = async (comp) => {
    setEligibilityComp(comp);
    setSelectedEligibleClubs(comp.eligibleClubIds || []);
    setShowEligibilityDialog(true);
  };

  const handleSaveEligibility = async () => {
    if (!eligibilityComp) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/competitions/${eligibilityComp.id}/eligibility`,
        { eligibleClubIds: selectedEligibleClubs },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Eligibility updated successfully');
      setShowEligibilityDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to update eligibility:', error);
      toast.error('Failed to update eligibility');
    }
  };

  const toggleClubEligibility = (clubId) => {
    if (selectedEligibleClubs.includes(clubId)) {
      setSelectedEligibleClubs(selectedEligibleClubs.filter(id => id !== clubId));
    } else {
      setSelectedEligibleClubs([...selectedEligibleClubs, clubId]);
    }
  };

  const selectAllClubs = () => {
    setSelectedEligibleClubs(clubs.map(c => c.id));
  };

  const deselectAllClubs = () => {
    setSelectedEligibleClubs([]);
  };

  // Category Splits Functions
  const openCategorySplitsDialog = async (comp) => {
    setCategorySplitsComp(comp);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/competitions/${comp.id}/category-splits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategorySplits(res.data.categorySplits || {});
    } catch (error) {
      console.error('Failed to load category splits:', error);
      setCategorySplits({});
    }
    setShowCategorySplitsDialog(true);
  };

  // Returns a stable string key for any split object
  const splitKey = (split) => {
    if (typeof split !== 'object') return String(split);
    if (split.type === 'range') return `${split.year}-${split.year2}`;
    return String(split.year);
  };

  const addSplitYear = (category) => {
    const currentSplits = categorySplits[category] || [];

    if (newSplitType === 'range') {
      const y1 = parseInt(newSplitYear);
      const y2 = parseInt(newSplitYear2);
      if (!y1 || !y2 || y1 < 2000 || y1 > 2030 || y2 < 2000 || y2 > 2030) {
        toast.error('Enter both valid years (2000–2030)');
        return;
      }
      if (y1 >= y2) {
        toast.error('First year must be older (e.g. 2016) and second year younger (e.g. 2017)');
        return;
      }
      const key = `${y1}-${y2}`;
      if (currentSplits.some(s => splitKey(s) === key)) {
        toast.error('This range already exists');
        return;
      }
      setCategorySplits({
        ...categorySplits,
        [category]: [...currentSplits, { year: y1, year2: y2, type: 'range' }]
          .sort((a, b) => (b.year || b) - (a.year || a))
      });
      setNewSplitYear('');
      setNewSplitYear2('');
      return;
    }

    const year = parseInt(newSplitYear);
    if (!year || year < 2000 || year > 2030) {
      toast.error('Please enter a valid year (2000-2030)');
      return;
    }
    if (currentSplits.some(s => splitKey(s) === String(year))) {
      toast.error('This year is already added');
      return;
    }
    setCategorySplits({
      ...categorySplits,
      [category]: [...currentSplits, { year, type: newSplitType }].sort((a, b) => (b.year || b) - (a.year || a))
    });
    setNewSplitYear('');
    setNewSplitType('exact');
  };

  const removeSplitYear = (category, keyToRemove) => {
    const currentSplits = categorySplits[category] || [];
    setCategorySplits({
      ...categorySplits,
      [category]: currentSplits.filter(s => splitKey(s) !== keyToRemove)
    });
  };

  const clearAllSplits = (category) => {
    setCategorySplits({
      ...categorySplits,
      [category]: []
    });
    toast.success(`Cleared all splits for ${category}`);
  };

  const toggleOverall = (category) => {
    const currentSplits = categorySplits[category] || [];
    const hasOverall = currentSplits.some(s => typeof s === 'object' && s.type === 'overall');
    setCategorySplits({
      ...categorySplits,
      [category]: hasOverall
        ? currentSplits.filter(s => !(typeof s === 'object' && s.type === 'overall'))
        : [...currentSplits, { type: 'overall' }]
    });
  };

  const clearAllCategorySplits = () => {
    setCategorySplits({});
    toast.success('Cleared all category splits');
  };

  const saveCategorySplits = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/competitions/${categorySplitsComp.id}/category-splits`, 
        { categorySplits },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Category splits saved');
      setShowCategorySplitsDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to save category splits:', error);
      toast.error('Failed to save category splits');
    }
  };

  // Qualification Functions
  const openQualificationDialog = async (comp) => {
    setQualificationComp(comp);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/competitions/${comp.id}/qualification`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQualificationEnabled(res.data.qualificationEnabled || false);
      setQualificationText(res.data.qualificationText || '');
      setQualificationCategories(res.data.qualificationCategories || {});
    } catch (error) {
      console.error('Failed to load qualification settings:', error);
      setQualificationEnabled(false);
      setQualificationText('');
      setQualificationCategories({});
    }
    setShowQualificationDialog(true);
  };

  const toggleCategoryQualification = (category) => {
    const current = qualificationCategories[category] || { enabled: false, score: 0 };
    setQualificationCategories({
      ...qualificationCategories,
      [category]: { ...current, enabled: !current.enabled }
    });
  };

  const updateCategoryQualScore = (category, score) => {
    const current = qualificationCategories[category] || { enabled: false, score: 0 };
    setQualificationCategories({
      ...qualificationCategories,
      [category]: { ...current, score: parseFloat(score) || 0 }
    });
  };

  const saveQualificationSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/competitions/${qualificationComp.id}/qualification`, 
        { 
          qualificationEnabled,
          qualificationText,
          qualificationCategories 
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Qualification settings saved');
      setShowQualificationDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to save qualification settings:', error);
      toast.error('Failed to save qualification settings');
    }
  };

  // Training Video Functions
  const openTrainingVideoDialog = async (comp) => {
    setTrainingVideoComp(comp);
    try {
      const res = await axios.get(`${API}/training-video/settings/${comp.id}`);
      setTrainingVideoSettings(res.data);
      setTrainingVideoUrl(res.data.videoUrl || '');
    } catch (error) {
      console.error('Failed to load training video settings:', error);
      setTrainingVideoSettings(null);
      setTrainingVideoUrl('');
    }
    setShowTrainingVideoDialog(true);
  };

  const handleTrainingVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    
    setTrainingVideoUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(
        `${API}/training-video/upload/${trainingVideoComp.id}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Training video uploaded successfully');
      setTrainingVideoUrl(res.data.videoUrl);
      setTrainingVideoSettings({ ...trainingVideoSettings, videoUrl: res.data.videoUrl });
    } catch (error) {
      console.error('Failed to upload training video:', error);
      toast.error('Failed to upload video');
    } finally {
      setTrainingVideoUploading(false);
    }
  };

  const handleSetTrainingVideoUrl = async () => {
    if (!trainingVideoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/training-video/set-url/${trainingVideoComp.id}`,
        { videoUrl: trainingVideoUrl },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Video URL saved');
      setTrainingVideoSettings({ ...trainingVideoSettings, videoUrl: trainingVideoUrl });
    } catch (error) {
      console.error('Failed to set video URL:', error);
      toast.error('Failed to save video URL');
    }
  };

  const handleBroadcastTrainingVideo = async () => {
    if (!trainingVideoSettings?.videoUrl) {
      toast.error('No training video configured');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/training-video/broadcast/${trainingVideoComp.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Training video is now playing on all judge tablets!');
      setTrainingVideoSettings({ ...trainingVideoSettings, isPlaying: true });
    } catch (error) {
      console.error('Failed to broadcast video:', error);
      toast.error('Failed to start broadcast');
    }
  };

  const handleStopTrainingVideo = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/training-video/stop/${trainingVideoComp.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Training video stopped');
      setTrainingVideoSettings({ ...trainingVideoSettings, isPlaying: false });
    } catch (error) {
      console.error('Failed to stop video:', error);
      toast.error('Failed to stop broadcast');
    }
  };

  // Judge Bypass Code Functions
  const openBypassCodeDialog = async (comp) => {
    setBypassCodeComp(comp);
    setBypassCodeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/competitions/${comp.id}/judge-bypass-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBypassCode(res.data.code);
    } catch (error) {
      console.error('Failed to load bypass code:', error);
      setBypassCode(null);
    } finally {
      setBypassCodeLoading(false);
    }
    setShowBypassCodeDialog(true);
  };

  const generateBypassCode = async () => {
    if (!bypassCodeComp) return;
    setBypassCodeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/competitions/${bypassCodeComp.id}/generate-judge-bypass-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setBypassCode(res.data.code);
      toast.success('New bypass code generated');
    } catch (error) {
      console.error('Failed to generate bypass code:', error);
      toast.error('Failed to generate bypass code');
    } finally {
      setBypassCodeLoading(false);
    }
  };

  const copyBypassCode = () => {
    if (bypassCode) {
      navigator.clipboard.writeText(bypassCode);
      toast.success('Code copied to clipboard');
    }
  };

  const handleOpenEntryDialog = async (entry) => {
    setEditingEntry(entry);
    
    // Load club's gymnasts, coaches, judges and competition categories
    try {
      const token = localStorage.getItem('token');
      const [gymRes, coachRes, judgeRes, teamsRes] = await Promise.all([
        axios.get(`${API}/superadmin/all-gymnasts`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/superadmin/all-coaches`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/superadmin/all-judges`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/superadmin/teams/${entry.competitionId}`, { headers: { Authorization: `Bearer ${token}` }})
      ]);

      const clubGymnasts = gymRes.data.filter(g => g.clubId === entry.clubId);
      const clubCoaches = coachRes.data.filter(c => c.clubId === entry.clubId);
      const clubJudges = judgeRes.data.filter(j => j.clubId === entry.clubId);
      const clubTeams = (teamsRes.data?.teams || []).filter(t => t.clubId === entry.clubId && t.status !== 'withdrawn');

      setEntryGymnasts(clubGymnasts);
      setEntryCoaches(clubCoaches);
      setEntryJudges(clubJudges);
      setEntryTeams(clubTeams);
      
      // Use competition-specific categories (from the entry's competition)
      const compCategories = entry.competition?.categories || [];
      setAvailableCategories(compCategories);

      setSelectedEntryGymnasts(entry.gymnastIds || []);
      setSelectedEntryCoaches(entry.coachIds || []);
      setSelectedEntryJudges(entry.judgeIds || []);
      setEntryGymnastCategories(entry.gymnastCategories || {});
      setEntryJudgeDayAssignments(entry.judgeDayAssignments || {});

      setShowEntryDialog(true);
    } catch (error) {
      console.error('Failed to load entry data:', error);
      toast.error('Failed to load entry data');
    }
  };

  const handleSaveEntry = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/entries/${editingEntry.id}`, {
        gymnastIds: selectedEntryGymnasts,
        coachIds: selectedEntryCoaches,
        judgeIds: selectedEntryJudges,
        petitionedGymnasts: editingEntry.petitionedGymnasts || [],
        gymnastCategories: entryGymnastCategories,
        judgeDayAssignments: entryJudgeDayAssignments
      }, { headers: { Authorization: `Bearer ${token}` }});

      // Save any team changes
      for (const team of entryTeams) {
        if (team._edited) {
          await axios.put(`${API}/superadmin/teams/${team.id}`, {
            name: team.name,
            category: team.category,
            gymnastIds: team.gymnastIds
          }, { headers: { Authorization: `Bearer ${token}` }});
        }
      }

      toast.success('Entry updated successfully');
      setShowEntryDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to update entry:', error);
      toast.error('Failed to update entry');
    }
  };


  const handleUnwithdrawGymnast = async (entryId, gymnastId, gymnastName) => {
    if (!window.confirm(`Are you sure you want to restore ${gymnastName} to this entry?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/superadmin/entries/${entryId}/unwithdraw-gymnast`,
        { gymnastId },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success(`${gymnastName} has been restored to the entry`);
      loadAllData();
    } catch (error) {
      console.error('Failed to unwithdraw gymnast:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore gymnast');
    }
  };


  // Handle refresh all judge terminals
  const handleRefreshAllJudges = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/refresh-judges`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Refresh signal sent to all judge terminals');
    } catch (error) {
      console.error('Failed to refresh judges:', error);
      toast.error(error.response?.data?.detail || 'Failed to send refresh signal');
    }
  };


  const handleDownloadAllMusic = async () => {
    try {
      const token = localStorage.getItem('token');
      let endpoint;
      let filename;
      
      if (selectedCompetition === 'all') {
        // Download ALL floor music from ALL competitions
        endpoint = `${API}/superadmin/download-all-music`;
        filename = 'All_Floor_Music.zip';
      } else {
        // Download floor music for specific competition
        endpoint = `${API}/superadmin/download-all-music/${selectedCompetition}`;
        const competition = competitions.find(c => c.id === selectedCompetition);
        filename = competition ? `${competition.title.replace(/ /g, '_')}_floor_music.zip` : 'floor_music.zip';
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(selectedCompetition === 'all' ? 'All floor music downloaded successfully' : 'Floor music downloaded successfully');
    } catch (error) {
      console.error('Failed to download music:', error);
      toast.error(error.response?.data?.detail || 'Failed to download floor music');
    }
  };


  const handleExportToExcel = async () => {
    // Only allow export when a specific competition is selected
    if (selectedCompetition === 'all') {
      toast.error('Please select a specific competition to export');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/export-entries`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          competitionId: selectedCompetition,
          submittedOnly: true 
        },
        responseType: 'blob'
      });

      const competition = competitions.find(c => c.id === selectedCompetition);
      const compName = competition?.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'Competition';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Scorz_${compName}_Submitted_Entries_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Submitted entries exported to Excel successfully');
    } catch (error) {
      console.error('Failed to export entries:', error);
      toast.error(error.response?.data?.detail || 'Failed to export entries to Excel');
    }
  };


  const handleToggleSuperAdmin = (clubId, clubName, currentStatus) => {
    setClubToToggle({ id: clubId, name: clubName, currentStatus });
    setShowSuperAdminDialog(true);
  };

  const confirmToggleSuperAdmin = async () => {
    if (!clubToToggle) return;
    
    const newStatus = !clubToToggle.currentStatus;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/toggle-superadmin/${clubToToggle.id}`, {
        isSuperAdmin: newStatus
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success(`${clubToToggle.name} ${newStatus ? 'promoted to' : 'removed from'} super admin successfully`);
      setShowSuperAdminDialog(false);
      setClubToToggle(null);
      loadAllData();
    } catch (error) {
      console.error('Failed to toggle super admin:', error);
      toast.error('Failed to update super admin status');
    }
  };


  const handleRemoveFloorMusic = async (gymnastId, firstName, lastName) => {
    if (!window.confirm(`⚠️ WARNING: Remove floor music for ${firstName} ${lastName}?\n\nThis will:\n• Delete the floor music file\n• Show a warning on the club's gymnast profile\n• Require the club to re-upload the music\n\nAre you sure?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/superadmin/gymnasts/${gymnastId}/remove-music`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Floor music removed for ${firstName} ${lastName}. Club will see a warning.`);
      loadAllData(); // Reload to update the table
    } catch (error) {
      console.error('Failed to remove floor music:', error);
      toast.error('Failed to remove floor music');
    }
  };


  const handleRemoveAllFloorMusic = async () => {
    const gymnastsWithMusic = allGymnasts.filter(g => g.floorMusicUrl);
    const count = gymnastsWithMusic.length;
    
    if (count === 0) {
      toast.error('No gymnasts have floor music to remove');
      return;
    }

    if (!window.confirm(`🚨 CRITICAL WARNING 🚨\n\nYou are about to REMOVE ALL FLOOR MUSIC from ${count} gymnast(s)!\n\nThis will:\n• Delete ALL floor music files\n• Show warnings on ALL club gymnast profiles\n• Require ALL clubs to re-upload music\n\nThis action affects multiple clubs and cannot be undone!\n\nType "REMOVE ALL" in the next prompt to confirm.`)) {
      return;
    }

    const confirmation = window.prompt('Type "REMOVE ALL" (without quotes) to confirm this bulk action:');
    if (confirmation !== 'REMOVE ALL') {
      toast.error('Bulk removal cancelled - confirmation text did not match');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/superadmin/gymnasts/remove-all-music`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Floor music removed from ${response.data.count} gymnast(s). All clubs will see warnings.`, { duration: 5000 });
      loadAllData(); // Reload to update the table
    } catch (error) {
      console.error('Failed to remove all floor music:', error);
      toast.error('Failed to remove all floor music');
    }
  };



  const handleImpersonate = async (clubId) => {
    try {
      const response = await axios.post(`${API}/superadmin/impersonate/${clubId}`);
      
      // Store the new token
      localStorage.setItem('token', response.data.access_token);
      
      toast.success('Successfully logged in as club');
      
      // Redirect to club dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Failed to impersonate club:', error);
      toast.error(error.response?.data?.detail || 'Failed to login as club');
    }
  };

  const handleDeleteClub = (clubId, clubName, peopleCount) => {
    setClubToDelete({ id: clubId, name: clubName, peopleCount });
    setShowDeleteDialog(true);
  };

  const confirmDeleteClub = async () => {
    if (!clubToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/superadmin/clubs/${clubToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${clubToDelete.name} deleted successfully`);
      setShowDeleteDialog(false);
      setClubToDelete(null);
      loadAllData();
    } catch (error) {
      console.error('Failed to delete club:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete club';
      toast.error(errorMsg);
      setShowDeleteDialog(false);
      setClubToDelete(null);
    }
  };


  const handleDeleteEntry = (entryId, clubName, competitionTitle) => {
    setEntryToDelete({ id: entryId, clubName, competitionTitle });
    setShowDeleteEntryDialog(true);
  };

  const handleResendConfirmation = (entry) => {
    setResendEntry(entry);
    setShowResendDialog(true);
  };

  const sendResendRequest = async (recipientType) => {
    if (!resendEntry) return;
    setResendLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/superadmin/entries/${resendEntry.id}/resend-confirmation`,
        { recipient_type: recipientType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Email resent successfully');
      setShowResendDialog(false);
      setResendEntry(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendLoading(false);
    }
  };

  const confirmDeleteEntry = async () => {
    if (!entryToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/superadmin/entries/${entryToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Entry deleted successfully');
      setShowDeleteEntryDialog(false);
      setEntryToDelete(null);
      loadAllData();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
      setShowDeleteEntryDialog(false);
      setEntryToDelete(null);
    }
  };

  const handleDeleteAllEntries = () => {
    setShowDeleteAllEntriesDialog(true);
  };

  const confirmDeleteAllEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/superadmin/entries/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowDeleteAllEntriesDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to delete all entries:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete all entries');
      setShowDeleteAllEntriesDialog(false);
    }
  };


  const handleAcceptPetition = async (gymnastId) => {
    if (!editingEntry) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/entries/${editingEntry.id}/petition/accept`, {
        gymnastId
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success('Petition accepted! Gymnast moved to main list.');
      setShowEntryDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to accept petition:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept petition');
    }
  };

  const handleDeclinePetition = async (gymnastId) => {
    if (!editingEntry) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/entries/${editingEntry.id}/petition/decline`, {
        gymnastId
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success('Petition declined');
      setShowEntryDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to decline petition:', error);
      toast.error(error.response?.data?.detail || 'Failed to decline petition');
    }
  };

  const handleEditClub = (club) => {
    setEditingClub(club);
    setClubFormData({ name: club.name, phone: club.phone, email: club.email });
    setShowEditClubDialog(true);
  };

  const handleSaveClub = async () => {
    if (!editingClub) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/clubs/${editingClub.id}`, clubFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Club details updated successfully');
      setShowEditClubDialog(false);
      loadAllData();
    } catch (error) {
      console.error('Failed to update club:', error);
      toast.error(error.response?.data?.detail || 'Failed to update club details');
    }
  };

  // Scoring System Functions
  const handleGenerateJudgeCodes = async () => {
    if (!scoringCompetition) {
      toast.error('Please select a competition');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/scoring/judge-codes/generate`, {
        competitionId: scoringCompetition,
        disciplines: judgeDisciplines,
        magMode: judgeMagMode,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setJudgeCodes(response.data.codes);
      setIsDualPanel(response.data.isDualPanel);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to generate codes:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate judge codes. Make sure a programme exists for this competition.');
    }
  };

  const loadCurrentJudgeCodes = async () => {
    if (!scoringCompetition) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/scoring/judge-codes/${scoringCompetition}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.codes && response.data.codes.length > 0) {
        setJudgeCodes(response.data.codes);
        // Determine if it's dual panel by checking if there are Panel A/B codes
        const hasDualPanel = response.data.codes.some(c => c.panel === 'A' || c.panel === 'B');
        setIsDualPanel(hasDualPanel);
      } else {
        setJudgeCodes([]);
        setIsDualPanel(false);
      }
    } catch (error) {
      console.error('Failed to load codes:', error);
    }
  };

  const loadScores = async () => {
    if (!scoringCompetition) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/scoring/scores/${scoringCompetition}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllScores(response.data.scores);
    } catch (error) {
      console.error('Failed to load scores:', error);
    }
  };

  const handleDeleteScore = async (scoreId) => {
    if (!window.confirm('Are you sure you want to delete this score?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/scoring/scores/${scoreId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Score deleted successfully');
      loadScores();
    } catch (error) {
      console.error('Failed to delete score:', error);
      toast.error('Failed to delete score');
    }
  };

  const handleEditScore = (score) => {
    setEditingScore(score);
    setShowEditScoreDialog(true);
  };

  const handleSaveScore = async () => {
    if (!editingScore) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/scoring/scores/${editingScore.id}`, {
        gymnastNumber: editingScore.gymnastNumber,
        difficulty: editingScore.difficulty,
        execution: editingScore.execution,
        bonus: editingScore.bonus,
        neutralDeductions: editingScore.neutralDeductions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Score updated successfully');
      setShowEditScoreDialog(false);
      loadScores();
    } catch (error) {
      console.error('Failed to update score:', error);
      toast.error('Failed to update score');
    }
  };

  // SMS Management Functions
  const loadClubsSMS = async () => {
    if (!smsCompetition) {
      toast.error('Please select a competition first');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      // Auto-sync coach phones from entry toggles first
      await axios.post(`${API}/sms/clubs/sync-from-entries/${smsCompetition}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const response = await axios.get(`${API}/sms/clubs/${smsCompetition}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClubsSMS(response.data.clubs);
      if (response.data.clubs.length === 0) {
        toast.info('No clubs found with entries for this competition');
      } else {
        toast.success(`Loaded ${response.data.clubs.length} clubs — coach phones synced from entries`);
      }
    } catch (error) {
      console.error('Failed to load clubs SMS:', error);
      toast.error('Failed to load clubs: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSendClubWelcome = async (clubId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/sms/clubs/${smsCompetition}/${clubId}/send-welcome`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Welcome message sent!');
      loadClubsSMS();
    } catch (error) {
      toast.error('Failed to send: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteParentPhone = async (gymnastId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/sms/gymnasts/delete-phone`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { competitionId: smsCompetition, gymnastId }
      });
      toast.success('Phone number deleted');
      loadGymnastsSMS();
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadGymnastsSMS = async () => {
    if (!smsCompetition) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sms/gymnasts/${smsCompetition}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGymnastsSMS(response.data.gymnasts);
    } catch (error) {
      console.error('Failed to load gymnasts SMS:', error);
    }
  };

  const handleAddClubPhone = async (clubId) => {
    const phoneNumber = newClubPhone[clubId];
    if (!phoneNumber || phoneNumber.trim().length < 7) {
      toast.error('Please enter a valid phone number (e.g. 07700900000 or +447700900000)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sms/clubs/add-phone`, {
        competitionId: smsCompetition,
        clubId: clubId,
        phoneNumber: phoneNumber
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Phone number added and welcome SMS sent!');
      setNewClubPhone({ ...newClubPhone, [clubId]: '' });
      loadClubsSMS();
    } catch (error) {
      console.error('Failed to add phone number:', error);
      toast.error('Failed to add phone number');
    }
  };

  const handleRemoveClubPhone = async (clubId, phoneNumber) => {
    if (!window.confirm('Remove this phone number?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/sms/clubs/remove-phone`, {
        data: {
          competitionId: smsCompetition,
          clubId: clubId,
          phoneNumber: phoneNumber
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Phone number removed');
      loadClubsSMS();
    } catch (error) {
      console.error('Failed to remove phone number:', error);
      toast.error('Failed to remove phone number');
    }
  };

  const handleAddParentPhone = async (gymnastId) => {
    const phoneNumber = newParentPhone[gymnastId];
    if (!phoneNumber || phoneNumber.trim().length < 7) {
      toast.error('Please enter a valid phone number (e.g. 07700900000 or +447700900000)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sms/gymnasts/add-phone`, {
        competitionId: smsCompetition,
        gymnastId: gymnastId,
        phoneNumber: phoneNumber
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Phone number added and welcome SMS sent!');
      setNewParentPhone({ ...newParentPhone, [gymnastId]: '' });
      setEditingParentPhone({ ...editingParentPhone, [gymnastId]: false });
      loadGymnastsSMS();
    } catch (error) {
      console.error('Failed to add phone number:', error);
      toast.error('Failed to add phone number');
    }
  };

  const handleUpdateParentPhone = async (gymnastId, oldPhone) => {
    const phoneNumber = editingParentPhone[gymnastId];
    if (!phoneNumber || phoneNumber.trim().length < 7) {
      toast.error('Please enter a valid phone number (e.g. 07700900000 or +447700900000)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sms/gymnasts/update-phone`, {
        competitionId: smsCompetition,
        gymnastId: gymnastId,
        phoneNumber: phoneNumber
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Phone number updated');
      setEditingParentPhone({ ...editingParentPhone, [gymnastId]: false });
      loadGymnastsSMS();
    } catch (error) {
      console.error('Failed to update phone number:', error);
      toast.error('Failed to update phone number');
    }
  };

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      document.documentElement.requestFullscreen?.();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullScreen(false);
    }
  };
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);





  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/scorz-logo.png" alt="Scorz Logo" className="h-12 sm:h-16 w-auto" />
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Quick Access Buttons */}
              <Button
                onClick={() => window.open('/registration', '_blank')}
                className="bg-black hover:bg-gray-800 text-white"
                size="sm"
              >
                Registration
              </Button>
              <Button
                onClick={() => window.open('/music', '_blank')}
                className="bg-black hover:bg-gray-800 text-white"
                size="sm"
              >
                Music
              </Button>
              <Button
                onClick={() => window.open('/open', '_blank')}
                className="bg-black hover:bg-gray-800 text-white"
                size="sm"
              >
                Open
              </Button>
              <Button
                onClick={() => window.open('/printing', '_blank')}
                className="bg-black hover:bg-gray-800 text-white"
                size="sm"
              >
                Printing
              </Button>
              <Button
                onClick={() => window.open('/ordersettings', '_blank')}
                className="bg-black hover:bg-gray-800 text-white"
                size="sm"
                data-testid="ordering-settings-btn"
              >
                Ordering
              </Button>
              <Button
                onClick={handleRefreshAllJudges}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                size="sm"
                data-testid="refresh-judges-btn"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh Judges
              </Button>
              
              <div className="hidden sm:block w-px bg-gray-300 mx-1"></div>
              
              <Button
                onClick={toggleFullScreen}
                variant="outline"
                size="sm"
                className="whitespace-nowrap"
              >
                <Maximize className="w-4 h-4 mr-2" />
                {isFullScreen ? 'Exit' : 'Full Screen'}
              </Button>
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-[#000000] text-[#000000] hover:bg-[#f5f5f5] whitespace-nowrap"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Mobile: Scrollable tabs */}
          <div className="block sm:hidden mb-6 overflow-x-auto">
            <TabsList className="inline-flex w-auto bg-gray-100 p-1 gap-1">
              <TabsTrigger
                value="clubs"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                <Users className="w-3 h-3 mr-1" />
                Clubs
              </TabsTrigger>
              <TabsTrigger
                value="gymnasts"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                Gymnasts
              </TabsTrigger>
              <TabsTrigger
                value="coaches"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                Coaches
              </TabsTrigger>
              <TabsTrigger
                value="judges"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                Judges
              </TabsTrigger>
              <TabsTrigger
                value="entries"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                Entries
              </TabsTrigger>
              <TabsTrigger
                value="competitions"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                Competitions
              </TabsTrigger>
              <TabsTrigger
                value="programmes"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                Programmes
              </TabsTrigger>
              <TabsTrigger
                value="rotations"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                Rotations
              </TabsTrigger>
              <TabsTrigger
                value="settings"
                className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-xs whitespace-nowrap px-3 py-2"
              >
                Settings
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Desktop: Grid tabs */}
          <TabsList className="hidden sm:grid w-full grid-cols-5 lg:grid-cols-9 mb-8 bg-gray-100 p-1 h-auto gap-1">
            <TabsTrigger
              value="clubs"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              <Users className="w-4 h-4 mr-1" />
              Clubs
            </TabsTrigger>
            <TabsTrigger
              value="gymnasts"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              Gymnasts
            </TabsTrigger>
            <TabsTrigger
              value="coaches"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              Coaches
            </TabsTrigger>
            <TabsTrigger
              value="judges"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              Judges
            </TabsTrigger>
            <TabsTrigger
              value="entries"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              Entries
            </TabsTrigger>
            <TabsTrigger
              value="competitions"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              Comps
            </TabsTrigger>
            <TabsTrigger
              value="programmes"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              Programmes
            </TabsTrigger>
            <TabsTrigger
              value="rotations"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              Rotations
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] text-sm py-2"
            >
              <Settings className="w-4 h-4" />
            </TabsTrigger>
          </TabsList>

          {/* Clubs Tab */}
          <TabsContent value="clubs">
            <div className="mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>All Registered Clubs</h2>
              <p className="text-gray-600 text-sm mt-1">Total: {clubs.length} clubs</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clubs.map(club => (
                <Card key={club.id} className={`hover:shadow-lg transition-shadow ${club.isOwner ? 'border-2 border-[#000000]' : ''}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1">
                        <CardTitle className="text-lg">{club.name}</CardTitle>
                        {club.isOwner && (
                          <span className="text-xs bg-gradient-to-r from-[#000000] to-gray-700 text-white px-2 py-1 rounded font-semibold">
                            OWNER
                          </span>
                        )}
                      </div>
                      {!club.isOwner && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClub(club)}
                          className="text-[#000000] hover:bg-[#f5f5f5]"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <CardDescription>{club.email}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{club.phone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gymnasts:</span>
                        <span className="font-medium">{club.gymnastsCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Coaches:</span>
                        <span className="font-medium">{club.coachesCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Judges:</span>
                        <span className="font-medium">{club.judgesCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Registered:</span>
                        <span className="font-medium text-xs">{club.createdAt ? format(new Date(club.createdAt), 'dd/MM/yyyy') : 'N/A'}</span>
                      </div>
                      <div className="pt-3 mt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">Super Admin</span>
                            {club.isSuperAdmin && (
                              <span className="text-xs bg-[#000000] text-white px-2 py-0.5 rounded">Active</span>
                            )}
                          </div>
                          <Switch
                            checked={club.isSuperAdmin}
                            disabled={club.isOwner}
                            onCheckedChange={() => handleToggleSuperAdmin(club.id, club.name, club.isSuperAdmin)}
                            className="data-[state=checked]:bg-[#000000] disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                        {club.isOwner && (
                          <p className="text-xs text-gray-500 mt-2 italic">
                            🔒 Owner account is protected and cannot be modified
                          </p>
                        )}
                      </div>
                      {!club.isOwner && (
                        <div className="pt-3 mt-3 border-t border-gray-200 space-y-2">
                          <Button
                            variant="outline"
                            className="w-full text-[#000000] border-[#000000] hover:bg-[#f5f5f5]"
                            onClick={() => handleImpersonate(club.id)}
                          >
                            <Users className="w-4 h-4 mr-2" />
                            Login as Club
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full text-gray-900 border-gray-300 hover:bg-gray-100 hover:text-black"
                            onClick={() => handleDeleteClub(club.id, club.name, club.gymnastsCount + club.coachesCount + club.judgesCount)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Club
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Gymnasts Tab */}
          <TabsContent value="gymnasts">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>All Gymnasts</h2>
                <p className="text-gray-600 text-sm mt-1">Total: {filteredGymnasts.length} gymnasts</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <Input
                  placeholder="Search by name, BG number, or club..."
                  value={gymnastSearch}
                  onChange={(e) => setGymnastSearch(e.target.value)}
                  className="w-full sm:w-64"
                />
                <Button
                  variant="destructive"
                  onClick={handleRemoveAllFloorMusic}
                  className="bg-gray-900 hover:bg-black"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove All Floor Music
                </Button>
                <Select value={selectedClub} onValueChange={setSelectedClub}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Filter by club" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clubs</SelectItem>
                    {clubs.map(club => (
                      <SelectItem key={club.id} value={club.id}>{club.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bulk action bar */}
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-gray-50 px-3 py-2">
              <div className="text-sm text-gray-700">
                <span className="font-semibold" data-testid="gymnasts-selected-count">
                  {selectedGymnastIds.size}
                </span>{' '}
                selected
                <span className="text-gray-400 mx-2">·</span>
                <button
                  type="button"
                  onClick={selectAllFilteredWithMusic}
                  className="text-black hover:underline text-sm"
                  data-testid="select-all-with-music-btn"
                >
                  Select all in view with music
                </button>
                {selectedGymnastIds.size > 0 && (
                  <>
                    <span className="text-gray-400 mx-2">·</span>
                    <button
                      type="button"
                      onClick={clearGymnastSelection}
                      className="text-gray-500 hover:underline text-sm"
                      data-testid="clear-gymnasts-selection-btn"
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>
              <Button
                onClick={handleDownloadSelectedMusic}
                disabled={selectedGymnastIds.size === 0 || downloadingMusic}
                className="bg-black hover:bg-gray-800 text-white"
                data-testid="download-selected-music-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloadingMusic ? 'Packaging…' : `Download Floor Music (${selectedGymnastIds.size})`}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="w-8 px-2">
                      <Checkbox
                        checked={
                          filteredGymnasts.length > 0 &&
                          filteredGymnasts.every(g => selectedGymnastIds.has(g.id))
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedGymnastIds(new Set(filteredGymnasts.map(g => g.id)));
                          } else {
                            clearGymnastSelection();
                          }
                        }}
                        aria-label="Select all gymnasts in view"
                        data-testid="select-all-gymnasts-checkbox"
                      />
                    </th>
                    <th>Name</th>
                    <th>Club</th>
                    <th>DOB</th>
                    <th>BG Number</th>
                    <th>Floor Music</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGymnasts.map(gymnast => {
                    const checked = selectedGymnastIds.has(gymnast.id);
                    return (
                      <tr
                        key={gymnast.id}
                        className={checked ? 'bg-amber-50' : undefined}
                      >
                        <td className="w-8 px-2">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleGymnastSelection(gymnast.id)}
                            aria-label={`Select ${gymnast.legalFirstName || gymnast.firstName}`}
                            data-testid={`select-gymnast-${gymnast.id}`}
                          />
                        </td>
                        <td>{gymnast.legalFirstName || gymnast.firstName} {gymnast.legalLastName || gymnast.lastName}</td>
                        <td>{gymnast.clubName}</td>
                        <td>{gymnast.dob ? format(new Date(gymnast.dob), 'dd/MM/yyyy') : 'N/A'}</td>
                        <td>{gymnast.bgNumber}</td>
                        <td>
                          {gymnast.floorMusicUrl ? (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRemoveFloorMusic(gymnast.id, gymnast.legalFirstName || gymnast.firstName, gymnast.legalLastName || gymnast.lastName)}
                              className="bg-gray-900 hover:bg-black"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              REMOVE
                            </Button>
                          ) : (
                            <span className="text-gray-400 text-sm">No music</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Coaches Tab */}
          <TabsContent value="coaches">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>All Coaches</h2>
                <p className="text-gray-600 text-sm mt-1">Total: {filteredCoaches.length} coaches</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <Input
                  placeholder="Search by name, BG number, or club..."
                  value={coachSearch}
                  onChange={(e) => setCoachSearch(e.target.value)}
                  className="w-full sm:w-64"
                />
                <Select value={selectedClub} onValueChange={setSelectedClub}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filter by club" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clubs</SelectItem>
                    {clubs.map(club => (
                      <SelectItem key={club.id} value={club.id}>{club.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Club</th>
                    <th>Qualification</th>
                    <th>DOB</th>
                    <th>BG Number</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCoaches.map(coach => (
                    <tr key={coach.id}>
                      <td>{coach.firstName} {coach.lastName}</td>
                      <td>{coach.clubName}</td>
                      <td>{coach.qualification}</td>
                      <td>{coach.dob ? format(new Date(coach.dob), 'dd/MM/yyyy') : 'N/A'}</td>
                      <td>{coach.bgNumber}</td>
                      <td className={!coach.phone ? 'text-red-500 font-medium' : ''}>{coach.phone || 'Missing!'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Judges Tab */}
          <TabsContent value="judges">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>All Judges</h2>
                <p className="text-gray-600 text-sm mt-1">Total: {filteredJudges.length} judges</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <Input
                  placeholder="Search by name, BG number, or club..."
                  value={judgeSearch}
                  onChange={(e) => setJudgeSearch(e.target.value)}
                  className="w-full sm:w-64"
                />
                <Select value={selectedClub} onValueChange={setSelectedClub}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filter by club" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clubs</SelectItem>
                    {clubs.map(club => (
                      <SelectItem key={club.id} value={club.id}>{club.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Club</th>
                    <th>Level</th>
                    <th>DOB</th>
                    <th>BG Number</th>
                    <th>Phone</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJudges.map(judge => (
                    <tr key={judge.id}>
                      <td>{judge.firstName} {judge.lastName}</td>
                      <td>{judge.clubName}</td>
                      <td>{judge.level}</td>
                      <td>{judge.dob ? format(new Date(judge.dob), 'dd/MM/yyyy') : 'N/A'}</td>
                      <td>{judge.bgNumber}</td>
                      <td className={!judge.phone ? 'text-red-500 font-medium' : ''}>{judge.phone || 'Missing!'}</td>
                      <td className={!judge.email ? 'text-red-500 font-medium' : ''} title={judge.email}>{judge.email ? (judge.email.length > 25 ? judge.email.substring(0, 22) + '...' : judge.email) : 'Missing!'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Competitions Tab */}
          <TabsContent value="competitions">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Competitions</h2>
              <Button
                onClick={() => handleOpenCompDialog()}
                className="bg-[#000000] hover:bg-[#333333]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Competition
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {competitions.map(comp => (
                <Card key={comp.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{comp.title}</CardTitle>
                        <CardDescription className="mt-1">
                          {comp.displayDate || (comp.startsOn && comp.endsOn ? `${format(new Date(comp.startsOn), 'dd/MM/yyyy')} - ${format(new Date(comp.endsOn), 'dd/MM/yyyy')}` : 'Date TBD')}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            comp.isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {comp.isOpen ? 'Open' : 'Closed'}
                        </span>
                        {comp.isHidden && (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                            Hidden
                          </span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4 space-y-1">
                      <p className="text-sm text-gray-600">Entry Fee: <span className="font-semibold text-[#000000]">£{comp.entryFee}</span></p>
                      <p className="text-xs text-gray-500">
                        Eligible Clubs: {(comp.eligibleClubIds?.length || 0) === 0 
                          ? <span className="text-green-600 font-medium">All clubs (open)</span> 
                          : <span className="text-blue-600 font-medium">{comp.eligibleClubIds.length} clubs only</span>}
                        {(comp.eligibleClubIds?.length || 0) > 0 && (
                          <button
                            onClick={async () => {
                              if (!window.confirm('Remove all eligibility restrictions? All clubs will be able to enter.')) return;
                              await axios.put(`${API}/competitions/${comp.id}`, { ...comp, eligibleClubIds: [] });
                              toast.success('Competition opened to all clubs');
                              loadAllData();
                            }}
                            className="ml-2 text-red-500 underline text-xs"
                          >
                            Open to all
                          </button>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleOpenCompDialog(comp)}
                          size="sm"
                          variant="outline"
                          className="flex-1"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleDeleteCompetition(comp.id)}
                          size="sm"
                          variant="outline"
                          className="text-gray-900 hover:bg-gray-100"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Button
                        onClick={() => handleOpenEligibilityDialog(comp)}
                        size="sm"
                        variant="outline"
                        className="w-full border-black text-black hover:bg-gray-100"
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        Eligibility ({comp.eligibleClubIds?.length || 0} clubs)
                      </Button>
                      <Button
                        onClick={() => openCategorySplitsDialog(comp)}
                        size="sm"
                        variant="outline"
                        className="w-full border-black text-black hover:bg-gray-100 mt-2"
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Category Age Splits
                      </Button>
                      <Button
                        onClick={() => openQualificationDialog(comp)}
                        size="sm"
                        variant="outline"
                        className="w-full border-black text-black hover:bg-gray-100 mt-2"
                      >
                        <Award className="w-4 h-4 mr-1" />
                        Qualification
                      </Button>
                      <Button
                        onClick={() => openTrainingVideoDialog(comp)}
                        size="sm"
                        variant="outline"
                        className="w-full border-black text-black hover:bg-gray-100 mt-2"
                      >
                        <Video className="w-4 h-4 mr-1" />
                        Judge Training Video
                      </Button>
                      <Button
                        onClick={() => openBypassCodeDialog(comp)}
                        size="sm"
                        variant="outline"
                        className="w-full border-black text-black hover:bg-gray-100 mt-2"
                      >
                        <Key className="w-4 h-4 mr-1" />
                        Judge Bypass Code
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full border-blue-600 text-blue-600 hover:bg-blue-50 mt-2"
                        data-testid={`export-scoresheet-${comp.id}`}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/superadmin/competitions/${comp.id}/export-scoresheet`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Export failed'); }
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `scoresheet_${comp.id}.xlsx`;
                            a.click();
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            toast.error(err.message || 'Failed to export scoresheet');
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M3 9h18M3 15h18"/></svg>
                        Export Scoresheet
                      </Button>
                      {comp.teamEventEnabled && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full border-purple-600 text-purple-700 hover:bg-purple-50 mt-2"
                          data-testid={`team-builder-${comp.id}`}
                          onClick={() => openTeamBuilder(comp)}
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Team Builder
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>



          {/* Settings Tab */}

          {/* Programmes Tab */}
          <TabsContent value="programmes">
            <ProgrammesTab competitions={competitions} />
          </TabsContent>

          <TabsContent value="rotations">
            <div className="mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Rotation Schedule</h2>
              <p className="text-gray-600 text-sm mt-1">Define which apparatus each group competes on in each rotation</p>
            </div>
            
            {/* Competition Selector */}
            <Card className="mb-6">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <Label>Competition:</Label>
                  <Select 
                    value={selectedCompetition === 'all' ? '' : selectedCompetition} 
                    onValueChange={(v) => setSelectedCompetition(v)}
                  >
                    <SelectTrigger className="w-72">
                      <SelectValue placeholder="Select a competition" />
                    </SelectTrigger>
                    <SelectContent>
                      {competitions.map(comp => (
                        <SelectItem key={comp.id} value={comp.id}>{comp.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {selectedCompetition && selectedCompetition !== 'all' ? (
              <RotationScheduleEditor 
                competitionId={selectedCompetition} 
                token={localStorage.getItem('token')} 
                discipline={competitions.find(c => c.id === selectedCompetition)?.sheetsDiscipline || 'WAG'}
              />
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  Please select a competition to manage rotation schedules
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="settings">
            <div className="mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Settings</h2>
              <p className="text-gray-600 text-sm mt-1">Manage system settings and configurations</p>
            </div>

            {/* Quick tools */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Results Tools</CardTitle>
                <CardDescription>
                  Edit rankings and manage public-facing result data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4 p-4 border rounded-lg hover:bg-gray-50">
                  <div>
                    <p className="font-semibold">Rank Overrides</p>
                    <p className="text-sm text-gray-600">
                      Manually set rankings for any category / apparatus (e.g. joint 1st places).
                      Applies instantly to live rankings, presentations, and PDF exports.
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/superadmin/rank-overrides')}
                    className="bg-black hover:bg-gray-800"
                    data-testid="open-rank-overrides-btn"
                  >
                    Open
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* System Status Control */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>System Status</CardTitle>
                <CardDescription>
                  Control the public-facing status indicator and maintenance mode
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status Text */}
                <div>
                  <Label htmlFor="systemStatusText">Status Text</Label>
                  <Input
                    id="systemStatusText"
                    value={systemStatusText}
                    onChange={(e) => setSystemStatusText(e.target.value)}
                    className="mt-1"
                    placeholder="All Systems Operational"
                    data-testid="system-status-text-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Displayed in the footer of the public landing page</p>
                </div>

                {/* Status Color */}
                <div>
                  <Label>Status Light Colour</Label>
                  <div className="flex gap-3 mt-2">
                    {[
                      { color: 'green', label: 'Green', bg: 'bg-green-500', ring: 'ring-green-300' },
                      { color: 'yellow', label: 'Yellow', bg: 'bg-yellow-500', ring: 'ring-yellow-300' },
                      { color: 'red', label: 'Red', bg: 'bg-red-500', ring: 'ring-red-300' },
                      { color: 'blue', label: 'Blue', bg: 'bg-blue-500', ring: 'ring-blue-300' }
                    ].map(opt => (
                      <button
                        key={opt.color}
                        onClick={() => setSystemStatusColor(opt.color)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                          systemStatusColor === opt.color
                            ? `border-black ring-2 ${opt.ring}`
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        data-testid={`status-color-${opt.color}`}
                      >
                        <div className={`w-3 h-3 rounded-full ${opt.bg}`} />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Maintenance Mode Toggle */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">Website Maintenance Mode</Label>
                      <p className="text-sm text-gray-500 mt-1">
                        When enabled, Live Scores and Rankings will be hidden on the public landing page.
                        Admin systems (registration, music, judges, etc.) remain fully operational.
                        Coaches can still access scores at <code className="bg-gray-200 px-1 py-0.5 rounded text-xs">/coaches</code>.
                      </p>
                    </div>
                    <Switch
                      checked={maintenanceMode}
                      onCheckedChange={(checked) => setMaintenanceMode(checked)}
                      data-testid="maintenance-mode-toggle"
                    />
                  </div>
                  {maintenanceMode && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 font-medium">
                        Maintenance mode is ON. Public visitors cannot see live scores or rankings.
                      </p>
                    </div>
                  )}
                </div>

                {/* Preview */}
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Footer Preview:</p>
                  <div className="flex items-center justify-center gap-2 py-2">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${
                      { green: 'bg-green-500', yellow: 'bg-yellow-500', red: 'bg-red-500', blue: 'bg-blue-500' }[systemStatusColor] || 'bg-green-500'
                    }`} />
                    <span className="text-sm text-gray-600">{systemStatusText || 'All Systems Operational'}</span>
                    <span className="text-gray-400 mx-1">|</span>
                    <span className="text-sm text-gray-600 underline">Privacy Notice</span>
                    <span className="text-gray-400 mx-1">|</span>
                    <span className="text-sm text-gray-600">&copy; 2025 - 2026</span>
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  onClick={() => saveSystemStatus()}
                  disabled={systemStatusLoading}
                  className="bg-black hover:bg-gray-800"
                  data-testid="save-system-status-btn"
                >
                  {systemStatusLoading ? 'Saving...' : 'Save System Status'}
                </Button>
              </CardContent>
            </Card>

            {/* Raffle Settings */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="w-5 h-5" />
                  Raffle
                </CardTitle>
                <CardDescription>
                  Enable and configure competition raffle
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Enable Raffle</Label>
                    <p className="text-sm text-gray-500">Allow ticket purchases at /raffle</p>
                  </div>
                  <Switch
                    checked={raffleEnabled}
                    onCheckedChange={setRaffleEnabled}
                  />
                </div>
                
                {raffleEnabled && (
                  <div className="space-y-4 pt-4 border-t">
                    <div>
                      <Label>Competition</Label>
                      <Select value={raffleCompetitionId} onValueChange={setRaffleCompetitionId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select competition for raffle" />
                        </SelectTrigger>
                        <SelectContent>
                          {competitions.map(comp => (
                            <SelectItem key={comp.id} value={comp.id}>{comp.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        onClick={saveRaffleSettings}
                        disabled={raffleLoading}
                        className="flex-1"
                      >
                        {raffleLoading ? 'Saving...' : 'Save Raffle Settings'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => window.open('/raffle/admin', '_blank')}
                      >
                        Open Admin
                      </Button>
                    </div>
                  </div>
                )}
                
                {!raffleEnabled && (
                  <Button 
                    onClick={saveRaffleSettings}
                    disabled={raffleLoading}
                    variant="outline"
                    className="w-full"
                  >
                    {raffleLoading ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Google Sheets Status */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Google Sheets Integration</CardTitle>
                <CardDescription>
                  Real-time score sync to Google Sheets
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="googleSheetId">Google Sheet ID</Label>
                  <div className="flex gap-2">
                    <Input
                      id="googleSheetId"
                      placeholder="Enter Google Sheet ID (from the URL)"
                      value={googleSheetId || ''}
                      onChange={(e) => setGoogleSheetId(e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                    <Button 
                      onClick={async () => {
                        if (!googleSheetId?.trim()) {
                          toast.error('Please enter a Sheet ID');
                          return;
                        }
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.put(`${API}/superadmin/google-sheets-config`, 
                            { sheet_id: googleSheetId.trim() },
                            { headers: { Authorization: `Bearer ${token}` }}
                          );
                          if (res.data.status === 'ok') {
                            toast.success(`✅ Connected to: ${res.data.sheet_title}`);
                            setGoogleSheetTitle(res.data.sheet_title);
                          }
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to update Google Sheet');
                        }
                      }}
                      className="bg-black hover:bg-gray-800"
                    >
                      Save & Test
                    </Button>
                  </div>
                  {googleSheetTitle && (
                    <p className="text-sm text-green-600">Currently connected to: {googleSheetTitle}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    The Sheet ID is the long string in the Google Sheet URL between /d/ and /edit
                    <br />
                    Example: docs.google.com/spreadsheets/d/<strong>1ABC123xyz</strong>/edit
                  </p>
                </div>
                
                <div className="flex items-center gap-4 pt-2">
                  <Button 
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${API}/superadmin/google-sheets-status`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (res.data.status === 'ok') {
                          toast.success(`✅ Connected to: ${res.data.spreadsheet_title}`);
                          setGoogleSheetTitle(res.data.spreadsheet_title);
                          if (res.data.current_sheet_id && !googleSheetId) {
                            setGoogleSheetId(res.data.current_sheet_id);
                          }
                        } else {
                          toast.error(`❌ ${res.data.message}`);
                        }
                      } catch (error) {
                        toast.error('Failed to check Google Sheets status');
                      }
                    }}
                    variant="outline"
                    className="border-black"
                  >
                    Check Connection
                  </Button>
                  <Button 
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${API}/superadmin/google-sheets-test`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (res.data.status === 'ok') {
                          toast.success('✅ Write test PASSED!');
                        } else {
                          toast.error(`❌ ${res.data.message}`);
                        }
                      } catch (error) {
                        toast.error('Failed to test Google Sheets');
                      }
                    }}
                    className="bg-black hover:bg-gray-800"
                  >
                    Test Write
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Scores are synced to tabs: VT-Vault, UB-Uneven Bars, BB-Balance Beam, FX-Floor, R&C-Range and Conditioning
                </p>
              </CardContent>
            </Card>

            {/* Floor Music Wordsearch */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  🔤 Floor Music Wordsearch
                </CardTitle>
                <CardDescription>
                  Entertainment for floor music operators - a wordsearch popup appears during playback
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Enable Wordsearch</p>
                    <p className="text-xs text-gray-500">Show wordsearch puzzle on the /music screen during playback</p>
                  </div>
                  <Switch
                    checked={wordsearchEnabled}
                    onCheckedChange={setWordsearchEnabled}
                    data-testid="wordsearch-enabled-toggle"
                  />
                </div>
                <div className={wordsearchEnabled ? '' : 'opacity-40 pointer-events-none'}>
                  <Label htmlFor="wordsearchWords">Words (comma-separated, max 150)</Label>
                  <textarea
                    id="wordsearchWords"
                    value={wordsearchWords}
                    onChange={(e) => setWordsearchWords(e.target.value)}
                    rows={6}
                    className="w-full mt-1 p-3 border border-gray-300 rounded-md text-sm"
                    placeholder="VAULT, BEAM, FLOOR, BARS, GYMNASTICS, TUMBLE, FLIP, SOMERSAULT, HANDSTAND, CARTWHEEL..."
                  />
                  <div className="flex justify-between mt-2">
                    <p className="text-xs text-gray-500">
                      Words appear 3 at a time during floor music playback. Add gymnastics terms, club names, etc.
                    </p>
                    <p className="text-xs text-gray-500">
                      {wordsearchWords.split(',').filter(w => w.trim()).length}/150 words
                    </p>
                  </div>
                </div>
                <Button
                  onClick={saveWordsearchWords}
                  disabled={wordsearchLoading}
                  className="bg-black hover:bg-gray-800"
                >
                  {wordsearchLoading ? 'Saving...' : 'Save Wordsearch Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* SumUp Solo Payment Settings */}
            <div className="mb-6">
              <SumUpSettings />
            </div>

            {/* Welcome Message Template */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>SMS Welcome Message Template</CardTitle>
                <CardDescription>
                  Customize the welcome message sent to parents when they opt in for live scores
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="welcomeTemplate">Message Template</Label>
                  <textarea
                    id="welcomeTemplate"
                    value={welcomeTemplate}
                    onChange={(e) => setWelcomeTemplate(e.target.value)}
                    rows={6}
                    className="w-full mt-1 p-3 border border-gray-300 rounded-md font-mono text-sm"
                    placeholder="Enter welcome message template..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    <strong>Placeholders:</strong> {'{COMP_TITLE}'} = Competition name, {'{RECIPIENT_NAME}'} = Gymnast name
                  </p>
                  <p className="text-xs text-gray-500">
                    <strong>Tip:</strong> Keep message short to stay within SMS character limits (~160 chars)
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    onClick={saveWelcomeTemplate} 
                    disabled={welcomeTemplateLoading}
                    className="bg-black hover:bg-gray-800"
                  >
                    {welcomeTemplateLoading ? 'Saving...' : 'Save Template'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setWelcomeTemplate(`Welcome to {COMP_TITLE}!

You'll receive live scores for: {RECIPIENT_NAME}

Save our contact: https://scorz.co.uk/vcard

Problems? Call Freddie: +44 7723 481 388`)}
                  >
                    Reset to Default
                  </Button>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Preview:</p>
                  <p className="text-sm whitespace-pre-wrap">
                    {welcomeTemplate
                      .replace('{COMP_TITLE}', 'Example Competition')
                      .replace('{RECIPIENT_NAME}', 'Jane Smith')}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Characters: {welcomeTemplate.length} / ~160 recommended
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Club SMS Template */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Club SMS Template</CardTitle>
                <CardDescription>
                  Customize the welcome message sent to club phone numbers when they opt in for updates
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="clubSmsTemplate">Message Template</Label>
                  <textarea
                    id="clubSmsTemplate"
                    value={clubSmsTemplate}
                    onChange={(e) => setClubSmsTemplate(e.target.value)}
                    rows={6}
                    className="w-full mt-1 p-3 border border-gray-300 rounded-md font-mono text-sm"
                    placeholder="Enter club SMS template..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    <strong>Placeholders:</strong> {'{COMP_TITLE}'} = Competition name, {'{RECIPIENT_NAME}'} = Club name
                  </p>
                  <p className="text-xs text-gray-500">
                    <strong>Tip:</strong> Keep message short to stay within SMS character limits (~160 chars)
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    onClick={saveClubSmsTemplate} 
                    disabled={clubSmsTemplateLoading}
                    className="bg-black hover:bg-gray-800"
                  >
                    {clubSmsTemplateLoading ? 'Saving...' : 'Save Template'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setClubSmsTemplate(`Welcome to {COMP_TITLE}!

You'll receive live updates for {RECIPIENT_NAME}.

Save our contact: https://scorz.co.uk/vcard

Problems? Call Freddie: +44 7723 481 388`)}
                  >
                    Reset to Default
                  </Button>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Preview:</p>
                  <p className="text-sm whitespace-pre-wrap">
                    {clubSmsTemplate
                      .replace('{COMP_TITLE}', 'Example Competition')
                      .replace('{RECIPIENT_NAME}', 'Kennylands Gymnastics')}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Characters: {clubSmsTemplate.length} / ~160 recommended
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Entry Email Templates */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Entry Email Templates</CardTitle>
                <CardDescription>
                  Customize emails sent to clubs for entry events. A copy of all emails is sent to admin@scorz.co.uk
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Select Template to Edit</Label>
                  <Select value={selectedEmailTemplate} onValueChange={setSelectedEmailTemplate}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="save_for_later">Save for Later</SelectItem>
                      <SelectItem value="entry_submitted">Entry Submitted</SelectItem>
                      <SelectItem value="withdrawal_refund">Withdrawal (with Refund)</SelectItem>
                      <SelectItem value="withdrawal_no_refund">Withdrawal (no Refund)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {emailTemplates[selectedEmailTemplate] && (
                  <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                    <div>
                      <Label htmlFor="emailSubject">Subject Line</Label>
                      <Input
                        id="emailSubject"
                        value={emailTemplates[selectedEmailTemplate]?.subject || ''}
                        onChange={(e) => updateEmailTemplate(selectedEmailTemplate, 'subject', e.target.value)}
                        className="mt-1"
                        placeholder="Email subject..."
                      />
                    </div>

                    <div>
                      <Label htmlFor="emailBody">Email Body (HTML)</Label>
                      <textarea
                        id="emailBody"
                        value={emailTemplates[selectedEmailTemplate]?.body || ''}
                        onChange={(e) => updateEmailTemplate(selectedEmailTemplate, 'body', e.target.value)}
                        rows={10}
                        className="w-full mt-1 p-3 border border-gray-300 rounded-md font-mono text-sm"
                        placeholder="Email body in HTML..."
                      />
                      <div className="mt-2 text-xs text-gray-500 space-y-1">
                        <p><strong>Available Placeholders:</strong></p>
                        <p><code className="bg-gray-200 px-1 rounded">{'{CLUB_NAME}'}</code> - Club name</p>
                        <p><code className="bg-gray-200 px-1 rounded">{'{COMP_NAME}'}</code> - Competition name</p>
                        <p><code className="bg-gray-200 px-1 rounded">{'{END_DATE_TIME}'}</code> - Entry deadline (Save for Later only)</p>
                        <p><code className="bg-gray-200 px-1 rounded">{'{CONFIRMATION_DETAILS}'}</code> - Gymnasts/Judges/Coaches list (Entry Submitted only)</p>
                        <p><code className="bg-gray-200 px-1 rounded">{'{GYMNAST_NAME}'}</code> - Gymnast name (Withdrawal emails)</p>
                        <p><code className="bg-gray-200 px-1 rounded">{'{REFUND_CASE_ID}'}</code> - Refund case ID (Withdrawal with Refund)</p>
                        <p className="mt-2"><strong>HTML Tips:</strong> Use <code className="bg-gray-200 px-1 rounded">&lt;p&gt;</code> for paragraphs, <code className="bg-gray-200 px-1 rounded">&lt;strong&gt;</code> for bold, <code className="bg-gray-200 px-1 rounded">&lt;br&gt;</code> for line breaks</p>
                        <p>For colors: <code className="bg-gray-200 px-1 rounded">&lt;span style="color: red;"&gt;text&lt;/span&gt;</code></p>
                        <p>For size: <code className="bg-gray-200 px-1 rounded">&lt;span style="font-size: 18px;"&gt;text&lt;/span&gt;</code></p>
                        <p>For alignment: <code className="bg-gray-200 px-1 rounded">&lt;p style="text-align: center;"&gt;text&lt;/p&gt;</code></p>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-lg border">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Preview:</p>
                      <div className="border p-4 rounded bg-gray-50">
                        <p className="text-sm font-semibold mb-2">
                          Subject: {(emailTemplates[selectedEmailTemplate]?.subject || '')
                            .replace('{CLUB_NAME}', 'Example Club')
                            .replace('{COMP_NAME}', 'Spring Championship 2025')
                            .replace('{GYMNAST_NAME}', 'Jane Smith')
                          }
                        </p>
                        <div 
                          className="text-sm"
                          dangerouslySetInnerHTML={{ 
                            __html: (emailTemplates[selectedEmailTemplate]?.body || '')
                              .replace('{CLUB_NAME}', 'Example Club')
                              .replace('{COMP_NAME}', 'Spring Championship 2025')
                              .replace('{END_DATE_TIME}', '15 March 2025 at 23:59')
                              .replace('{CONFIRMATION_DETAILS}', '<p><strong>Gymnasts:</strong><br>Jane Smith - Level 3<br>Emma Johnson - Level 4</p><p><strong>Judges:</strong><br>Sarah Brown - Regional</p><p><strong>Coaches:</strong><br>Mike Wilson - Level 2</p>')
                              .replace('{GYMNAST_NAME}', 'Jane Smith')
                              .replace('{REFUND_CASE_ID}', 'RC-ABC123')
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button 
                    onClick={saveEmailTemplates} 
                    disabled={emailTemplatesLoading}
                    className="bg-black hover:bg-gray-800"
                  >
                    {emailTemplatesLoading ? 'Saving...' : 'Save All Templates'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => resetEmailTemplateToDefault(selectedEmailTemplate)}
                  >
                    Reset Current to Default
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Scoring System Management */}
            <Card>
              <CardHeader>
                <CardTitle>Scoring System Management</CardTitle>
                <CardDescription>
                  Generate judge codes and manage scores for competitions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Universal Judge Code Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Universal Judge Code</h3>
                  <p className="text-sm text-gray-600">Set one code that works for all apparatus (Vault, Bars, Beam, Floor)</p>
                  
                  <div>
                    <Label>Select Competition</Label>
                    <Select value={scoringCompetition} onValueChange={(value) => {
                      setScoringCompetition(value);
                      setJudgeCodes([]);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose competition..." />
                      </SelectTrigger>
                      <SelectContent>
                        {competitions.map((comp) => (
                          <SelectItem key={comp.id} value={comp.id}>
                            {comp.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {scoringCompetition && (
                    <>
                      <div className="border rounded-lg p-3 bg-gray-50 space-y-3" data-testid="judge-code-discipline-block">
                        <div>
                          <Label className="text-sm font-semibold">Disciplines to generate codes for</Label>
                          <div className="flex gap-2 mt-1">
                            {['WAG', 'MAG', 'BOTH'].map((opt) => {
                              const arr = opt === 'BOTH' ? ['WAG', 'MAG'] : [opt];
                              const active = JSON.stringify([...judgeDisciplines].sort()) === JSON.stringify([...arr].sort());
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => setJudgeDisciplines(arr)}
                                  className={`flex-1 px-3 py-2 rounded border text-sm font-semibold transition ${active ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                                  data-testid={`judge-discipline-${opt}`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {judgeDisciplines.includes('MAG') && (
                          <div>
                            <Label className="text-sm font-semibold">MAG scoring style</Label>
                            <div className="flex gap-2 mt-1">
                              {[
                                { id: 'individual', label: 'Individual apparatus pages' },
                                { id: 'universal',  label: 'One universal /mag terminal' },
                              ].map((opt) => {
                                const active = judgeMagMode === opt.id;
                                return (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => setJudgeMagMode(opt.id)}
                                    className={`flex-1 px-3 py-2 rounded border text-sm font-semibold transition ${active ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                                    data-testid={`judge-mag-mode-${opt.id}`}
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Individual: one code per MAG apparatus. Universal: a single code unlocks /mag and apparatus is auto-resolved from the rotation schedule.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button onClick={loadCurrentJudgeCodes} variant="outline">
                          Load Current Codes
                        </Button>
                        <Button onClick={handleGenerateJudgeCodes} className="bg-[#000000] hover:bg-[#333333]">
                          Generate New Codes
                        </Button>
                      </div>

                      {judgeCodes.length > 0 && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-4">
                          <div className="flex justify-between items-center">
                            <p className="text-sm font-semibold text-green-800">
                              Judge Codes ({isDualPanel ? 'Dual Panel' : 'Single Panel'})
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-800 border-green-400 hover:bg-green-100 flex items-center gap-1.5"
                              data-testid="export-judge-codes-pdf"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await fetch(`${API}/scoring/judge-codes/${scoringCompetition}/export-pdf`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  if (!res.ok) throw new Error(await res.text());
                                  const blob = await res.blob();
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `judge_codes_${scoringCompetition}.pdf`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                } catch (err) {
                                  toast.error('Failed to export PDF');
                                  console.error(err);
                                }
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                              Export PDF
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-700 border-blue-400 hover:bg-blue-50 flex items-center gap-1.5"
                              data-testid="export-scoresheet-xlsx"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await fetch(`${API}/superadmin/competitions/${scoringCompetition}/export-scoresheet`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  if (!res.ok) throw new Error(await res.text());
                                  const blob = await res.blob();
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `scoresheet_${scoringCompetition}.xlsx`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                } catch (err) {
                                  toast.error('Failed to export scoresheet');
                                  console.error(err);
                                }
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M3 9h18M3 15h18"/></svg>
                              Export Scoresheet
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {(() => {
                              const APP_NAMES = {
                                vault: 'VAULT',
                                bars: 'UNEVEN BARS',
                                beam: 'BALANCE BEAM',
                                floor: 'FLOOR',
                                range: 'R&C',
                                rings: 'RINGS',
                                pommel: 'POMMEL HORSE',
                                pbars: 'PARALLEL BARS',
                                hbar: 'HIGH BAR',
                                mag: 'MAG (UNIVERSAL)',
                                wagvault: 'WAG VAULT',
                                magvault: 'MAG VAULT',
                                wagfloor: 'WAG FLOOR',
                                magfloor: 'MAG FLOOR',
                              };
                              const order = ['wagvault', 'vault', 'bars', 'beam', 'wagfloor', 'floor', 'range', 'magvault', 'magfloor', 'pommel', 'rings', 'pbars', 'hbar', 'mag'];
                              const seen = new Set();
                              const apparatusesInCodes = judgeCodes
                                .map(c => c.apparatus)
                                .filter(a => { if (seen.has(a)) return false; seen.add(a); return true; });
                              const sorted = apparatusesInCodes.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                              return sorted.map(apparatus => {
                                const apparatusName = APP_NAMES[apparatus] || apparatus.toUpperCase();
                                const codes = judgeCodes.filter(c => c.apparatus === apparatus);
                                return (
                                  <div key={apparatus} className="bg-white p-3 rounded border border-green-300">
                                    <p className="text-xs font-semibold text-gray-700 mb-2">{apparatusName}</p>
                                    {codes.map(code => (
                                      <div key={`${code.panel}-${code.code}`} className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-gray-600">
                                          {code.panel === 'single' || code.panel === 'SINGLE PANEL'
                                            ? 'Single Panel:'
                                            : code.panel === 'MAG UNIVERSAL'
                                              ? 'Universal:'
                                              : `Panel ${code.panel}:`}
                                        </span>
                                        <span className="text-lg font-bold tracking-wider text-green-800 font-mono">
                                          {code.code}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                          <p className="text-xs text-gray-600">
                            Codes are unique per competition (no more shared 250707). Single-panel rounds, dual-panel rounds, and the universal MAG code are all reflected above.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* SMS Management System */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>SR SMS - Live Score Notifications</CardTitle>
                <CardDescription>
                  Manage SMS notifications for clubs and parents via Twilio
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Competition Selector */}
                <div>
                  <Label>Select Competition</Label>
                  <Select value={smsCompetition} onValueChange={(value) => {
                    setSmsCompetition(value);
                    setClubsSMS([]);
                    setGymnastsSMS([]);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose competition..." />
                    </SelectTrigger>
                    <SelectContent>
                      {competitions.map((comp) => (
                        <SelectItem key={comp.id} value={comp.id}>
                          {comp.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {smsCompetition && (
                  <div className="flex gap-4">
                    <Button onClick={() => { loadClubsSMS(); setShowClubsSMS(true); setShowParentsSMS(false); }} className="bg-[#000000] hover:bg-[#333333]">
                      Manage Clubs SMS
                    </Button>
                    <Button onClick={() => { loadGymnastsSMS(); setShowParentsSMS(true); setShowClubsSMS(false); }} className="bg-[#000000] hover:bg-[#333333]">
                      Manage Parents SMS
                    </Button>
                  </div>
                )}

                {/* CLUBS SMS Section */}
                {showClubsSMS && (
                  <div className="pt-6 border-t">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-lg">CLUBS SMS</h3>
                      <Button onClick={() => setShowClubsSMS(false)} variant="outline" size="sm">
                        <X className="w-4 h-4 mr-2" />
                        Close
                      </Button>
                    </div>
                    {clubsSMS.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>No clubs found with entries for this competition</p>
                      </div>
                    ) : (
                    <div className="space-y-4">
                      {clubsSMS.map((club) => (
                        <div key={club.id} className="p-4 border rounded-lg">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold">{club.name}</h4>
                              <p className="text-sm text-gray-600">{club.email}</p>
                            </div>
                            {club.welcomeSent && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                Welcome Sent
                              </span>
                            )}
                          </div>

                          {/* Phone Numbers List */}
                          <div className="mb-3">
                            {club.phoneNumbers && club.phoneNumbers.length > 0 ? (
                              <div className="space-y-2">
                                {club.phoneNumbers.map((phone, idx) => (
                                  <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                    <span className="font-mono">{phone}</span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-gray-900"
                                      onClick={() => handleRemoveClubPhone(club.id, phone)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No phone numbers added</p>
                            )}
                          </div>

                          {/* Add Phone Number + Send Welcome */}
                          <div className="flex gap-2 flex-wrap">
                            <Input
                              placeholder="07xxx or +447xxx..."
                              value={newClubPhone[club.id] || ''}
                              onChange={(e) => setNewClubPhone({ ...newClubPhone, [club.id]: e.target.value })}
                              className="flex-1 min-w-[160px]"
                            />
                            <Button onClick={() => handleAddClubPhone(club.id)} className="bg-[#000000] hover:bg-[#333333]">
                              <Plus className="w-4 h-4 mr-2" />
                              Add Phone
                            </Button>
                            {club.phoneNumbers && club.phoneNumbers.length > 0 && (
                              <Button
                                variant="outline"
                                onClick={() => handleSendClubWelcome(club.id)}
                                className="border-green-600 text-green-700 hover:bg-green-50"
                                data-testid={`send-welcome-${club.id}`}
                              >
                                Send Welcome Message
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}

                {/* PARENT SMS Section */}
                {showParentsSMS && (
                  <div className="pt-6 border-t">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-lg">PARENT SMS</h3>
                      <Button onClick={() => setShowParentsSMS(false)} variant="outline" size="sm">
                        <X className="w-4 h-4 mr-2" />
                        Close
                      </Button>
                    </div>
                    {gymnastsSMS.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>No gymnasts found for this competition</p>
                      </div>
                    ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border p-2 text-left">Number</th>
                            <th className="border p-2 text-left">Preferred Name</th>
                            <th className="border p-2 text-left">Club</th>
                            <th className="border p-2 text-left">Phone Number</th>
                            <th className="border p-2 text-center">Status</th>
                            <th className="border p-2 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gymnastsSMS.map((gymnast) => (
                            <tr key={gymnast.id} className="hover:bg-gray-50">
                              <td className="border p-2">{gymnast.gymnastNumber}</td>
                              <td className="border p-2">{gymnast.preferredName}</td>
                              <td className="border p-2">{gymnast.clubName}</td>
                              <td className="border p-2">
                                {editingParentPhone[gymnast.id] !== false && editingParentPhone[gymnast.id] !== undefined ? (
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="07xxx or +447xxx..."
                                      value={editingParentPhone[gymnast.id] || ''}
                                      onChange={(e) => setEditingParentPhone({ ...editingParentPhone, [gymnast.id]: e.target.value })}
                                      className="font-mono"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleUpdateParentPhone(gymnast.id, gymnast.phoneNumber)}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditingParentPhone({ ...editingParentPhone, [gymnast.id]: false })}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : gymnast.phoneNumber ? (
                                  <span className="font-mono">{gymnast.phoneNumber}</span>
                                ) : newParentPhone[gymnast.id] !== undefined ? (
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="07xxx or +447xxx..."
                                      value={newParentPhone[gymnast.id] || ''}
                                      onChange={(e) => setNewParentPhone({ ...newParentPhone, [gymnast.id]: e.target.value })}
                                      className="font-mono"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleAddParentPhone(gymnast.id)}
                                      className="bg-[#000000] hover:bg-[#333333]"
                                    >
                                      Add
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setNewParentPhone({ ...newParentPhone, [gymnast.id]: undefined })}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-gray-500">No added phone number</span>
                                )}
                              </td>
                              <td className="border p-2 text-center">
                                {gymnast.welcomeSent && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                    Welcome Sent
                                  </span>
                                )}
                              </td>
                              <td className="border p-2 text-center">
                                {editingParentPhone[gymnast.id] === false || editingParentPhone[gymnast.id] === undefined ? (
                                  <div className="flex gap-1 justify-center">
                                    {gymnast.phoneNumber ? (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => setEditingParentPhone({ ...editingParentPhone, [gymnast.id]: gymnast.phoneNumber })}
                                          title="Edit phone"
                                        >
                                          <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-red-600 hover:bg-red-50"
                                          onClick={() => handleDeleteParentPhone(gymnast.id)}
                                          title="Delete phone"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </>
                                    ) : newParentPhone[gymnast.id] === undefined ? (
                                      <Button
                                        size="sm"
                                        onClick={() => setNewParentPhone({ ...newParentPhone, [gymnast.id]: '' })}
                                        className="bg-[#000000] hover:bg-[#333333]"
                                      >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Phone
                                      </Button>
                                    ) : null}
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}
                    {gymnastsSMS.length > 0 && (
                      <p className="text-sm text-gray-600 mt-4">
                        Note: You can manually add/edit phone numbers, or they are added automatically when parents opt-in during registration check-in.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Background Music Tracks */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Background Music Tracks</CardTitle>
                <CardDescription>
                  Upload MP3 files for venue background music. These will play on loop on the Background Music page (/music/background).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <input
                    ref={backgroundTrackInputRef}
                    type="file"
                    accept=".mp3"
                    onChange={(e) => e.target.files?.[0] && uploadBackgroundTrack(e.target.files[0])}
                    className="hidden"
                    id="background-track-upload"
                  />
                  <Button 
                    onClick={() => backgroundTrackInputRef.current?.click()}
                    disabled={backgroundTrackUploading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {backgroundTrackUploading ? 'Uploading...' : 'Upload MP3'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Upload MP3 files to create your background music playlist. Tracks will play in order and loop.
                </p>

                {backgroundTracks.length > 0 ? (
                  <div className="space-y-2">
                    {backgroundTracks.map((track, index) => (
                      <div 
                        key={track.id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                            <span className="text-blue-600 font-bold">{index + 1}</span>
                          </div>
                          <div>
                            <p className="font-medium">{track.name}</p>
                            <p className="text-xs text-gray-500">
                              Uploaded {track.uploadedAt ? new Date(track.uploadedAt).toLocaleDateString() : 'recently'}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeBackgroundTrack(track.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No background tracks uploaded</p>
                    <p className="text-xs mt-1">Upload MP3 files above</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Announcement Audio Upload */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Announcement Audio Files</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Upload MP3 files for timer announcements (2 minutes, 1 minute, warmup done)
                </p>
                <AnnouncementUploader />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Entries Tab */}
          <TabsContent value="entries">
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>All Competition Entries</h2>
                  <p className="text-gray-600 text-sm mt-1">
                    Total: {allEntries.length} entries
                    {selectedCompetition !== 'all' && ` • Showing: ${
                      allEntries.filter(e => {
                        const matchesComp = e.competitionId === selectedCompetition;
                        const hasContent = (e.gymnastIds?.length > 0) || (e.coachIds?.length > 0) || (e.judgeIds?.length > 0);
                        const isSubmitted = e.status === 'Submitted' || e.submittedAt;
                        const isInProgress = hasContent && !isSubmitted;
                        
                        if (!matchesComp) return false;
                        if (entryStatusFilter === 'submitted') return isSubmitted;
                        if (entryStatusFilter === 'in-progress') return isInProgress;
                        return hasContent || isSubmitted; // 'all' - show entries with content
                      }).length
                    } filtered`}
                  </p>
                </div>
                <div className="flex gap-3">
                  {allEntries.length > 0 && selectedCompetition !== 'all' && (
                    <>
                      <Button
                        onClick={handleExportToExcel}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Export Submitted to Excel
                      </Button>
                    </>
                  )}
                  {allEntries.length > 0 && (
                    <Button
                      onClick={handleDeleteAllEntries}
                      variant="outline"
                      className="text-gray-900 border-gray-300 hover:bg-gray-100"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All Entries
                    </Button>
                  )}
                  <Button
                    onClick={handleDownloadAllMusic}
                    className="bg-[#000000] hover:bg-[#333333]"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {selectedCompetition === 'all' ? 'Download ALL Floor Music' : 'Download Floor Music'}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label htmlFor="comp-filter" className="text-sm font-semibold">Competition:</Label>
                  <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
                    <SelectTrigger id="comp-filter" className="w-72">
                      <SelectValue placeholder="Select competition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Competitions</SelectItem>
                      {competitions.map(comp => (
                        <SelectItem key={comp.id} value={comp.id}>{comp.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="status-filter" className="text-sm font-semibold">Status:</Label>
                  <Select value={entryStatusFilter} onValueChange={setEntryStatusFilter}>
                    <SelectTrigger id="status-filter" className="w-40">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Gymnast summary stats — shown only when a specific competition is selected */}
              {selectedCompetition !== 'all' && (() => {
                const compEntries = allEntries.filter(e => e.competitionId === selectedCompetition);
                const submittedGymnasts = compEntries
                  .filter(e => e.status === 'Submitted' || e.submittedAt)
                  .reduce((sum, e) => sum + (e.gymnasts?.length || e.gymnastIds?.length || 0), 0);
                const inProgressGymnasts = compEntries
                  .filter(e => !(e.status === 'Submitted' || e.submittedAt) && ((e.gymnasts?.length || e.gymnastIds?.length || 0) > 0))
                  .reduce((sum, e) => sum + (e.gymnasts?.length || e.gymnastIds?.length || 0), 0);
                return (
                  <div className="flex items-center gap-3 mt-3" data-testid="gymnast-summary-stats">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 border border-green-200">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-sm text-green-800">
                        <span className="font-bold text-lg">{submittedGymnasts}</span>
                        <span className="ml-1 font-medium">gymnast{submittedGymnasts !== 1 ? 's' : ''} submitted</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200">
                      <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                      <span className="text-sm text-amber-800">
                        <span className="font-bold text-lg">{inProgressGymnasts}</span>
                        <span className="ml-1 font-medium">gymnast{inProgressGymnasts !== 1 ? 's' : ''} in progress</span>
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {(() => {
              // Filter entries based on competition and status
              const filteredEntries = allEntries.filter(entry => {
                // Competition filter
                if (selectedCompetition !== 'all' && entry.competitionId !== selectedCompetition) {
                  return false;
                }
                
                // Check if entry has actual content (at least one gymnast, coach, or judge)
                const hasContent = (entry.gymnastIds?.length > 0) || (entry.coachIds?.length > 0) || (entry.judgeIds?.length > 0);
                const isSubmitted = entry.status === 'Submitted' || entry.submittedAt;
                const isInProgress = hasContent && !isSubmitted;
                
                // Status filter
                if (entryStatusFilter === 'submitted') return isSubmitted;
                if (entryStatusFilter === 'in-progress') return isInProgress;
                
                // 'all' - show entries with content OR submitted (hide empty forms)
                return hasContent || isSubmitted;
              });

              if (filteredEntries.length === 0) {
                return (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-gray-500">
                        {selectedCompetition === 'all' 
                          ? 'No entries found.' 
                          : `No ${entryStatusFilter === 'all' ? '' : entryStatusFilter + ' '}entries for this competition.`}
                      </p>
                    </CardContent>
                  </Card>
                );
              }

              return (
                <div className="space-y-4">
                  {filteredEntries.map(entry => (
                    <Card key={entry.id} className={`border-l-4 ${entry.status === 'Submitted' || entry.submittedAt ? 'border-l-green-600' : 'border-l-orange-500'}`}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{entry.club?.name}</CardTitle>
                            <CardDescription>
                              {entry.competition?.title}
                              {entry.status === "Submitted" && entry.submittedAt ? (
                                <span className="ml-2 text-green-600 font-semibold">
                                  ✓ Submitted: {formatUKDateTime(entry.submittedAt)}
                                </span>
                              ) : entry.status === "Submitted" ? (
                                <span className="ml-2 text-green-600 font-semibold">
                                  ✓ Submitted
                                </span>
                              ) : (
                                <span className="ml-2 text-orange-600 font-semibold">
                                  ⏳ In Progress
                                </span>
                              )}
                            </CardDescription>
                          </div>
                          <div className="flex gap-2">
                            {(entry.status === 'Submitted' || entry.submittedAt) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResendConfirmation(entry)}
                                className="text-blue-700 border-blue-300 hover:bg-blue-50"
                                data-testid={`resend-email-btn-${entry.id}`}
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Resend Email
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenEntryDialog(entry)}
                              className="text-[#000000] border-[#000000]"
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteEntry(entry.id, entry.club?.name, entry.competition?.title)}
                              className="text-gray-900 border-gray-300 hover:bg-gray-100"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="font-semibold text-sm mb-1">Gymnasts ({entry.gymnasts?.length || 0}):</p>
                          <p className="text-sm text-gray-700">
                            {entry.gymnasts?.map(g => {
                              const firstName = g.legalFirstName || g.firstName || '';
                            const lastName = g.legalLastName || g.lastName || '';
                            const category = entry.gymnastCategories?.[g.id] || g.category || 'N/A';
                            return `${firstName} ${lastName} (${category})`;
                          }).join(', ') || 'None'}
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-sm mb-1">Coaches ({entry.coaches?.length || 0}):</p>
                        <p className="text-sm text-gray-700">
                          {entry.coaches?.map(c => `${c.firstName} ${c.lastName}`).join(', ') || 'None'}
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-sm mb-1">Judges ({entry.judges?.length || 0}):</p>
                        {entry.judges?.length > 0 ? (
                          <div className="space-y-1">
                            {entry.judges.map(j => {
                              const compDays = entry.competition?.competitionDays || 1;
                              const days = entry.judgeDayAssignments?.[j.id];
                              let dayLabel = '';
                              if (compDays > 1 && days?.length > 0) {
                                const startsOn = entry.competition?.startsOn;
                                const dayNames = days.map(d => {
                                  if (startsOn) {
                                    try {
                                      const dt = new Date(startsOn);
                                      dt.setDate(dt.getDate() + d - 1);
                                      return format(dt, 'EEE d MMM');
                                    } catch {}
                                  }
                                  return `Day ${d}`;
                                });
                                dayLabel = ` — judging: ${dayNames.join(', ')}`;
                              }
                              return (
                                <p key={j.id} className="text-sm text-gray-700">
                                  {j.firstName} {j.lastName}
                                  {j.level && <span className="text-gray-500"> ({j.level})</span>}
                                  {dayLabel && <span className="text-blue-700 font-medium">{dayLabel}</span>}
                                </p>
                              );
                            })}
                          </div>
                        ) : <p className="text-sm text-gray-700">None</p>}
                      </div>
                      {entry.teams?.filter(t => t.status !== 'withdrawn').length > 0 && (
                        <div className="border-t pt-2">
                          <p className="font-semibold text-sm mb-1">Teams ({entry.teams.filter(t => t.status !== 'withdrawn').length}):</p>
                          <div className="space-y-1">
                            {entry.teams.filter(t => t.status !== 'withdrawn').map(team => (
                              <div key={team.id} className="text-sm text-gray-700">
                                <span className="font-medium">{team.name}</span>
                                {team.category && <span className="text-gray-500"> ({team.category})</span>}
                                {team.gymnasts?.length > 0 && (
                                  <span className="text-gray-500"> — {team.gymnasts.map(g =>
                                    `${g.legalFirstName || g.firstName || ''} ${g.legalLastName || g.lastName || ''}`.trim()
                                  ).join(', ')}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {entry.withdrawnGymnasts && entry.withdrawnGymnasts.length > 0 && (
                        <div className="border-t pt-2">
                          <p className="font-semibold text-sm mb-2 text-gray-900">Withdrawn Gymnasts:</p>
                          <div className="space-y-2">
                            {entry.withdrawnGymnasts.map((w, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-gray-100 p-2 rounded">
                                <p className="text-sm text-gray-700">
                                  {w.gymnastName} - {w.reason} {w.withdrawnAt ? `(${formatUKDate(w.withdrawnAt)})` : ''}
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUnwithdrawGymnast(entry.id, w.gymnastId, w.gymnastName)}
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                >
                                  Unwithdraw
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {entry.petitionedGymnasts && entry.petitionedGymnasts.filter(p => p.status === 'pending').length > 0 && (
                        <div className="border-t pt-2">
                          <p className="font-semibold text-sm mb-1 text-yellow-600">Pending Petitioned Gymnasts:</p>
                          {entry.petitionedGymnasts.filter(p => p.status === 'pending').map((petition, idx) => {
                            const petitionedGymnast = entry.gymnasts?.find(g => g.id === petition.gymnastId);
                            const firstName = petitionedGymnast?.legalFirstName || petitionedGymnast?.firstName || '';
                            const lastName = petitionedGymnast?.legalLastName || petitionedGymnast?.lastName || '';
                            const category = entry.gymnastCategories?.[petition.gymnastId] || 'N/A';
                            return (
                              <p key={idx} className="text-sm text-gray-700">
                                {firstName} {lastName} ({category}) - <span className="text-yellow-600 font-semibold">PENDING REVIEW</span>
                              </p>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="font-semibold text-[#000000]">Total: £{entry.total?.toFixed(2) || '0.00'}</span>
                        {entry.paymentProofUrl && (
                          <a
                            href={`${API}/payment-proof/${entry.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#000000] hover:underline text-sm"
                          >
                            View Payment Proof
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              );
            })()}
          </TabsContent>
        </Tabs>
      </main>

      {/* Competition Dialog */}
      <Dialog open={showCompDialog} onOpenChange={setShowCompDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingComp ? 'Edit Competition' : 'Create Competition'}</DialogTitle>
            <DialogDescription>
              {editingComp ? 'Update competition details' : 'Add a new competition'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveCompetition} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="title">Competition Title *</Label>
                <Input
                  id="title"
                  name="title"
                  required
                  value={compFormData.title}
                  onChange={handleCompChange}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  name="location"
                  value={compFormData.location}
                  onChange={handleCompChange}
                  placeholder="Venue address"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="displayDate">Display Date</Label>
                <Input
                  id="displayDate"
                  name="displayDate"
                  value={compFormData.displayDate}
                  onChange={handleCompChange}
                  placeholder="e.g., Sunday 23rd November 2025"
                />
              </div>
              <div>
                <Label htmlFor="startsOn">Competition Start *</Label>
                <Input
                  id="startsOn"
                  name="startsOn"
                  type="date"
                  required
                  value={compFormData.startsOn}
                  onChange={handleCompChange}
                />
              </div>
              <div>
                <Label htmlFor="endsOn">Competition End *</Label>
                <Input
                  id="endsOn"
                  name="endsOn"
                  type="date"
                  required
                  value={compFormData.endsOn}
                  onChange={handleCompChange}
                />
              </div>
              <div>
                <Label htmlFor="entryFee">Default Entry Fee (£) *</Label>
                <Input
                  id="entryFee"
                  name="entryFee"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={compFormData.entryFee}
                  onChange={handleCompChange}
                />
                <p className="text-xs text-gray-500 mt-1">Used for any category without a custom fee below.</p>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="isOpen"
                  checked={compFormData.isOpen}
                  onCheckedChange={(checked) => setCompFormData({ ...compFormData, isOpen: checked })}
                />
                <Label htmlFor="isOpen">Competition is Open</Label>
              </div>
              <div className="flex items-center space-x-2 pt-3">
                <Switch
                  id="isHidden"
                  checked={compFormData.isHidden || false}
                  onCheckedChange={(checked) => setCompFormData({ ...compFormData, isHidden: checked })}
                />
                <div>
                  <Label htmlFor="isHidden">Hidden from public listing</Label>
                  <p className="text-xs text-gray-500 mt-0.5">When ON, this competition won't appear on the public website. Scoring and admin pages still work normally.</p>
                </div>
              </div>
            </div>

            {/* Custom slug */}
            <div className="border-t pt-4 mt-4">
              <Label htmlFor="slug" className="text-base font-semibold">Custom URL slug</Label>
              <p className="text-sm text-gray-500 mb-2">
                Optional. Lets clubs go directly to this competition via <code>scorz.co.uk/&lt;slug&gt;</code>.
                Lowercase letters, numbers and hyphens only (e.g. <code>cartwheel-carnival</code>). Leave blank to disable.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 select-none">scorz.co.uk/</span>
                <Input
                  id="slug"
                  name="slug"
                  type="text"
                  placeholder="competition-name"
                  value={compFormData.slug || ''}
                  onChange={(e) => setCompFormData({ ...compFormData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  data-testid="comp-slug-input"
                />
              </div>
            </div>

            {/* Payment proof toggle */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <Switch
                  id="paymentProofRequired"
                  checked={compFormData.paymentProofRequired !== false}
                  onCheckedChange={(checked) => setCompFormData({ ...compFormData, paymentProofRequired: checked })}
                  data-testid="comp-payment-proof-switch"
                />
                <div className="flex-1">
                  <Label htmlFor="paymentProofRequired" className="font-semibold">Require payment proof to submit entry</Label>
                  <p className="text-xs text-gray-500">
                    When ON, clubs must upload a payment screenshot before they can submit. When OFF, entries can be submitted on trust (e.g. for clubs paying within 3–4 working days).
                  </p>
                </div>
              </div>
              {compFormData.paymentProofRequired === false && (
                <div className="mt-2 pl-2">
                  <Label className="text-sm font-medium">Payment instruction message</Label>
                  <p className="text-xs text-gray-500 mb-1">Shown to clubs on the entry page instead of the upload widget.</p>
                  <textarea
                    data-testid="comp-payment-message-input"
                    className="w-full border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-black"
                    rows={3}
                    placeholder="e.g. Payment is required within 3–4 working days from moment of submission. Please use your club name as the payment reference."
                    value={compFormData.paymentMessage || ''}
                    onChange={(e) => setCompFormData({ ...compFormData, paymentMessage: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* Self-withdraw toggle */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <Switch
                  id="selfWithdrawEnabled"
                  checked={compFormData.selfWithdrawEnabled !== false}
                  onCheckedChange={(checked) => setCompFormData({ ...compFormData, selfWithdrawEnabled: checked })}
                  data-testid="comp-self-withdraw-switch"
                />
                <div className="flex-1">
                  <Label htmlFor="selfWithdrawEnabled" className="font-semibold">Allow clubs to self-withdraw gymnasts</Label>
                  <p className="text-xs text-gray-500">
                    When ON, clubs see a Withdraw button on submitted entries. When OFF, the button is blocked and clubs see your contact message instead.
                  </p>
                </div>
              </div>
              {compFormData.selfWithdrawEnabled === false && (
                <div className="mt-2 pl-2">
                  <Label className="text-sm font-medium">Withdrawal contact message</Label>
                  <p className="text-xs text-gray-500 mb-1">Shown when a club clicks Withdraw (e.g. direct them to email you).</p>
                  <textarea
                    data-testid="comp-withdraw-message-input"
                    className="w-full border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-black"
                    rows={2}
                    placeholder="e.g. To withdraw a gymnast please email withdrawals@example.com"
                    value={compFormData.withdrawContactMessage || ''}
                    onChange={(e) => setCompFormData({ ...compFormData, withdrawContactMessage: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* Secondary organiser email */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-semibold">Secondary organiser email (CC on entries)</Label>
              <p className="text-xs text-gray-500 mb-1">
                This address will receive a copy of every <strong>entry submitted</strong> confirmation email. Leave blank if not needed.
              </p>
              <Input
                data-testid="comp-secondary-email-input"
                type="email"
                placeholder="e.g. assistant@example.com"
                value={compFormData.secondaryOrganizerEmail || ''}
                onChange={(e) => setCompFormData({ ...compFormData, secondaryOrganizerEmail: e.target.value })}
              />
            </div>

            {/* Team event */}
            <div className="border-t pt-4 mt-4 space-y-3">
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <Switch
                  id="teamEventEnabled"
                  checked={compFormData.teamEventEnabled === true}
                  onCheckedChange={(checked) => setCompFormData({ ...compFormData, teamEventEnabled: checked })}
                  data-testid="comp-team-enabled-switch"
                />
                <div className="flex-1">
                  <Label htmlFor="teamEventEnabled" className="font-semibold">Enable team event</Label>
                  <p className="text-xs text-gray-500">
                    When ON, clubs see an extra "Enter a team?" step in the entry flow after individual entry. Top-N scores per apparatus count toward the team total.
                  </p>
                </div>
              </div>

              {compFormData.teamEventEnabled && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 border rounded-lg bg-gray-50">
                  <div>
                    <Label className="text-xs">Team size (max)</Label>
                    <Input type="number" min="1" max="20" value={compFormData.teamSize}
                      onChange={(e) => setCompFormData({ ...compFormData, teamSize: parseInt(e.target.value) || 6 })}
                      data-testid="comp-team-size" />
                  </div>
                  <div>
                    <Label className="text-xs">Counting scores (top N)</Label>
                    <Input type="number" min="1" max="20" value={compFormData.teamCountingScores}
                      onChange={(e) => setCompFormData({ ...compFormData, teamCountingScores: parseInt(e.target.value) || 4 })}
                      data-testid="comp-team-counting" />
                  </div>
                  <div>
                    <Label className="text-xs">Team fee (£)</Label>
                    <Input type="number" min="0" step="0.01" value={(compFormData.teamEntryFee || 0) / 100}
                      onChange={(e) => setCompFormData({ ...compFormData, teamEntryFee: Math.round(parseFloat(e.target.value || '0') * 100) })}
                      data-testid="comp-team-fee" />
                  </div>
                  <div>
                    <Label className="text-xs">Max teams per club (0 = unlimited)</Label>
                    <Input type="number" min="0" max="50" value={compFormData.teamMaxPerClub}
                      onChange={(e) => setCompFormData({ ...compFormData, teamMaxPerClub: parseInt(e.target.value) || 0 })}
                      data-testid="comp-team-max-per-club" />
                  </div>
                </div>
              )}

              {/* Team advanced (Wave 1) — visible only when team event is enabled */}
              {compFormData.teamEventEnabled && (
                <div className="p-3 border rounded-lg bg-gray-50 space-y-3">
                  <div>
                    <Label className="text-sm font-semibold">Team format (5-5-3 etc.)</Label>
                    <p className="text-xs text-gray-500">Configure how many gymnasts compete on each apparatus and how many scores count toward the team total.</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Competing on each apparatus (0 = same as roster)</Label>
                      <Input type="number" min="0" max="20" value={compFormData.teamCompetingScores}
                        onChange={(e) => setCompFormData({ ...compFormData, teamCompetingScores: parseInt(e.target.value) || 0 })}
                        data-testid="comp-team-competing" />
                    </div>
                    <div>
                      <Label className="text-xs">Team awards top N</Label>
                      <Input type="number" min="1" max="10" value={compFormData.teamAwardsTopN}
                        onChange={(e) => setCompFormData({ ...compFormData, teamAwardsTopN: parseInt(e.target.value) || 3 })}
                        data-testid="comp-team-awards-topn" />
                    </div>
                    <div className="flex items-center gap-2 pt-4">
                      <Switch
                        id="teamTrophyForFirst"
                        checked={compFormData.teamTrophyForFirst}
                        onCheckedChange={(v) => setCompFormData({ ...compFormData, teamTrophyForFirst: v })}
                        data-testid="comp-team-trophy-switch"
                      />
                      <Label htmlFor="teamTrophyForFirst" className="text-xs">Trophy for 1st place</Label>
                    </div>
                  </div>

                  <div className="border-t pt-3 space-y-2">
                    <Label className="text-sm font-semibold">Multi-club teams</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="teamAllowMultiClub"
                        checked={compFormData.teamAllowMultiClub}
                        onCheckedChange={(v) => setCompFormData({ ...compFormData, teamAllowMultiClub: v })}
                        data-testid="comp-team-allow-multiclub"
                      />
                      <Label htmlFor="teamAllowMultiClub" className="text-xs">Allow a team to include gymnasts from multiple clubs</Label>
                    </div>
                    {compFormData.teamAllowMultiClub && (
                      <div className="max-w-[240px]">
                        <Label className="text-xs">Max distinct clubs per team</Label>
                        <Input type="number" min="1" max="5" value={compFormData.teamMaxClubsPerTeam}
                          onChange={(e) => setCompFormData({ ...compFormData, teamMaxClubsPerTeam: parseInt(e.target.value) || 1 })}
                          data-testid="comp-team-max-clubs" />
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm font-semibold">Named team categories</Label>
                        <p className="text-xs text-gray-500">e.g. Junior Team (born 2011–2016), Senior Team (born 2010 & older).</p>
                      </div>
                      <Button type="button" size="sm" variant="outline"
                        onClick={() => {
                          const next = [...(compFormData.teamCategories || []), { name: '', minBirthYear: null, maxBirthYear: null, feePence: compFormData.teamEntryFee || 0, notes: '' }];
                          setCompFormData({ ...compFormData, teamCategories: next });
                        }}
                        data-testid="comp-team-cat-add"
                      >+ Add</Button>
                    </div>
                    {(compFormData.teamCategories || []).map((tc, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 bg-white rounded border" data-testid={`comp-team-cat-row-${idx}`}>
                        <div className="col-span-4">
                          <Label className="text-xs">Name</Label>
                          <Input value={tc.name || ''} onChange={(e) => {
                            const next = [...compFormData.teamCategories];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setCompFormData({ ...compFormData, teamCategories: next });
                          }} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Oldest yr</Label>
                          <Input type="number" placeholder="open" value={tc.minBirthYear ?? ''} onChange={(e) => {
                            const v = e.target.value.trim();
                            const next = [...compFormData.teamCategories];
                            next[idx] = { ...next[idx], minBirthYear: v === '' ? null : (parseInt(v) || null) };
                            setCompFormData({ ...compFormData, teamCategories: next });
                          }} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Youngest yr</Label>
                          <Input type="number" placeholder="open" value={tc.maxBirthYear ?? ''} onChange={(e) => {
                            const v = e.target.value.trim();
                            const next = [...compFormData.teamCategories];
                            next[idx] = { ...next[idx], maxBirthYear: v === '' ? null : (parseInt(v) || null) };
                            setCompFormData({ ...compFormData, teamCategories: next });
                          }} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Fee (£)</Label>
                          <Input type="number" min="0" step="0.01" value={(tc.feePence || 0) / 100} onChange={(e) => {
                            const next = [...compFormData.teamCategories];
                            next[idx] = { ...next[idx], feePence: Math.round(parseFloat(e.target.value || '0') * 100) };
                            setCompFormData({ ...compFormData, teamCategories: next });
                          }} />
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <Button type="button" variant="ghost" size="sm" className="text-red-600"
                            onClick={() => {
                              const next = compFormData.teamCategories.filter((_, i) => i !== idx);
                              setCompFormData({ ...compFormData, teamCategories: next });
                            }}
                          >Remove</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Advanced category configuration (Wave 1) */}
            <div className="border-t pt-4 mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-semibold">Advanced category configuration</Label>
                  <p className="text-xs text-gray-500">
                    Optional — when populated, replaces the simple category list above with rich per-category rules (birth-year window, vault format, awards).
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm"
                    onClick={async () => {
                      if (!editingComp) { toast.error('Save the competition first, then apply the template.'); return; }
                      if (!window.confirm('Apply Healthy Living 2026 template? Existing category config, team format, judges ladder and payment settings will be overwritten with the HL2026 rulebook values.')) return;
                      try {
                        const resp = await axios.post(`${API}/superadmin/competitions/${editingComp.id}/apply-template/healthy-living-2026`);
                        toast.success('Healthy Living 2026 template applied');
                        // Refresh form from response
                        const c = resp.data.competition || {};
                        setCompFormData({
                          ...compFormData,
                          categoriesConfig: c.categoriesConfig || [],
                          categories: c.categories || [],
                          categoryFees: c.categoryFees || {},
                          teamEventEnabled: !!c.teamEventEnabled,
                          teamSize: c.teamSize ?? 5,
                          teamCompetingScores: c.teamCompetingScores ?? 5,
                          teamCountingScores: c.teamCountingScores ?? 3,
                          teamEntryFee: c.teamEntryFee ?? 4000,
                          teamAllowMultiClub: !!c.teamAllowMultiClub,
                          teamMaxClubsPerTeam: c.teamMaxClubsPerTeam ?? 2,
                          teamCategories: c.teamCategories || [],
                          judgeRequirementLadder: c.judgeRequirementLadder || [],
                          requireFloorMusicForEntry: !!c.requireFloorMusicForEntry,
                          entryEligibilityText: c.entryEligibilityText || '',
                          photographyOptOutEmail: c.photographyOptOutEmail || '',
                          missingJudgeFinePence: c.missingJudgeFinePence || 0,
                        });
                        loadAllData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to apply template');
                      }
                    }}
                    data-testid="apply-hl2026-template"
                  >
                    Apply "Healthy Living 2026" template
                  </Button>
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => {
                      const next = [...(compFormData.categoriesConfig || []), {
                        name: '', group: 'Custom', categoryType: 'individual',
                        minBirthYear: null, maxBirthYear: null,
                        feeGbp: compFormData.entryFee || 40, vaultCount: 1, vaultRanking: 'first',
                        vaultGroupsRule: 'any', vaultForAATeam: 'first',
                        awardsTopN: 3, ribbonsTopN: 6, awardAA: true, awardPerApparatus: true, enabled: true,
                      }];
                      setCompFormData({ ...compFormData, categoriesConfig: next });
                    }}
                    data-testid="comp-cat-cfg-add"
                  >+ Add category</Button>
                </div>
              </div>
              {(compFormData.categoriesConfig || []).length > 0 && (
                <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                  {(compFormData.categoriesConfig || []).map((c, idx) => (
                    <div key={idx} className={`p-3 border rounded-lg ${c.categoryType === 'team' ? 'bg-blue-50 border-blue-200' : 'bg-white'}`} data-testid={`cat-cfg-row-${idx}`}>
                      {/* Row 1: Type badge + Name + Birth years + Fee + Remove */}
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-1">
                          <Label className="text-xs">Type</Label>
                          <Select value={c.categoryType || 'individual'} onValueChange={(v) => {
                            const next = [...compFormData.categoriesConfig];
                            next[idx] = { ...next[idx], categoryType: v };
                            setCompFormData({ ...compFormData, categoriesConfig: next });
                          }}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="individual">Ind</SelectItem>
                              <SelectItem value="team">Team</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Category name</Label>
                          <Input value={c.name || ''} onChange={(e) => {
                            const next = [...compFormData.categoriesConfig];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setCompFormData({ ...compFormData, categoriesConfig: next });
                          }} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Min age (oldest)</Label>
                          <Input type="number" placeholder="open" min="1" max="99"
                            value={c.minAge ?? (c.minBirthYear ? Math.max(0, (new Date(compFormData.startsOn || Date.now()).getFullYear()) - c.minBirthYear) : '')}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              const next = [...compFormData.categoriesConfig];
                              const compYear = new Date(compFormData.startsOn || Date.now()).getFullYear();
                              const age = v === '' ? null : (parseInt(v) || null);
                              next[idx] = {
                                ...next[idx],
                                minAge: age,
                                // min age → gymnast must be at LEAST this old → maxBirthYear = compYear - minAge
                                maxBirthYear: age !== null ? compYear - age : null
                              };
                              setCompFormData({ ...compFormData, categoriesConfig: next });
                            }} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Max age (youngest)</Label>
                          <Input type="number" placeholder="open" min="1" max="99"
                            value={c.maxAge ?? (c.maxBirthYear ? Math.max(0, (new Date(compFormData.startsOn || Date.now()).getFullYear()) - c.maxBirthYear) : '')}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              const next = [...compFormData.categoriesConfig];
                              const compYear = new Date(compFormData.startsOn || Date.now()).getFullYear();
                              const age = v === '' ? null : (parseInt(v) || null);
                              next[idx] = {
                                ...next[idx],
                                maxAge: age,
                                // max age → gymnast must be at MOST this old → minBirthYear = compYear - maxAge
                                minBirthYear: age !== null ? compYear - age : null
                              };
                              setCompFormData({ ...compFormData, categoriesConfig: next });
                            }} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Fee (£)</Label>
                          <Input type="number" min="0" step="0.01" value={c.feeGbp ?? 40} onChange={(e) => {
                            const next = [...compFormData.categoriesConfig];
                            next[idx] = { ...next[idx], feeGbp: parseFloat(e.target.value || '0') };
                            setCompFormData({ ...compFormData, categoriesConfig: next });
                          }} />
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <Button type="button" variant="ghost" size="sm" className="text-red-600"
                            onClick={() => {
                              const next = compFormData.categoriesConfig.filter((_, i) => i !== idx);
                              setCompFormData({ ...compFormData, categoriesConfig: next });
                            }}
                          >Remove</Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-12 gap-2 mt-2 items-end">
                        {c.categoryType !== 'team' ? (
                          <>
                            <div className="col-span-2">
                              <Label className="text-xs">Vaults</Label>
                              <Select value={String(c.vaultCount ?? 1)} onValueChange={(v) => {
                                const next = [...compFormData.categoriesConfig];
                                next[idx] = { ...next[idx], vaultCount: parseInt(v) };
                                setCompFormData({ ...compFormData, categoriesConfig: next });
                              }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">1 vault</SelectItem>
                                  <SelectItem value="2">2 vaults</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs">Vault ranking</Label>
                              <Select value={c.vaultRanking || 'first'} onValueChange={(v) => {
                                const next = [...compFormData.categoriesConfig];
                                next[idx] = { ...next[idx], vaultRanking: v };
                                setCompFormData({ ...compFormData, categoriesConfig: next });
                              }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="first">1st vault only</SelectItem>
                                  <SelectItem value="best">Best of vaults</SelectItem>
                                  <SelectItem value="average">Average of vaults</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs">Vault groups</Label>
                              <Select value={c.vaultGroupsRule || 'any'} onValueChange={(v) => {
                                const next = [...compFormData.categoriesConfig];
                                next[idx] = { ...next[idx], vaultGroupsRule: v };
                                setCompFormData({ ...compFormData, categoriesConfig: next });
                              }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="any">Any groups</SelectItem>
                                  <SelectItem value="different_groups">Must be different groups</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">AA/team uses</Label>
                              <Select value={c.vaultForAATeam || 'first'} onValueChange={(v) => {
                                const next = [...compFormData.categoriesConfig];
                                next[idx] = { ...next[idx], vaultForAATeam: v };
                                setCompFormData({ ...compFormData, categoriesConfig: next });
                              }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="first">1st vault</SelectItem>
                                  <SelectItem value="best">Best</SelectItem>
                                  <SelectItem value="average">Average</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-1">
                              <Label className="text-xs">Awards</Label>
                              <Input type="number" min="1" max="10" value={c.awardsTopN ?? 3} onChange={(e) => {
                                const next = [...compFormData.categoriesConfig];
                                next[idx] = { ...next[idx], awardsTopN: parseInt(e.target.value) || 3 };
                                setCompFormData({ ...compFormData, categoriesConfig: next });
                              }} />
                            </div>
                            <div className="col-span-1">
                              <Label className="text-xs">Ribbons</Label>
                              <Input type="number" min="0" max="20" value={c.ribbonsTopN ?? 6} onChange={(e) => {
                                const next = [...compFormData.categoriesConfig];
                                next[idx] = { ...next[idx], ribbonsTopN: parseInt(e.target.value) || 6 };
                                setCompFormData({ ...compFormData, categoriesConfig: next });
                              }} />
                            </div>
                          </>
                        ) : (
                          <div className="col-span-12">
                            <p className="text-xs text-blue-600 italic">
                              Team category — entered as a team unit. Gymnasts can also appear in individual categories without double-counting.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sponsors */}
            <div className="border-t pt-4 mt-4 space-y-3">
              <Label className="text-base font-semibold">Sponsors</Label>
              <div className="space-y-3">
                {(compFormData.sponsors || []).map((sponsor, idx) => (
                  <div key={sponsor.id || idx} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                    <div className="flex items-start gap-3">
                      {/* Logo preview / upload */}
                      <div className="shrink-0">
                        {sponsor.logoUrl ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${sponsor.logoUrl}`}
                            alt="Sponsor logo"
                            className="h-16 w-24 object-contain border rounded bg-white"
                          />
                        ) : (
                          <div className="h-16 w-24 border-2 border-dashed rounded flex items-center justify-center text-gray-400 text-xs">
                            No logo
                          </div>
                        )}
                        <label className="mt-1 block">
                          <span className="text-xs text-blue-600 cursor-pointer hover:underline">
                            {sponsor.logoUrl ? 'Change' : 'Upload logo'}
                          </span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/superadmin/upload-sponsor-logo`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                  body: formData
                                });
                                const data = await res.json();
                                const updated = [...(compFormData.sponsors || [])];
                                updated[idx] = { ...updated[idx], logoUrl: data.url };
                                setCompFormData({ ...compFormData, sponsors: updated });
                              } catch (err) {
                                console.error('Upload failed', err);
                              }
                            }}
                          />
                        </label>
                      </div>
                      {/* Text + remove */}
                      <div className="flex-1 space-y-1">
                        <Input
                          placeholder="Sponsor name / text (optional)"
                          value={sponsor.text || ''}
                          onChange={(e) => {
                            const updated = [...(compFormData.sponsors || [])];
                            updated[idx] = { ...updated[idx], text: e.target.value };
                            setCompFormData({ ...compFormData, sponsors: updated });
                          }}
                          className="text-sm"
                        />
                        <button
                          type="button"
                          className="text-xs text-red-500 hover:underline"
                          onClick={() => {
                            const updated = (compFormData.sponsors || []).filter((_, i) => i !== idx);
                            setCompFormData({ ...compFormData, sponsors: updated });
                          }}
                        >
                          Remove sponsor
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCompFormData({
                    ...compFormData,
                    sponsors: [...(compFormData.sponsors || []), { id: Date.now().toString(), logoUrl: '', text: '' }]
                  })}
                >
                  + Add Sponsor
                </Button>
              </div>
            </div>

            {/* Display settings */}
            <div className="border-t pt-4 mt-4 space-y-3">
              <Label className="text-base font-semibold">Display</Label>
              <div className="p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={compFormData.splitPanelDisplay === true}
                    onCheckedChange={(v) => setCompFormData({ ...compFormData, splitPanelDisplay: v })}
                    data-testid="comp-split-panel-display"
                  />
                  <div>
                    <Label className="text-sm font-medium">Separate Panel Displays</Label>
                    <p className="text-xs text-gray-500">Enables /display/a and /display/b — each shows a 4-panel score view (VT, UB, BB, FX) for that panel only</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced validation & info (Wave 1) */}
            <div className="border-t pt-4 mt-4 space-y-3">
              <Label className="text-base font-semibold">Advanced validation & info</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 border rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Switch checked={compFormData.requireFloorMusicForEntry}
                      onCheckedChange={(v) => setCompFormData({ ...compFormData, requireFloorMusicForEntry: v })}
                      data-testid="comp-require-music" />
                    <Label className="text-sm">Require floor music (.mp3) before entry can be submitted</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={compFormData.strictBirthYearValidation}
                      onCheckedChange={(v) => setCompFormData({ ...compFormData, strictBirthYearValidation: v })}
                      data-testid="comp-strict-yob" />
                    <Label className="text-sm">Strict birth-year validation (block save on mismatch)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={compFormData.petitionedGymnastsEnabled === true}
                      onCheckedChange={(v) => setCompFormData({ ...compFormData, petitionedGymnastsEnabled: v })}
                      data-testid="comp-petitioned-enabled" />
                    <Label className="text-sm">Allow clubs to submit petitioned gymnasts</Label>
                  </div>
                </div>
                <div className="p-3 border rounded-lg space-y-2">
                  <div>
                    <Label className="text-xs">Entry capacity (0 = unlimited)</Label>
                    <Input type="number" min="0" value={compFormData.entryCapacity}
                      onChange={(e) => setCompFormData({ ...compFormData, entryCapacity: parseInt(e.target.value) || 0 })}
                      data-testid="comp-capacity" />
                  </div>
                  <div>
                    <Label className="text-xs">Missing-judge fine (£, informational)</Label>
                    <Input type="number" min="0" step="0.01" value={(compFormData.missingJudgeFinePence || 0) / 100}
                      onChange={(e) => setCompFormData({ ...compFormData, missingJudgeFinePence: Math.round(parseFloat(e.target.value || '0') * 100) })}
                      data-testid="comp-fine" />
                  </div>
                </div>
                <div className="p-3 border rounded-lg space-y-2 md:col-span-2">
                  <div>
                    <Label className="text-xs">Eligibility note (shown to clubs)</Label>
                    <Input value={compFormData.entryEligibilityText}
                      onChange={(e) => setCompFormData({ ...compFormData, entryEligibilityText: e.target.value })}
                      placeholder="e.g. gymnasts must be BG Silver members"
                      data-testid="comp-eligibility-text" />
                  </div>
                  <div>
                    <Label className="text-xs">Photography opt-out email</Label>
                    <Input value={compFormData.photographyOptOutEmail}
                      onChange={(e) => setCompFormData({ ...compFormData, photographyOptOutEmail: e.target.value })}
                      placeholder="e.g. organiser@club.co.uk"
                      data-testid="comp-photo-optout" />
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Judge requirement ladder</Label>
              <p className="text-xs text-gray-500 mb-3">
                Map gymnast count → judges required. The largest threshold a club meets wins.
                <strong className="text-gray-700"> When any rungs are added here, this ladder is used instead of the flat "min judges" floor above.</strong>
              </p>
              {/* Competition days */}
              <div className="flex items-center gap-3 mb-4">
                <Label className="text-sm font-medium w-40 shrink-0">Competition days</Label>
                <Input
                  data-testid="comp-competition-days-input"
                  type="number" min="1" max="7"
                  className="w-24"
                  value={compFormData.competitionDays ?? 1}
                  onChange={(e) => setCompFormData({ ...compFormData, competitionDays: Math.max(1, parseInt(e.target.value) || 1) })}
                />
                <p className="text-xs text-gray-500">Clubs can assign the same judge to multiple days — each day they cover counts as one judge-slot toward the requirement.</p>
              </div>
              <div className="space-y-2">
                {(compFormData.judgeRequirementLadder || []).map((rung, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`judge-ladder-row-${idx}`}>
                    <span className="text-xs text-gray-500 w-16">If ≥</span>
                    <Input type="number" min="1" value={rung.minGymnasts}
                      onChange={(e) => {
                        const next = [...compFormData.judgeRequirementLadder];
                        next[idx] = { ...next[idx], minGymnasts: parseInt(e.target.value) || 1 };
                        setCompFormData({ ...compFormData, judgeRequirementLadder: next });
                      }}
                      className="w-24" />
                    <span className="text-xs text-gray-500">gymnasts → require</span>
                    <Input type="number" min="0" value={rung.judgesRequired}
                      onChange={(e) => {
                        const next = [...compFormData.judgeRequirementLadder];
                        next[idx] = { ...next[idx], judgesRequired: parseInt(e.target.value) || 0 };
                        setCompFormData({ ...compFormData, judgeRequirementLadder: next });
                      }}
                      className="w-24" />
                    <span className="text-xs text-gray-500">judges</span>
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => {
                        const next = compFormData.judgeRequirementLadder.filter((_, i) => i !== idx);
                        setCompFormData({ ...compFormData, judgeRequirementLadder: next });
                      }}
                      data-testid={`judge-ladder-remove-${idx}`}
                    >Remove</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setCompFormData({
                    ...compFormData,
                    judgeRequirementLadder: [
                      ...(compFormData.judgeRequirementLadder || []),
                      { minGymnasts: 1, judgesRequired: 0 },
                    ],
                  })}
                  data-testid="judge-ladder-add"
                >+ Add rung</Button>
              </div>
              {compFormData.judgeRequirementLadder?.length === 0 && (
                <p className="text-xs text-gray-400 mt-2">Tip: try 1→0, 2→1, 11→2.</p>
              )}
            </div>

            {/* Per-category fees & age bands */}
            {compFormData.categories && compFormData.categories.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <Label className="text-base font-semibold">Per-category fees & age bands</Label>
                <p className="text-sm text-gray-500 mb-3">
                  Set an optional fee override and age eligibility for each category. Leave age blank for no restriction.
                </p>
                <div className="space-y-2">
                  {compFormData.categories.map((cat) => (
                    <div key={cat} className="flex items-center gap-2 border rounded-lg p-2 flex-wrap">
                      <span className="w-32 text-sm font-medium truncate shrink-0">{cat}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400 text-xs">£</span>
                        <Input
                          type="number" step="0.01" min="0"
                          placeholder={String(compFormData.entryFee ?? '')}
                          className="w-20 text-sm"
                          title="Fee (£)"
                          value={compFormData.categoryFees?.[cat] !== undefined ? compFormData.categoryFees[cat] : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCompFormData(prev => {
                              const next = { ...(prev.categoryFees || {}) };
                              if (val === '') { delete next[cat]; } else { const n = parseFloat(val); if (!isNaN(n)) next[cat] = n; }
                              return { ...prev, categoryFees: next };
                            });
                          }}
                          data-testid={`category-fee-input-${cat}`}
                        />
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <span className="text-xs text-gray-500 shrink-0">Age:</span>
                        <Input
                          type="number" min="1" max="99"
                          placeholder="min"
                          className="w-16 text-sm"
                          title="Minimum age (oldest eligible)"
                          value={compFormData.categoryAgeBands?.[cat]?.minAge ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCompFormData(prev => {
                              const bands = { ...(prev.categoryAgeBands || {}) };
                              bands[cat] = { ...(bands[cat] || {}), minAge: val === '' ? undefined : parseInt(val) };
                              if (!bands[cat].minAge && !bands[cat].maxAge) delete bands[cat];
                              return { ...prev, categoryAgeBands: bands };
                            });
                          }}
                        />
                        <span className="text-xs text-gray-400">–</span>
                        <Input
                          type="number" min="1" max="99"
                          placeholder="max"
                          className="w-16 text-sm"
                          title="Maximum age (youngest eligible)"
                          value={compFormData.categoryAgeBands?.[cat]?.maxAge ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCompFormData(prev => {
                              const bands = { ...(prev.categoryAgeBands || {}) };
                              bands[cat] = { ...(bands[cat] || {}), maxAge: val === '' ? undefined : parseInt(val) };
                              if (!bands[cat].minAge && !bands[cat].maxAge) delete bands[cat];
                              return { ...prev, categoryAgeBands: bands };
                            });
                          }}
                        />
                        {compFormData.categoryAgeBands?.[cat]?.minAge || compFormData.categoryAgeBands?.[cat]?.maxAge ? (
                          <span className="text-xs text-blue-600 ml-1">
                            {compFormData.categoryAgeBands[cat]?.minAge && compFormData.categoryAgeBands[cat]?.maxAge
                              ? `${compFormData.categoryAgeBands[cat].minAge}–${compFormData.categoryAgeBands[cat].maxAge} yrs`
                              : compFormData.categoryAgeBands[cat]?.minAge
                                ? `${compFormData.categoryAgeBands[cat].minAge}+ yrs`
                                : `under ${compFormData.categoryAgeBands[cat].maxAge + 1} yrs`}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Entry Window Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Entry Window</Label>
              <p className="text-sm text-gray-500 mb-3">Set when entries open and close</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="entryStartDate">Entries Open Date</Label>
                  <Input
                    id="entryStartDate"
                    name="entryStartDate"
                    type="date"
                    value={compFormData.entryStartDate}
                    onChange={handleCompChange}
                  />
                </div>
                <div>
                  <Label htmlFor="entryStartTime">Entries Open Time</Label>
                  <Input
                    id="entryStartTime"
                    name="entryStartTime"
                    value={compFormData.entryStartTime}
                    onChange={handleCompChange}
                    placeholder="e.g., 9:00am"
                  />
                </div>
                <div>
                  <Label htmlFor="closingDate">Entries Close Date</Label>
                  <Input
                    id="closingDate"
                    name="closingDate"
                    type="date"
                    value={compFormData.closingDate}
                    onChange={handleCompChange}
                  />
                </div>
                <div>
                  <Label htmlFor="closingTime">Entries Close Time</Label>
                  <Input
                    id="closingTime"
                    name="closingTime"
                    value={compFormData.closingTime}
                    onChange={handleCompChange}
                    placeholder="e.g., 11:59pm"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="entriesOpenSoonMessage">"Entries Open Soon" Message</Label>
                  <Input
                    id="entriesOpenSoonMessage"
                    name="entriesOpenSoonMessage"
                    value={compFormData.entriesOpenSoonMessage}
                    onChange={handleCompChange}
                    placeholder="Entries open soon"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="closedMessage">"Entries Closed" Message</Label>
                  <Input
                    id="closedMessage"
                    name="closedMessage"
                    value={compFormData.closedMessage}
                    onChange={handleCompChange}
                    placeholder="Entries for this competition are now closed"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="judgeBypassMessage">Judge Bypass Help Text</Label>
                  <Input
                    id="judgeBypassMessage"
                    name="judgeBypassMessage"
                    value={compFormData.judgeBypassMessage}
                    onChange={handleCompChange}
                    placeholder="Don't have a judge available? Contact Scorz for a one-time bypass code."
                  />
                  <p className="text-xs text-gray-500 mt-1">Shown to clubs in the judge selection section</p>
                </div>
              </div>
            </div>

            {/* Bank Account Details Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Bank Account Details</Label>
              <p className="text-sm text-gray-500 mb-3">Payment details shown to clubs</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bankAccountName">Name on Account</Label>
                  <Input
                    id="bankAccountName"
                    name="bankAccountName"
                    value={compFormData.bankAccountName}
                    onChange={handleCompChange}
                    placeholder="Account holder name"
                  />
                </div>
                <div>
                  <Label htmlFor="bankName">Bank Name</Label>
                  <Input
                    id="bankName"
                    name="bankName"
                    value={compFormData.bankName}
                    onChange={handleCompChange}
                    placeholder="e.g., Barclays"
                  />
                </div>
                <div>
                  <Label htmlFor="bankSortCode">Sort Code</Label>
                  <Input
                    id="bankSortCode"
                    name="bankSortCode"
                    value={compFormData.bankSortCode}
                    onChange={handleCompChange}
                    placeholder="00-00-00"
                  />
                </div>
                <div>
                  <Label htmlFor="bankAccountNumber">Account Number</Label>
                  <Input
                    id="bankAccountNumber"
                    name="bankAccountNumber"
                    value={compFormData.bankAccountNumber}
                    onChange={handleCompChange}
                    placeholder="12345678"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="referenceTemplate">Payment Reference Template</Label>
                  <Input
                    id="referenceTemplate"
                    name="referenceTemplate"
                    value={compFormData.referenceTemplate}
                    onChange={handleCompChange}
                    placeholder="e.g., {COMP_DATE} {CLUB_NAME}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Placeholders: {'{CLUB_NAME}'}, {'{CLUB_CODE}'}, {'{COMP_DATE}'} (e.g., 23NOV), {'{COMP_TITLE}'}
                  </p>
                </div>
              </div>
            </div>

            {/* Spectator Payment Settings */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Spectator Payment Settings (SumUp Solo)</Label>
              <p className="text-sm text-gray-500 mb-3">Configure spectator pricing for check-in payments</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="includedSpectators">Included Spectators</Label>
                  <Input
                    id="includedSpectators"
                    name="includedSpectators"
                    type="number"
                    min="0"
                    value={compFormData.includedSpectators}
                    onChange={handleCompChange}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Free with entry</p>
                </div>
                <div>
                  <Label htmlFor="extraSpectatorPriceGbp">Extra Spectator Price (£)</Label>
                  <Input
                    id="extraSpectatorPriceGbp"
                    name="extraSpectatorPriceGbp"
                    type="number"
                    min="0"
                    step="0.01"
                    value={compFormData.extraSpectatorPriceGbp}
                    onChange={handleCompChange}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Per extra spectator</p>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    id="requirePaymentForExtraSpectators"
                    checked={compFormData.requirePaymentForExtraSpectators}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, requirePaymentForExtraSpectators: checked })}
                    data-testid="require-spectator-payment-toggle"
                  />
                  <Label htmlFor="requirePaymentForExtraSpectators" className="cursor-pointer">
                    Require payment for extra spectators
                  </Label>
                </div>
              </div>
            </div>

            {/* Public Display Fields */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Public Display Settings</Label>
              <p className="text-sm text-gray-500 mb-3">Configure what is shown on the public event page</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="organiserImage">Organiser Image URL</Label>
                  <Input
                    id="organiserImage"
                    name="organiserImage"
                    value={compFormData.organiserImage}
                    onChange={handleCompChange}
                    placeholder="https://example.com/logo.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">Image shown on event tile and detail page</p>
                </div>
                <div>
                  <Label htmlFor="publicVenue">Public Venue Name</Label>
                  <Input
                    id="publicVenue"
                    name="publicVenue"
                    value={compFormData.publicVenue}
                    onChange={handleCompChange}
                    placeholder="e.g., Sports Centre"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to use Location field</p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="publicAddress">Public Address</Label>
                  <Input
                    id="publicAddress"
                    name="publicAddress"
                    value={compFormData.publicAddress}
                    onChange={handleCompChange}
                    placeholder="e.g., 123 Main Street, City, Postcode"
                  />
                  <p className="text-xs text-gray-500 mt-1">Address shown publicly</p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="publicDates">Public Dates</Label>
                  <Input
                    id="publicDates"
                    name="publicDates"
                    value={compFormData.publicDates}
                    onChange={handleCompChange}
                    placeholder="e.g., 23rd - 24th November 2025"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate from start/end dates</p>
                </div>
              </div>
            </div>

            {/* Scoring Configuration */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Scoring Configuration</Label>
              <p className="text-sm text-gray-500 mb-3">Configure how scores are calculated for this competition</p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="scoreBaseValue">Base Value</Label>
                  <Input
                    id="scoreBaseValue"
                    name="scoreBaseValue"
                    type="number"
                    step="0.001"
                    value={compFormData.scoreBaseValue}
                    onChange={(e) => setCompFormData({ ...compFormData, scoreBaseValue: parseFloat(e.target.value) || 0 })}
                    className="w-32"
                    data-testid="score-base-value-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Starting value before component calculations (e.g., 10.000)</p>
                </div>
                
                <div>
                  <Label className="mb-2 block">Score Components</Label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { key: 'D', label: 'D (Difficulty)', sign: '+' },
                      { key: 'E', label: 'E (Execution)', sign: '−' },
                      { key: 'B', label: 'B (Bonus)', sign: '+' },
                      { key: 'ND', label: 'ND (Neutral Ded.)', sign: '−' }
                    ].map(comp => (
                      <div key={comp.key} className="flex items-center gap-2">
                        <Switch
                          checked={compFormData.scoreComponents?.[comp.key] ?? false}
                          onCheckedChange={(checked) => setCompFormData({
                            ...compFormData,
                            scoreComponents: { ...compFormData.scoreComponents, [comp.key]: checked }
                          })}
                          data-testid={`score-toggle-${comp.key}`}
                        />
                        <span className="text-sm">{comp.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Formula Preview */}
                <div className="bg-gray-50 rounded-lg p-3 border">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Formula Preview:</p>
                  <p className="text-lg font-mono font-semibold text-black" data-testid="score-formula-preview">
                    {compFormData.scoreBaseValue.toFixed(3)}
                    {compFormData.scoreComponents?.D && ' + D'}
                    {compFormData.scoreComponents?.E && ' − E'}
                    {compFormData.scoreComponents?.B && ' + B'}
                    {compFormData.scoreComponents?.ND && ' − ND'}
                  </p>
                </div>
              </div>
            </div>

            {/* SMS Notifications */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">SMS Notifications</Label>
              <p className="text-sm text-gray-500 mb-3">
                Master toggles for SMS notifications during this competition.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={!!compFormData.smsCoachesEnabled}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, smsCoachesEnabled: checked })}
                    data-testid="comp-sms-coaches-toggle"
                  />
                  <div className="text-sm">
                    <div className="font-semibold">Coach SMS</div>
                    <div className="text-xs text-gray-500">During entry, clubs can opt each coach in to receive live score-update SMS.</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={!!compFormData.smsSpectatorsEnabled}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, smsSpectatorsEnabled: checked })}
                    data-testid="comp-sms-spectators-toggle"
                  />
                  <div className="text-sm">
                    <div className="font-semibold">Spectator SMS</div>
                    <div className="text-xs text-gray-500">Spectators / parents can register a phone number to receive score-update SMS.</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Programme / Rotation */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Programme & Rotations</Label>
              <p className="text-sm text-gray-500 mb-3">
                Disable this if the competition does not run with a programme / rotation system.
              </p>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <Switch
                  checked={!!compFormData.programmeEnabled}
                  onCheckedChange={(checked) => setCompFormData({ ...compFormData, programmeEnabled: checked })}
                  data-testid="comp-programme-enabled-toggle"
                />
                <div className="text-sm">
                  <div className="font-semibold">Enable programme & rotation tooling</div>
                  <div className="text-xs text-gray-500">When OFF, programme/rotation pages will be hidden for this competition.</div>
                </div>
              </div>
            </div>

            {/* Google Sheets sync */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Google Sheets Sync</Label>
              <p className="text-sm text-gray-500 mb-3">
                Push scores into a specific Google Sheet for this competition (overrides the global sheet when set).
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={!!compFormData.googleSheetsEnabled}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, googleSheetsEnabled: checked })}
                    data-testid="comp-google-sheets-toggle"
                  />
                  <div className="text-sm">
                    <div className="font-semibold">Connect this competition to Google Sheets</div>
                    <div className="text-xs text-gray-500">Make sure the sheet is shared with the Scorz service account email.</div>
                  </div>
                </div>
                {compFormData.googleSheetsEnabled && (
                  <>
                    <div>
                      <Label htmlFor="googleSheetsId" className="text-sm">Google Sheet ID</Label>
                      <Input
                        id="googleSheetsId"
                        value={compFormData.googleSheetsId}
                        placeholder="e.g. 1AbCdEfGhIj..."
                        onChange={(e) => setCompFormData({ ...compFormData, googleSheetsId: e.target.value.trim() })}
                        data-testid="comp-google-sheets-id-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Paste only the ID from the URL: <span className="font-mono">/spreadsheets/d/<strong>SHEET_ID</strong>/edit</span>
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm">Sheet layout (discipline)</Label>
                      <div className="flex gap-2 mt-1">
                        {['WAG', 'MAG', 'BOTH'].map((opt) => {
                          const active = (compFormData.sheetsDiscipline || 'WAG') === opt;
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => setCompFormData({ ...compFormData, sheetsDiscipline: opt })}
                              className={`flex-1 px-3 py-2 rounded border text-sm font-semibold transition ${
                                active ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                              data-testid={`comp-sheets-discipline-${opt}`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        WAG: tabs <strong>WAG-VT, WAG-UB, WAG-BB, WAG-FX, WAG-R&amp;C</strong>.
                        MAG: tabs <strong>MAG-FX, MAG-PH, MAG-SR, MAG-VT, MAG-PB, MAG-HB</strong>.
                        BOTH: WAG-* tabs plus MAG-PH/SR/PB/HB (FX and VT share the WAG-* tab).
                        Layout per tab: A # · B First · C Last · D Category · E Discipline · F Round · G Group · H D · I E · J B · K ND · L Final.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Entry Requirements */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Entry Requirements</Label>
              <p className="text-sm text-gray-500 mb-3">
                Control whether clubs must select gymnasts, coaches and judges to submit their entry — and the minimum count.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={!!compFormData.gymnastsRequired}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, gymnastsRequired: checked })}
                    data-testid="comp-gymnasts-required-toggle"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Gymnasts required</div>
                    <div className="text-xs text-gray-500">At least one gymnast must be selected.</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={!!compFormData.coachesRequired}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, coachesRequired: checked })}
                    data-testid="comp-coaches-required-toggle"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Coaches required</div>
                    <div className="text-xs text-gray-500">Minimum number of coaches each club must add.</div>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    className="w-20"
                    value={compFormData.minCoaches}
                    disabled={!compFormData.coachesRequired}
                    onChange={(e) => setCompFormData({ ...compFormData, minCoaches: parseInt(e.target.value || '0', 10) })}
                    data-testid="comp-min-coaches-input"
                  />
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={!!compFormData.judgesRequired}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, judgesRequired: checked })}
                    data-testid="comp-judges-required-toggle"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Judges required</div>
                    <div className="text-xs text-gray-500">Minimum number of judges each club must add (bypass code can override).</div>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    className="w-20"
                    value={compFormData.minJudges}
                    disabled={!compFormData.judgesRequired}
                    onChange={(e) => setCompFormData({ ...compFormData, minJudges: parseInt(e.target.value || '0', 10) })}
                    data-testid="comp-min-judges-input"
                  />
                </div>
              </div>
            </div>

            {/* Registration/Check-in Settings */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Registration/Check-in Settings</Label>
              <p className="text-sm text-gray-500 mb-3">
                Configure what information is collected during check-in registration
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                  <Switch checked={true} disabled />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Gymnast Selection</div>
                    <div className="text-xs text-gray-500">Select which gymnasts to check in (always enabled)</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={compFormData.checkInMusicEnabled !== false}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, checkInMusicEnabled: checked })}
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Music Check</div>
                    <div className="text-xs text-gray-500">Allow checking/confirming music during check-in</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={compFormData.checkInPhoneEnabled !== false}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, checkInPhoneEnabled: checked })}
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Phone Number</div>
                    <div className="text-xs text-gray-500">Collect parent/guardian phone number for SMS</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={compFormData.checkInPrintNumberEnabled !== false}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, checkInPrintNumberEnabled: checked })}
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Print Number</div>
                    <div className="text-xs text-gray-500">Allow printing gymnast competition number</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={compFormData.checkInSpectatorsEnabled !== false}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, checkInSpectatorsEnabled: checked })}
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Spectators</div>
                    <div className="text-xs text-gray-500">Allow selecting number of spectators</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    checked={compFormData.checkInCompetingApparatusEnabled !== false}
                    onCheckedChange={(checked) => setCompFormData({ ...compFormData, checkInCompetingApparatusEnabled: checked })}
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">Competing Apparatus</div>
                    <div className="text-xs text-gray-500">Allow selecting which apparatus gymnast will compete on</div>
                  </div>
                </div>

                {/* Apparatus selector — shown when Competing Apparatus is enabled */}
                {compFormData.checkInCompetingApparatusEnabled !== false && (
                  <div className="ml-4 p-3 border rounded-lg bg-gray-50">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Active apparatus for this competition:</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'vault', label: 'Vault' },
                        { id: 'bars',  label: 'Bars' },
                        { id: 'beam',  label: 'Beam' },
                        { id: 'floor', label: 'Floor' },
                        { id: 'range', label: 'Range & Conditioning' },
                      ].map(app => {
                        const active = (compFormData.competitionApparatus || []).includes(app.id);
                        return (
                          <button
                            key={app.id}
                            type="button"
                            onClick={() => {
                              const current = compFormData.competitionApparatus || ['vault','bars','beam','floor','range'];
                              const next = active ? current.filter(a => a !== app.id) : [...current, app.id];
                              setCompFormData({ ...compFormData, competitionApparatus: next });
                            }}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                              active
                                ? 'bg-black text-white border-black'
                                : 'bg-white text-gray-400 border-gray-300'
                            }`}
                          >
                            {app.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Tap to toggle. Only active apparatus appear at registration check-in.</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Categories for this competition */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Competition Categories</Label>
              <p className="text-sm text-gray-500 mb-3">Add the gymnast categories available for this competition</p>
              
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="e.g., Compulsory 2, Compulsory 3..."
                  value={newCompCategory}
                  onChange={(e) => setNewCompCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCompCategory())}
                />
                <Button type="button" onClick={handleAddCompCategory} className="bg-black hover:bg-gray-800">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {compFormData.categories.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {compFormData.categories.map((cat, index) => (
                    <div key={index} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                      <span className="text-sm">{cat}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCompCategory(index)}
                        className="text-gray-500 hover:text-black"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No categories added yet</p>
              )}
            </div>

            {/* Excel Import — only available when editing an existing competition */}
            {editingComp && (
              <div className="border-t pt-4 space-y-3" data-testid="excel-import-section">
                <div>
                  <h3 className="font-semibold text-sm">Import gymnasts from Excel</h3>
                  <p className="text-xs text-gray-500">
                    Columns: A Number · B First · C Last · D Year of Birth · E Round (e.g. "Round 1") · F Group · G Floor music filename (no extension) · H Discipline (WAG / MAG).
                    Re-importing <strong>wipes & recreates</strong> all imported gymnasts, the entry and the programme for this competition.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold">Club</label>
                    <select
                      value={importClubId}
                      onChange={(e) => setImportClubId(e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                      data-testid="import-club-select"
                    >
                      <option value="">Select club…</option>
                      {clubs.map((c) => (
                        <option key={c.id} value={c.id}>{c.clubName || c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold">Cloudflare folder (for floor music)</label>
                    <input
                      type="text"
                      value={importFolder}
                      onChange={(e) => setImportFolder(e.target.value)}
                      placeholder="e.g. AdvancedChampionships"
                      className="w-full border rounded px-2 py-1.5 text-sm"
                      data-testid="import-folder-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold">Excel file (.xlsx)</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm"
                    data-testid="import-file-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleImportExcel}
                  disabled={importing || !importClubId || !importFile}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="import-excel-btn"
                >
                  {importing ? 'Importing…' : 'Wipe & Import'}
                </Button>
                {importResult && (
                  <div className="text-xs bg-green-50 border border-green-200 rounded p-2" data-testid="import-result">
                    Imported <strong>{importResult.imported_gymnasts}</strong> gymnasts ·
                    {' '}<strong>{importResult.rounds}</strong> round(s) ·
                    {' '}<strong>{importResult.groups}</strong> group(s) ·
                    {' '}<strong>{importResult.music_urls_generated}</strong> floor-music URL(s)
                    {importResult.categories_added?.length > 0 && (
                      <div className="mt-1 text-green-800">
                        Added categories: {importResult.categories_added.join(', ')}
                      </div>
                    )}
                    {importResult.errors?.length > 0 && (
                      <div className="mt-1 text-amber-700">
                        {importResult.errors.length} row(s) skipped: {importResult.errors.slice(0, 3).join('; ')}
                        {importResult.errors.length > 3 ? '…' : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCompDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#000000] hover:bg-[#333333]"
              >
                {editingComp ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Team Builder Dialog ─────────────────────────────────────────────── */}
      <Dialog open={showTeamBuilder} onOpenChange={(open) => { setShowTeamBuilder(open); if (!open) setBuilderEditTeam(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Team Builder — {teamBuilderComp?.title}</DialogTitle>
            <DialogDescription>
              Create and manage teams. Any gymnast entered in this competition can join any team.
              Top {teamBuilderComp?.teamCountingScores || 3} scores per apparatus count.
            </DialogDescription>
          </DialogHeader>

          {builderLoadingTeams ? (
            <p className="text-center text-gray-400 py-8">Loading…</p>
          ) : (
            <div className="space-y-6">
              {/* ── Existing Teams ── */}
              {builderTeams.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Teams ({builderTeams.length})</h3>
                  <div className="space-y-3">
                    {builderTeams.map(team => (
                      <div key={team.id} className="border rounded-lg p-3 bg-gray-50" data-testid={`builder-team-${team.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-gray-900">{team.name}
                              {team.category && <span className="ml-2 text-xs font-medium text-purple-700 bg-purple-100 rounded px-2 py-0.5">{team.category}</span>}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{team.gymnasts?.length || 0} gymnasts</p>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {(team.gymnasts || []).map(g => (
                                <div key={g.id} className="flex items-center gap-1 bg-white border border-gray-200 rounded px-2 py-0.5 text-xs text-gray-700">
                                  <span className="font-medium">{g.name}</span>
                                  <span className="text-gray-400">{g.clubName}</span>
                                  {/* Category badge / edit */}
                                  {editingGymnastCat[g.id] !== undefined ? (
                                    <span className="flex items-center gap-1 ml-1">
                                      <input
                                        autoFocus
                                        value={editingGymnastCat[g.id]}
                                        onChange={e => setEditingGymnastCat(prev => ({ ...prev, [g.id]: e.target.value }))}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') saveGymnastCategory(team, g.id, editingGymnastCat[g.id]);
                                          if (e.key === 'Escape') setEditingGymnastCat(prev => { const n = { ...prev }; delete n[g.id]; return n; });
                                        }}
                                        className="border rounded px-1 w-24 text-xs py-0.5"
                                        placeholder="Category"
                                        data-testid={`cat-input-${g.id}`}
                                      />
                                      <button
                                        onClick={() => saveGymnastCategory(team, g.id, editingGymnastCat[g.id])}
                                        disabled={savingGymnastCat[g.id]}
                                        className="text-green-600 hover:text-green-800 text-xs font-bold"
                                        data-testid={`cat-save-${g.id}`}
                                      >{savingGymnastCat[g.id] ? '…' : '✓'}</button>
                                      <button
                                        onClick={() => setEditingGymnastCat(prev => { const n = { ...prev }; delete n[g.id]; return n; })}
                                        className="text-red-400 hover:text-red-600 text-xs"
                                      >✕</button>
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => setEditingGymnastCat(prev => ({ ...prev, [g.id]: g.category || '' }))}
                                      className="ml-1 text-xs px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200"
                                      title="Edit category"
                                      data-testid={`cat-edit-${g.id}`}
                                    >
                                      {g.category || <span className="text-orange-500 font-medium">No category</span>}
                                      <span className="ml-1 text-gray-400">✎</span>
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button size="sm" variant="outline" onClick={() => startEditBuilderTeam(team)} data-testid={`edit-team-${team.id}`}>Edit</Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteBuilderTeam(team.id)} data-testid={`delete-team-${team.id}`}>Delete</Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Add / Edit Team Form ── */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">{builderEditTeam ? `Editing: ${builderEditTeam.name}` : 'Add New Team'}</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Team Name *</label>
                      <Input value={newTeamName} onChange={e => setNewTeamName(e.target.value)} placeholder="e.g. Team Alpha" data-testid="new-team-name" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Category (free text)</label>
                      <Input value={newTeamCategory} onChange={e => setNewTeamCategory(e.target.value)} placeholder="e.g. Senior Team" data-testid="new-team-category" />
                    </div>
                  </div>

                  {/* Gymnast Picker */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Selected Gymnasts ({newTeamGymnastIds.length})</label>
                    <div className="flex flex-wrap gap-1 mb-2 mt-1 min-h-[32px] p-2 border rounded-lg bg-white">
                      {newTeamGymnastIds.map(gid => {
                        const g = builderGymnasts.find(x => x.id === gid);
                        return g ? (
                          <span key={gid} className="inline-flex items-center gap-1 text-xs bg-black text-white rounded px-2 py-0.5">
                            {g.name}
                            <button onClick={() => setNewTeamGymnastIds(ids => ids.filter(i => i !== gid))} className="text-white hover:text-gray-300 leading-none">×</button>
                          </span>
                        ) : null;
                      })}
                      {newTeamGymnastIds.length === 0 && <span className="text-xs text-gray-400">No gymnasts selected</span>}
                    </div>

                    <Input
                      placeholder="Search gymnasts…"
                      value={builderGymnastSearch}
                      onChange={e => setBuilderGymnastSearch(e.target.value)}
                      className="mb-1 text-sm"
                      data-testid="gymnast-search"
                    />
                    <div className="max-h-48 overflow-y-auto border rounded-lg bg-white">
                      {builderGymnasts
                        .filter(g => !newTeamGymnastIds.includes(g.id) &&
                          (g.name.toLowerCase().includes(builderGymnastSearch.toLowerCase()) ||
                           g.clubName.toLowerCase().includes(builderGymnastSearch.toLowerCase())))
                        .map(g => (
                          <button
                            key={g.id}
                            onClick={() => setNewTeamGymnastIds(ids => [...ids, g.id])}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b last:border-0 flex justify-between"
                            data-testid={`pick-gymnast-${g.id}`}
                          >
                            <span className="font-medium text-gray-900">{g.name}</span>
                            <span className="text-gray-400 text-xs">{g.clubName}</span>
                          </button>
                        ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    {builderEditTeam && (
                      <Button type="button" variant="outline" onClick={() => { setBuilderEditTeam(null); setNewTeamName(''); setNewTeamCategory(''); setNewTeamGymnastIds([]); }}>
                        Cancel Edit
                      </Button>
                    )}
                    <Button type="button" onClick={saveBuilderTeam} disabled={savingTeam} className="flex-1 bg-black text-white hover:bg-gray-800" data-testid="save-team-btn">
                      {savingTeam ? 'Saving…' : builderEditTeam ? 'Update Team' : 'Add Team'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* ── Seed Test Data ── */}
              <div className="border-t pt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-gray-500 border-dashed"
                  data-testid="seed-test-comp-btn"
                  onClick={async () => {
                    if (!window.confirm('This will create a "Scorz Testing Competition" with 20 gymnasts and 3 teams. Continue?')) return;
                    try {
                      const token = localStorage.getItem('token');
                      const r = await axios.post(`${API}/superadmin/seed-test-competition`, {}, { headers: { Authorization: `Bearer ${token}` }});
                      if (r.data.success) {
                        toast.success(`Created! ${r.data.gymnasts} gymnasts, ${r.data.teams} teams`);
                        loadAllData();
                      } else {
                        toast.info(r.data.message || 'Already exists');
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Seed failed');
                    }
                  }}
                >
                  Seed Test Competition (20 gymnasts + 3 teams)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Eligibility Dialog */}
      <Dialog open={showEligibilityDialog} onOpenChange={setShowEligibilityDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Club Eligibility</DialogTitle>
            <DialogDescription>
              Select which clubs can enter {eligibilityComp?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={selectAllClubs}>
                Select All
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={deselectAllClubs}>
                Deselect All
              </Button>
            </div>
            
            <div className="text-sm text-gray-600">
              {selectedEligibleClubs.length} of {clubs.length} clubs selected
            </div>
            
            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              {clubs.map(club => (
                <label
                  key={club.id}
                  className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedEligibleClubs.includes(club.id)}
                    onCheckedChange={() => toggleClubEligibility(club.id)}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{club.name}</div>
                    <div className="text-xs text-gray-500">{club.email}</div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {club.gymnastsCount} gymnasts
                  </div>
                </label>
              ))}
            </div>
            
            {selectedEligibleClubs.length === 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> No clubs are selected. This competition will not be available to any clubs.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEligibilityDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEligibility} className="bg-black hover:bg-gray-800">
              Save Eligibility
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Age Splits Dialog */}
      <Dialog open={showCategorySplitsDialog} onOpenChange={setShowCategorySplitsDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Category Age Splits</DialogTitle>
            <DialogDescription>
              Split categories by birth year for live rankings.
              <br />
              <strong>Exact year (2018)</strong> = Only gymnasts born in 2018
              <br />
              <strong>Year+ (2017+)</strong> = Gymnasts born in 2017 or earlier (older)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {categorySplitsComp?.categories?.length > 0 ? (
              categorySplitsComp.categories.map(cat => (
                <div key={cat} className="border rounded-lg p-3">
                  <h4 className="font-semibold mb-2">{cat}</h4>
                  
                  <div className="flex gap-2 mb-2 items-center flex-wrap">
                    <Input
                      type="number"
                      placeholder={newSplitType === 'range' ? 'From (e.g. 2016)' : 'Year (e.g. 2018)'}
                      value={newSplitYear}
                      onChange={(e) => setNewSplitYear(e.target.value)}
                      className="w-36"
                      min="2000"
                      max="2030"
                    />
                    {newSplitType === 'range' && (
                      <>
                        <span className="text-gray-400 font-medium">–</span>
                        <Input
                          type="number"
                          placeholder="To (e.g. 2017)"
                          value={newSplitYear2}
                          onChange={(e) => setNewSplitYear2(e.target.value)}
                          className="w-36"
                          min="2000"
                          max="2030"
                        />
                      </>
                    )}
                    <Select value={newSplitType} onValueChange={(v) => { setNewSplitType(v); setNewSplitYear2(''); }}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="exact">Exact year</SelectItem>
                        <SelectItem value="plus">Year+ (older)</SelectItem>
                        <SelectItem value="range">Range (e.g. 2016–2017)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button" 
                      size="sm"
                      onClick={() => addSplitYear(cat)}
                      className="bg-black hover:bg-gray-800"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {(categorySplits[cat]?.length > 0) ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {categorySplits[cat].filter(s => !(typeof s === 'object' && s.type === 'overall')).map(split => (
                          <div key={splitKey(split)} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                            <span className="text-sm">
                              {typeof split === 'object'
                                ? split.type === 'range'
                                  ? `${split.year}–${split.year2}`
                                  : `${split.year}${split.type === 'plus' ? '+' : ''}`
                                : `${split}`
                              }
                            </span>
                            <button
                              type="button"
                              onClick={() => removeSplitYear(cat, splitKey(split))}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        {/* Overall toggle */}
                        {(() => {
                          const hasOverall = (categorySplits[cat] || []).some(s => typeof s === 'object' && s.type === 'overall');
                          return (
                            <button
                              type="button"
                              onClick={() => toggleOverall(cat)}
                              className={`text-xs px-2 py-1 rounded border transition-colors ${
                                hasOverall
                                  ? 'bg-black text-white border-black'
                                  : 'bg-white text-gray-400 border-gray-300 hover:border-gray-500 hover:text-gray-600'
                              }`}
                            >
                              {hasOverall ? '✓ Overall' : '+ Overall'}
                            </button>
                          );
                        })()}
                      </div>
                      <button
                        type="button"
                        onClick={() => clearAllSplits(cat)}
                        className="text-xs text-red-500 hover:text-red-700 underline"
                      >
                        Clear all splits for {cat}
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No splits (shows as single category)</p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No categories defined for this competition. Add categories first.</p>
            )}
          </div>
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={clearAllCategorySplits}
              className="mr-auto"
            >
              Clear All Splits
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCategorySplitsDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveCategorySplits} className="bg-black hover:bg-gray-800">
                Save Splits
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Qualification Settings Dialog */}
      <Dialog open={showQualificationDialog} onOpenChange={setShowQualificationDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Qualification Settings</DialogTitle>
            <DialogDescription>
              Enable qualification scoring for categories. Gymnasts meeting the score threshold will show "Q" on live rankings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
              <div>
                <Label className="font-semibold">Enable Qualification</Label>
                <p className="text-xs text-gray-500">Turn on to configure category qualifications</p>
              </div>
              <Switch
                checked={qualificationEnabled}
                onCheckedChange={setQualificationEnabled}
              />
            </div>
            
            {qualificationEnabled && (
              <>
                {/* Qualification Text */}
                <div className="space-y-2">
                  <Label className="font-semibold">Qualifies for:</Label>
                  <Input
                    placeholder="e.g., Regional Finals 2026"
                    value={qualificationText}
                    onChange={(e) => setQualificationText(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">This text appears in the key on live rankings</p>
                </div>
                
                {/* Categories */}
                <div className="space-y-3">
                  <Label className="font-semibold">Category Qualification Scores:</Label>
                  {qualificationComp?.categories?.length > 0 ? (
                    qualificationComp.categories.map(cat => {
                      const catQual = qualificationCategories[cat] || { enabled: false, score: 0 };
                      return (
                        <div key={cat} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{cat}</span>
                            <Switch
                              checked={catQual.enabled}
                              onCheckedChange={() => toggleCategoryQualification(cat)}
                            />
                          </div>
                          {catQual.enabled && (
                            <div className="space-y-3">
                              {/* Primary Qualification: Score threshold */}
                              <div className="flex items-center gap-2">
                                <Label className="text-sm">Qualifying AA Score:</Label>
                                <Input
                                  type="number"
                                  step="0.001"
                                  min="0"
                                  max="100"
                                  placeholder="0.000"
                                  value={catQual.score || ''}
                                  onChange={(e) => updateCategoryQualScore(cat, e.target.value)}
                                  className="w-28"
                                />
                              </div>
                              
                              {/* Secondary Qualification: Top N + Min Score */}
                              <div className="border-t pt-3 mt-3">
                                <p className="text-xs text-gray-500 mb-2">OR Top N also qualify with minimum score:</p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Label className="text-sm">Top</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="50"
                                    placeholder="6"
                                    value={catQual.topN || ''}
                                    onChange={(e) => {
                                      const val = e.target.value ? parseInt(e.target.value) : null;
                                      setQualificationCategories({
                                        ...qualificationCategories,
                                        [cat]: { ...catQual, topN: val }
                                      });
                                    }}
                                    className="w-16"
                                  />
                                  <Label className="text-sm">with min score:</Label>
                                  <Input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    max="100"
                                    placeholder="0.000"
                                    value={catQual.topNMinScore || ''}
                                    onChange={(e) => {
                                      const val = e.target.value ? parseFloat(e.target.value) : null;
                                      setQualificationCategories({
                                        ...qualificationCategories,
                                        [cat]: { ...catQual, topNMinScore: val }
                                      });
                                    }}
                                    className="w-28"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500">No categories defined for this competition.</p>
                  )}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQualificationDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveQualificationSettings} className="bg-black hover:bg-gray-800">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entry Edit Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Entry</DialogTitle>
            <DialogDescription>
              {editingEntry?.club?.name} - {editingEntry?.competition?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Gymnasts Selection with Category */}
            <div>
              <h3 className="font-semibold mb-3">Gymnasts & Categories</h3>
              <p className="text-sm text-gray-500 mb-3">Select gymnasts and assign their competition category. Changing a category will update across all of Scorz (rankings, printing, etc.) and scores will carry over.</p>
              {entryGymnasts.length === 0 ? (
                <p className="text-sm text-gray-500">No gymnasts in this club</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {entryGymnasts.map(gymnast => {
                    const isSelected = selectedEntryGymnasts.includes(gymnast.id);
                    const currentCategory = entryGymnastCategories[gymnast.id] || '';
                    return (
                      <div key={gymnast.id} className={`flex items-center gap-3 p-3 border rounded ${isSelected ? 'bg-green-50 border-green-300' : 'bg-gray-50'}`}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedEntryGymnasts([...selectedEntryGymnasts, gymnast.id]);
                            } else {
                              setSelectedEntryGymnasts(selectedEntryGymnasts.filter(id => id !== gymnast.id));
                              // Remove category when deselecting
                              const newCats = { ...entryGymnastCategories };
                              delete newCats[gymnast.id];
                              setEntryGymnastCategories(newCats);
                            }
                          }}
                        />
                        <div className="flex-1">
                          <span className="font-medium">
                            {gymnast.preferredFirstName || gymnast.legalFirstName || gymnast.firstName} {gymnast.preferredLastName || gymnast.legalLastName || gymnast.lastName}
                          </span>
                          {gymnast.dob && (
                            <span className="text-sm text-gray-500 ml-2">
                              (DOB: {new Date(gymnast.dob).getFullYear()})
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <Select
                              value={currentCategory}
                              onValueChange={(val) => {
                                setEntryGymnastCategories({
                                  ...entryGymnastCategories,
                                  [gymnast.id]: val
                                });
                              }}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {/* Show current category first if not in list */}
                                {currentCategory && !availableCategories.includes(currentCategory) && (
                                  <SelectItem key={currentCategory} value={currentCategory} className="text-amber-600">
                                    {currentCategory} (current)
                                  </SelectItem>
                                )}
                                {availableCategories.map(cat => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {currentCategory && !availableCategories.includes(currentCategory) && (
                              <span className="text-xs text-amber-600" title="Category not in competition list">⚠️</span>
                            )}
                          </div>
                        )}
                        {!isSelected && currentCategory && (
                          <span className="text-sm text-gray-400">Was: {currentCategory}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Coaches Selection */}
            <div>
              <h3 className="font-semibold mb-3">Select Coaches</h3>
              {entryCoaches.length === 0 ? (
                <p className="text-sm text-gray-500">No coaches in this club</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {entryCoaches.map(coach => (
                    <div key={coach.id} className="flex items-center space-x-2 p-2 border rounded">
                      <Checkbox
                        checked={selectedEntryCoaches.includes(coach.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedEntryCoaches([...selectedEntryCoaches, coach.id]);
                          } else {
                            setSelectedEntryCoaches(selectedEntryCoaches.filter(id => id !== coach.id));
                          }
                        }}
                      />
                      <Label className="flex-1 cursor-pointer">
                        {coach.firstName} {coach.lastName} - {coach.qualification}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Judges Selection */}
            <div>
              <h3 className="font-semibold mb-3">Select Judges</h3>
              {entryJudges.length === 0 ? (
                <p className="text-sm text-gray-500">No judges in this club</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {entryJudges.map(judge => (
                    <div key={judge.id} className="flex items-center space-x-2 p-2 border rounded">
                      <Checkbox
                        checked={selectedEntryJudges.includes(judge.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedEntryJudges([...selectedEntryJudges, judge.id]);
                          } else {
                            setSelectedEntryJudges(selectedEntryJudges.filter(id => id !== judge.id));
                          }
                        }}
                      />
                      <Label className="flex-1 cursor-pointer">
                        {judge.firstName} {judge.lastName} - {judge.level}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>


            {/* Petitioned Gymnasts */}
            {editingEntry?.petitionedGymnasts && editingEntry.petitionedGymnasts.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Petitioned Gymnasts (Awaiting Approval)</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {editingEntry.petitionedGymnasts.map(petition => {
                    const gymnast = entryGymnasts.find(g => g.id === petition.gymnastId);
                    if (!gymnast) return null;
                    
                    // Get category from gymnastCategories field
                    const category = editingEntry.gymnastCategories?.[petition.gymnastId] || 'N/A';
                    
                    return (
                      <div key={petition.gymnastId} className={`flex items-center justify-between p-3 border rounded ${
                        petition.status === 'accepted' ? 'bg-green-50 border-green-200' : 
                        petition.status === 'declined' ? 'bg-gray-100 border-gray-300' : 
                        'bg-yellow-50 border-yellow-200'
                      }`}>
                        <div className="flex-1">
                          <div className="font-medium">
                            {gymnast.legalFirstName || gymnast.firstName} {gymnast.legalLastName || gymnast.lastName}
                          </div>
                          <div className="text-sm text-gray-600">{category}</div>
                          <div className="text-xs mt-1">
                            {petition.status === 'pending' && (
                              <span className="text-yellow-700 font-semibold">⚠️ Pending Review</span>
                            )}
                            {petition.status === 'accepted' && (
                              <span className="text-green-700 font-semibold">✓ Accepted - Moved to main list</span>
                            )}
                            {petition.status === 'declined' && (
                              <span className="text-black font-semibold">✗ DECLINED</span>
                            )}
                          </div>
                        </div>
                        {petition.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleAcceptPetition(petition.gymnastId)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDeclinePetition(petition.gymnastId)}
                              className="bg-gray-900 hover:bg-black text-white"
                            >
                              Decline
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}


            {/* Judge Day Assignments — only when competition has multiple days */}
            {(editingEntry?.competition?.competitionDays || 1) > 1 && selectedEntryJudges.length > 0 && (() => {
              const compDays = editingEntry.competition.competitionDays;
              const startsOn = editingEntry.competition?.startsOn;
              const getDayLabel = (dayNum) => {
                if (startsOn) {
                  try {
                    const dt = new Date(startsOn);
                    dt.setDate(dt.getDate() + dayNum - 1);
                    return format(dt, 'EEE d MMM');
                  } catch {}
                }
                return `Day ${dayNum}`;
              };
              return (
                <div>
                  <h3 className="font-semibold mb-1">Judge Day Assignments</h3>
                  <p className="text-xs text-gray-500 mb-3">Tick which days each judge will attend. Each day counts as one judge-day toward the requirement.</p>
                  <div className="space-y-3">
                    {entryJudges.filter(j => selectedEntryJudges.includes(j.id)).map(judge => {
                      const assignedDays = entryJudgeDayAssignments[judge.id] || Array.from({ length: compDays }, (_, i) => i + 1);
                      return (
                        <div key={judge.id} className="flex items-center gap-3 p-3 border rounded bg-gray-50">
                          <span className="font-medium text-sm w-48 shrink-0">{judge.firstName} {judge.lastName} <span className="text-gray-500 font-normal">({judge.level})</span></span>
                          <div className="flex flex-wrap gap-2">
                            {Array.from({ length: compDays }, (_, i) => i + 1).map(dayNum => {
                              const checked = assignedDays.includes(dayNum);
                              return (
                                <label key={dayNum} className={`flex items-center gap-1.5 text-xs cursor-pointer px-2.5 py-1.5 rounded-md border transition-colors ${checked ? 'bg-blue-50 border-blue-300 text-blue-800 font-medium' : 'bg-white border-gray-200 text-gray-500'}`}>
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(v) => {
                                      const current = entryJudgeDayAssignments[judge.id] || Array.from({ length: compDays }, (_, i) => i + 1);
                                      const updated = v ? [...new Set([...current, dayNum])] : current.filter(d => d !== dayNum);
                                      setEntryJudgeDayAssignments({ ...entryJudgeDayAssignments, [judge.id]: updated });
                                    }}
                                  />
                                  {getDayLabel(dayNum)}
                                </label>
                              );
                            })}
                          </div>
                          <span className="text-xs text-gray-500 ml-auto">{assignedDays.length} day{assignedDays.length !== 1 ? 's' : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Teams — only shown when the competition has team events and club has teams */}
            {entryTeams.length > 0 && (
              <div>
                <h3 className="font-semibold mb-1">Teams ({entryTeams.length})</h3>
                <p className="text-xs text-gray-500 mb-3">You can update which gymnasts are in each team. Changes are saved when you click Save.</p>
                <div className="space-y-3">
                  {entryTeams.map((team, teamIdx) => (
                    <div key={team.id} className="p-3 border rounded bg-gray-50">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium text-sm">{team.name}</span>
                        {team.category && <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">{team.category}</span>}
                        <span className="text-xs text-gray-500 ml-auto">{(team.gymnastIds || []).length} gymnast{(team.gymnastIds || []).length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {entryGymnasts.map(g => {
                          const inTeam = (team.gymnastIds || []).includes(g.id);
                          const gName = `${g.preferredFirstName || g.legalFirstName || ''} ${g.preferredLastName || g.legalLastName || ''}`.trim();
                          return (
                            <label key={g.id} className={`flex items-center gap-1.5 text-xs cursor-pointer px-2 py-1 rounded border transition-colors ${inTeam ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-gray-200 text-gray-500'}`}>
                              <Checkbox
                                checked={inTeam}
                                onCheckedChange={(v) => {
                                  const updated = entryTeams.map((t, i) => {
                                    if (i !== teamIdx) return t;
                                    const ids = t.gymnastIds || [];
                                    return { ...t, _edited: true, gymnastIds: v ? [...ids, g.id] : ids.filter(id => id !== g.id) };
                                  });
                                  setEntryTeams(updated);
                                }}
                              />
                              {gName}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="border-t pt-4">
              <p className="text-sm">
                <strong>Total Selected:</strong> {selectedEntryGymnasts.length} Gymnasts, {selectedEntryCoaches.length} Coaches, {selectedEntryJudges.length} Judges
              </p>
              <p className="text-sm text-[#000000] font-semibold mt-1">
                Entry Fee: £{(() => {
                  const comp = editingEntry?.competition || {};
                  const cf = comp.categoryFees || {};
                  const cats = editingEntry?.gymnastCategories || {};
                  const def = parseFloat(comp.entryFee) || 40;
                  const total = selectedEntryGymnasts.reduce((sum, gid) => {
                    const c = cats[gid];
                    const fee = c && cf[c] !== undefined ? parseFloat(cf[c]) : def;
                    return sum + (Number.isFinite(fee) ? fee : 0);
                  }, 0);
                  return total.toFixed(2);
                })()}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowEntryDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEntry}
              className="flex-1 bg-[#000000] hover:bg-[#333333]"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Super Admin Toggle Confirmation Dialog */}
      <AlertDialog open={showSuperAdminDialog} onOpenChange={setShowSuperAdminDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {clubToToggle?.currentStatus ? 'Remove Super Admin Access?' : 'Grant Super Admin Access?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {clubToToggle?.currentStatus ? (
                <>
                  Are you sure you want to remove super admin access from <strong>{clubToToggle?.name}</strong>? 
                  They will no longer be able to manage all clubs and system data.
                </>
              ) : (
                <>
                  Are you sure you want to grant super admin access to <strong>{clubToToggle?.name}</strong>? 
                  They will be able to manage all clubs, competitions, and system data.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmToggleSuperAdmin}
              className="bg-[#000000] hover:bg-[#333333]"
            >
              {clubToToggle?.currentStatus ? 'Remove Access' : 'Grant Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Club Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Club?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{clubToDelete?.name}</strong>?
              <br /><br />
              This will permanently delete:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>The club account</li>
                <li>All {clubToDelete?.peopleCount} registered people (gymnasts, coaches, judges)</li>
                <li>All user accounts associated with this club</li>
              </ul>
              <br />
              <strong className="text-gray-900">This action cannot be undone.</strong>
              {' '}Note: Clubs with competition entries cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteClub}
              className="bg-gray-900 hover:bg-black"
            >
              Delete Club
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Delete Entry Confirmation Dialog */}
      <AlertDialog open={showDeleteEntryDialog} onOpenChange={setShowDeleteEntryDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the entry from <strong>{entryToDelete?.clubName}</strong> for{' '}
              <strong>{entryToDelete?.competitionTitle}</strong>?
              <br /><br />
              <strong className="text-gray-900">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteEntry}
              className="bg-gray-900 hover:bg-black"
            >
              Delete Entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Entries Confirmation Dialog */}
      <AlertDialog open={showDeleteAllEntriesDialog} onOpenChange={setShowDeleteAllEntriesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete ALL Entries?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-gray-900">⚠️ WARNING: This will delete ALL {allEntries.length} competition entries!</strong>
              <br /><br />
              This is a destructive action that will remove all competition entries from all clubs.
              <br /><br />
              <strong className="text-gray-900">This action cannot be undone.</strong>
              <br /><br />
              Are you absolutely sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteAllEntries}
              className="bg-gray-900 hover:bg-black"
            >
              Yes, Delete ALL Entries
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resend Confirmation Email Dialog */}
      <Dialog open={showResendDialog} onOpenChange={(open) => { if (!resendLoading) { setShowResendDialog(open); if (!open) setResendEntry(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Resend Confirmation Email</DialogTitle>
            <DialogDescription>
              {resendEntry && (
                <span>Resend the entry confirmation for <strong>{resendEntry.club?.name}</strong> — {resendEntry.competition?.title}.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button
              disabled={resendLoading}
              onClick={() => sendResendRequest('all')}
              className="w-full text-left p-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="resend-all-btn"
            >
              <div className="font-semibold text-gray-900">Send to All</div>
              <div className="text-sm text-gray-500 mt-0.5">Club receives the email; Scorz (admin@scorz.co.uk) and the secondary organiser (if set) also receive a copy.</div>
            </button>
            <button
              disabled={resendLoading}
              onClick={() => sendResendRequest('club')}
              className="w-full text-left p-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="resend-club-btn"
            >
              <div className="font-semibold text-gray-900">Club Only</div>
              <div className="text-sm text-gray-500 mt-0.5">Only the club email receives the confirmation.</div>
            </button>
            <button
              disabled={resendLoading}
              onClick={() => sendResendRequest('organisers')}
              className="w-full text-left p-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="resend-organisers-btn"
            >
              <div className="font-semibold text-gray-900">Organisers Only</div>
              <div className="text-sm text-gray-500 mt-0.5">Scorz and the secondary organiser receive a copy — the club is not emailed.</div>
            </button>
          </div>
          {resendLoading && (
            <p className="text-sm text-center text-gray-500 pb-2">Sending email…</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResendDialog(false); setResendEntry(null); }} disabled={resendLoading}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Club Dialog */}
      <Dialog open={showEditClubDialog} onOpenChange={setShowEditClubDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Club Details</DialogTitle>
            <DialogDescription>
              Update the club information for {editingClub?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="clubName">Club Name</Label>
              <Input
                id="clubName"
                value={clubFormData.name}
                onChange={(e) => setClubFormData({ ...clubFormData, name: e.target.value })}
                placeholder="Enter club name"
              />
            </div>
            <div>
              <Label htmlFor="clubEmail">Email Address</Label>
              <Input
                id="clubEmail"
                type="email"
                value={clubFormData.email}
                onChange={(e) => setClubFormData({ ...clubFormData, email: e.target.value })}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label htmlFor="clubPhone">Phone Number</Label>
              <Input
                id="clubPhone"
                value={clubFormData.phone}
                onChange={(e) => setClubFormData({ ...clubFormData, phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditClubDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveClub}
              className="bg-[#000000] hover:bg-[#333333]"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Score Dialog */}
      <Dialog open={showEditScoreDialog} onOpenChange={setShowEditScoreDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Score</DialogTitle>
            <DialogDescription>
              Update the score for Gymnast #{editingScore?.gymnastNumber}
            </DialogDescription>
          </DialogHeader>
          {editingScore && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Gymnast Number</Label>
                <Input
                  value={editingScore.gymnastNumber}
                  onChange={(e) => setEditingScore({ ...editingScore, gymnastNumber: e.target.value })}
                />
              </div>
              <div>
                <Label>Difficulty</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editingScore.difficulty}
                  onChange={(e) => setEditingScore({ ...editingScore, difficulty: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Execution</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editingScore.execution}
                  onChange={(e) => setEditingScore({ ...editingScore, execution: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Neutral Deductions</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editingScore.neutralDeductions}
                  onChange={(e) => setEditingScore({ ...editingScore, neutralDeductions: parseFloat(e.target.value) })}
                />
              </div>
              <div className="pt-2 border-t">
                <Label>Final Score (Calculated)</Label>
                <div className="text-2xl font-bold text-[#000000] mt-1">
                  {(10.000 + editingScore.difficulty - editingScore.execution - editingScore.neutralDeductions).toFixed(3)}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditScoreDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveScore} className="bg-[#000000] hover:bg-[#333333]">
              Save Score
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training Video Dialog */}
      <Dialog open={showTrainingVideoDialog} onOpenChange={setShowTrainingVideoDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5" />
              Judge Training Video
            </DialogTitle>
            <DialogDescription>
              Upload and broadcast a training video to all judge tablets for {trainingVideoComp?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Current Status */}
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="font-medium">Status:</span>
                {trainingVideoSettings?.isPlaying ? (
                  <span className="flex items-center gap-2 text-green-600 font-semibold">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Playing on all tablets
                  </span>
                ) : trainingVideoSettings?.videoUrl ? (
                  <span className="text-gray-600">Ready to broadcast</span>
                ) : (
                  <span className="text-orange-600">No video configured</span>
                )}
              </div>
              {trainingVideoSettings?.videoUrl && (
                <div className="mt-2 text-xs text-gray-500 truncate">
                  Video: {trainingVideoSettings.videoUrl.split('/').pop()}
                </div>
              )}
            </div>

            {/* Upload Section */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Upload Video</Label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept="video/*"
                  onChange={handleTrainingVideoUpload}
                  disabled={trainingVideoUploading}
                  className="flex-1"
                />
              </div>
              {trainingVideoUploading && (
                <p className="text-sm text-blue-600">Uploading video...</p>
              )}
            </div>

            {/* Or use URL */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Or Enter Video URL</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/video.mp4"
                  value={trainingVideoUrl}
                  onChange={(e) => setTrainingVideoUrl(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={handleSetTrainingVideoUrl}
                  variant="outline"
                  className="border-black"
                >
                  Save URL
                </Button>
              </div>
            </div>

            {/* Broadcast Controls */}
            {trainingVideoSettings?.videoUrl && (
              <div className="pt-4 border-t space-y-3">
                <Label className="text-base font-semibold">Broadcast Control</Label>
                <div className="flex gap-3">
                  {trainingVideoSettings?.isPlaying ? (
                    <Button 
                      onClick={handleStopTrainingVideo}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      <Square className="w-4 h-4 mr-2" />
                      Stop Video
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleBroadcastTrainingVideo}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Play on All Judge Tablets
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500 text-center">
                  The video will appear full-screen on all judge tablets and play automatically
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrainingVideoDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Judge Bypass Code Dialog */}
      <Dialog open={showBypassCodeDialog} onOpenChange={setShowBypassCodeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Judge Bypass Code</DialogTitle>
            <DialogDescription>
              {bypassCodeComp?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Generate a one-time code that allows a club to submit an entry without selecting a judge. 
              Once used, the code is invalidated and you'll need to generate a new one for another club.
            </p>

            {bypassCodeLoading ? (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">Loading...</p>
              </div>
            ) : bypassCode ? (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Current Bypass Code</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-4 bg-gray-100 rounded-lg text-center">
                    <span className="text-3xl font-mono font-bold tracking-widest">{bypassCode}</span>
                  </div>
                  <Button onClick={copyBypassCode} variant="outline" size="icon">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-amber-600">
                  ⚠️ This code can only be used once. After a club uses it, you must generate a new code.
                </p>
              </div>
            ) : (
              <div className="text-center py-4 space-y-3">
                <p className="text-sm text-gray-500">No bypass code is currently active for this competition.</p>
              </div>
            )}

            <Button 
              onClick={generateBypassCode} 
              className="w-full bg-black hover:bg-gray-800"
              disabled={bypassCodeLoading}
            >
              <Key className="w-4 h-4 mr-2" />
              {bypassCode ? 'Generate New Code' : 'Generate Bypass Code'}
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBypassCodeDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
